import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import RegistrationDiagnosticsCard from "../components/RegistrationDiagnosticsCard";
import {
  activeDivisionsPool,
  isProviderConfirmedLocked,
  layoutFailuresMessage,
  layoutPaymentDecision,
  ONLINE_PAYMENT_PROVIDERS,
} from "../lib/registrationLayout";
import { tournamentStartNotice } from "../lib/registrationDates";
import { categoryConflictNames, countCurpToReview, countCurpUnchecked, curpApplyConfirmText, curpApplySuccessText, curpCanRequestAgain, curpChipClass, curpHistoryLine, curpNeedsReview, curpNoteTexts, curpReasonTexts, curpRequestHint, curpRequestResultText, curpReviewActions, curpReviewRunMessage, curpReviewTitle, curpReviewTone, curpSexOnlyHint, curpStatusText, curpUploadResultText, formatCurpLine, formatRegisteredLine, ordersUseCurp } from "../lib/curpReview";
import { isClosedLike } from "../lib/teamScope";
import { api, API_BASE_URL, ApiError, isSupportUser, participantDisplayName, type MembershipChangePreview, type PlayerUpdateInput, type RegistrationDocumentType, type RegistrationFormField, type RegistrationFormSource, type RegistrationOrder, type RegistrationPaymentStatus, type DeletedRegistration, type RegistrationSummary, type RegistrationSettings, type ScheduleReadinessRow, type Tournament, type TournamentDivision, type TournamentParticipant, type PlayerProfile } from "../lib/api";
import { participantClubLabel } from "./AdminTournamentRankingPage";
import { CREDENTIAL_PHOTO_KEY, EDITOR_DOCUMENT_KEYS, GUARDIAN_AUTO_ENABLED_NOTICE, GUARDIAN_STILL_ON_HINT, MEX_TOUR_CUP_ORGANIZER_EMAILS, REGISTRATION_FIELD_SECTIONS, REGISTRATION_FIELD_SECTION_LABELS, enabledRegistrationFields, fieldSection, guardianMinimumFieldsOff, isMexTourCupSchema, isMinimalRegistrationSchema, mexTourCupRegistrationFormSchema, minimalRegistrationFormSchema, normalizeRegistrationFormSchema, registrationFieldValue, suggestsGuardianSection, tableRegistrationFields, withCredentialPhoto } from "../lib/registrationFields";
import { isKidsDivision } from "../lib/publicRegistrationMode";
import FieldHint from "../components/FieldHint";
import PendingApprovalNotice from "../components/PendingApprovalNotice";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import { escapeHtml, openPrintWindow } from "../lib/printTemplate";
import { uploadAssetHref } from "../lib/assets";
import { feeCategoryLabel } from "../lib/registrationCategories";
import { divisionDetailsLabel, yearFromTournamentDate } from "../lib/tournamentDivisions";
import { coverageOwnerByParticipant, isTeamAnchoredOrder, orderApprovableOtherCategories, orderCoveredCategories, orderInPlayOtherCategories, orderLiveOtherCategories, orderOtherCategories, orderReinstatableOtherCategories, pendingApprovalCounts, reconcileOrderSelection, registrationAwaitingApproval, registrationOrderCollectable, registrationOrderOwes, retainFailedSelection, runBulkMarkPaid, scopeOrdersToIndividuals, teamLayoutRowDecision, type CoverageOptions, type OtherCategory } from "../lib/registrationInbox";
import { joinSpanishList } from "../lib/rrSheetOrder";
import { membershipConfirmText, membershipLosesSchedule, mergedRrRebuildNotice, rrRebuildNotice, settleWithin } from "../lib/rrRebuild";
import { formatDate, formatDateTime } from "../lib/dateFormat";
import { paymentStatusLabel } from "../lib/labels";
import { formatMoney } from "../lib/money";
import { registrationDocumentLinks, registrationDocumentReceived, renewRegistrationDocumentLinks } from "../lib/registrationDocumentLinks";
import { useSignedLinkRefresh } from "../lib/useSignedLinkRefresh";
import {
  accountNotOwnedEmailNote,
  accountNotOwnedInfo,
  isAccountLockedProfileField,
  welcomeNotAccountOwnerMessage,
} from "../lib/playerAccountOwnership";
import GuideHelpButton from "../components/GuideHelpButton";
import EnrollExistingPlayersDialog from "../components/EnrollExistingPlayersDialog";
import ReplacePlayerDialog from "../components/ReplacePlayerDialog";
import { divisionRegistrationState, divisionStateChipLabel, todayMexicoIso } from "../lib/divisionRegistrationState";

function money(value: string | number | null | undefined, currency = "MXN") {
  return formatMoney(value, currency);
}

function statusLabel(status: string) {
  return paymentStatusLabel(status);
}

// `stillPlaying`: el jugador sigue jugando con este pago aunque la fila
// esté de baja. Al crear la pareja el backend marca "dropped" a su registro
// individual (por diseño: la pareja es otro participante), y un pago de
// varias categorías sigue cubriendo las que no se dieron de baja → no es un
// rechazo.
function registrationPaymentVisualState(order: RegistrationOrder, stillPlaying = false): { key: string; label: string; hint: string } {
  if (order.status === "paid") {
    return { key: "paid", label: "Pago confirmado", hint: order.paid_at ? `Confirmado ${formatDate(order.paid_at)}` : "Listo para liberar" };
  }
  if (order.status === "refunded") {
    return { key: "refunded", label: "Reembolsado", hint: "Pago devuelto" };
  }
  if (order.status === "cancelled" || (order.participant?.current_status === "dropped" && !stillPlaying)) {
    return { key: "rejected", label: "Rechazado", hint: order.notes || "Inscripción no aceptada" };
  }
  if (registrationDocumentReceived(order.participant?.registration_documents, "payment_receipt")) {
    return { key: "payment-sent", label: "Pago enviado", hint: "Comprobante pendiente de validar" };
  }
  return {
    key: "pending",
    label: "Pendiente de pago",
    hint: order.provider === "stripe_connect" || order.provider === "mercadopago_marketplace" ? "Pago en línea pendiente" : "Sin comprobante",
  };
}

/** Nombre(s) del compañero de `playerProfileId` en la pareja `team`. */
function teamPartnerNames(team: TournamentParticipant, playerProfileId: number): string {
  return team.team?.members
    .filter((candidate) => candidate.player_profile_id !== playerProfileId)
    .map((candidate) => candidate.player?.display_name ?? `Jugador #${candidate.player_profile_id}`)
    .join(" / ") || team.team?.name || "Pareja";
}

function isPaymentStatusLockedForProvider(order: RegistrationOrder): boolean {
  return isProviderConfirmedLocked(order);
}

function participantStatusLabel(status: string | null | undefined) {
  if (status === "pre_registered") return "Pendiente de revisión";
  if (status === "active") return "Aprobado / activo";
  if (status === "dropped") return "Baja";
  if (status === "waitlist") return "Lista de espera";
  return status ?? "—";
}

/** Pago confirmado por el proveedor en línea (Mercado Pago / Stripe): el
 * comprobante ya no aplica — Eduardo 2026-09-09: "si el pago ya entró por
 * Mercado Pago debería estar en verde, no rojo". */
function registrationPaidOnline(order: RegistrationOrder): boolean {
  return isPaymentStatusLockedForProvider(order);
}

/** `usesCredential=false` (Eduardo 2026-10-05): la «Foto de medio cuerpo»
 * solo sirve para la credencial; sin credencial no se pide ni cuenta. */
function registrationDocsComplete(order: RegistrationOrder, usesCredential = true): boolean {
  const docs = order.participant?.registration_documents;
  const receiptOk = registrationDocumentReceived(docs, "payment_receipt") || registrationPaidOnline(order);
  const photoOk = !usesCredential || registrationDocumentReceived(docs, "photo");
  return photoOk && registrationDocumentReceived(docs, "curp_pdf") && receiptOk;
}

function sourceLabel(source: string | null | undefined) {
  if (source === "public_link") return "link público";
  if (source === "setup_wizard") return "alta en el asistente";
  if (source === "admin_charge") return "cobro del admin";
  if (source === "admin_quick") return "alta del admin";
  if (!source) return "captura manual";
  return source;
}

// El preinscrito "por cobro" (Sección 1 del spec) usa su propio flujo de
// badge/acciones (pago pendiente → copiar link/cancelar; pagado → activar)
// en vez del genérico "Aprobar" de las inscripciones públicas.
// Marca visible del chip de documento (el estado completo va en aria-label):
// listo ✓, falta, revisar ?, alerta !, sin leer ·
function docChipMark(dotClass: string): string {
  if (dotClass.includes("is-ready")) return "✓";
  if (dotClass.includes("is-alert")) return "!";
  if (dotClass.includes("is-review")) return "?";
  if (dotClass.includes("is-unread")) return "·";
  return "falta";
}

function isAdminChargeOrder(order: RegistrationOrder): boolean {
  return order.participant?.registration_source === "admin_charge";
}

function normalizeSearch(value: string | number | null | undefined) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function csvCell(value: string | number | null | undefined) {
  const text = String(value ?? "");
  if (!/[",\n\r]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  const cleaned = text.replace(/^\uFEFF/, "");
  for (let index = 0; index < cleaned.length; index += 1) {
    const char = cleaned[index];
    const next = cleaned[index + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      row.push(cell.trim());
      cell = "";
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell.trim());
      if (row.some((value) => value !== "")) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  row.push(cell.trim());
  if (row.some((value) => value !== "")) rows.push(row);
  return rows;
}

function normalizeLayoutHeader(value: string) {
  return normalizeSearch(value).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function documentStatusLabel(hasDocument: boolean, label: string) {
  return hasDocument ? `${label} listo` : `${label} falta`;
}

// Credencial (Eduardo 2026-10-29): antes el chip decía «ID falta» y un admin
// creyó que faltaba subir una identificación. No es un documento: es la
// credencial digital que genera Torneos123 con el botón «Credencial» (pide
// foto e inscripción activa). El texto visible es corto; el title/aria dice
// qué hacer para tenerla.
// Con credencial la foto de medio cuerpo es Activa y Obligatoria (Eduardo
// 2026-10-30): plantillas, «Usar el formulario de otro torneo» y «Guardar
// campos» la fuerzan y lo dicen con este texto.
const CREDENTIAL_PHOTO_FORCED_TEXT =
  "La «Foto de medio cuerpo» quedó Activa y Obligatoria porque el torneo usa credencial (02).";

function credentialStatus(ready: boolean, active: boolean, hasPhoto: boolean): { label: string; hint: string } {
  if (ready) return { label: "Credencial lista", hint: "Credencial lista · ábrela con «Ver» en el detalle" };
  if (!hasPhoto) return { label: "Credencial pendiente", hint: "Credencial pendiente · primero carga la foto" };
  if (!active) {
    return {
      label: "Credencial pendiente",
      hint: "Credencial pendiente · se genera con el botón «Credencial» cuando la inscripción esté aprobada",
    };
  }
  return { label: "Credencial pendiente", hint: "Credencial pendiente · genérala con el botón «Credencial» en el detalle" };
}

function registrationFileUrl(path: string | null | undefined) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const appBase = API_BASE_URL.replace(/\/api\/v2$/, "").replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${appBase}${normalized}`;
}

function documentButton(label: string, path: string | null | undefined, backPath: string, extraParams: Record<string, string | number | null | undefined> = {}) {
  const url = registrationFileUrl(path);
  if (!url) return <span className="muted">{label}: —</span>;
  const params = new URLSearchParams({ src: url, label, back: backPath });
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") params.set(key, String(value));
  });
  return (
    <Link className="button button--sm button--ghost" to={`/admin/documento?${params.toString()}`}>
      {label}
    </Link>
  );
}


const PAYMENT_VISUAL_STATES = [
  { key: "pending", label: "Pendiente de pago" },
  { key: "payment-sent", label: "Pago enviado" },
  { key: "paid", label: "Pago confirmado" },
  { key: "rejected", label: "Rechazado" },
  { key: "refunded", label: "Reembolsado" },
];


interface AdminTournamentRegistrationsPageProps {
  setupMode?: boolean;
}

/** Qué significan los tres controles de cada campo. Eduardo los señaló en
 * pantalla sin saber qué hacía cada uno (2026-08-06); desde 2026-09-12 vive
 * en el "?" del título para no ocupar pantalla, sobre todo en móvil. */
function RegistrationFieldLegend() {
  return (
    <ul className="registration-field-legend">
      <li><strong>Activo:</strong> el campo aparece en el formulario público. Apagado, el jugador ni lo ve.</li>
      <li><strong>Obligatorio:</strong> no puede enviar su inscripción sin llenarlo.</li>
      <li><strong>En tabla:</strong> se muestra como columna en Registros recibidos, para revisarlos de un vistazo.</li>
      <li><strong>Requerido por el sistema:</strong> nombre, apellido paterno y correo. La plataforma los necesita para crear al jugador, por eso no se pueden apagar.</li>
      <li><strong>Va en:</strong> la sección donde lo ve la familia (Jugador, Papá, mamá o tutor, u Otros).</li>
    </ul>
  );
}

export default function AdminTournamentRegistrationsPage({ setupMode = false }: AdminTournamentRegistrationsPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token, hasRole, user } = useAuth();
  // Soporte Torneos123 (Eduardo 2026-10-29): aprueba y rechaza inscripciones,
  // pero no toca dinero: sin marcar pagado, reembolsos, reemitir, saldos,
  // reclasificar ni cobros. Solo esconde botones: el servidor también niega.
  const canMoney = !isSupportUser(user);
  const location = useLocation();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [settings, setSettings] = useState<RegistrationSettings | null>(null);
  // «Este torneo usa credencial» (Eduardo 2026-10-05). Sin el dato = sí.
  const usesCredential = settings?.credential_enabled !== false;
  const [savingCredentialEnabled, setSavingCredentialEnabled] = useState(false);
  // Doble clic y respuestas fuera de orden del Sí/No de la credencial: una
  // sola petición a la vez y solo la última (del torneo en pantalla) aplica.
  const credentialBusyRef = useRef(false);
  const credentialSeqRef = useRef(0);
  // «Cerrar inscripciones ahora» (Eduardo 2026-10-30): mismo cuidado que el
  // Sí/No de la credencial. La secuencia también deja saber a una carga de la
  // pantalla que empezó antes del cambio que su dato ya es viejo.
  const [savingClosedNow, setSavingClosedNow] = useState(false);
  const closedNowBusyRef = useRef(false);
  const closedNowSeqRef = useRef(0);
  // Torneo en pantalla, para que una respuesta del torneo anterior no pise
  // el formulario del nuevo (guardar, copiar, credencial).
  const currentTournamentRef = useRef(tournamentId);
  currentTournamentRef.current = tournamentId;
  // Toda pregunta de esta pantalla pertenece al torneo en pantalla. El diálogo
  // es global: sin esto seguía abierto tras salir o cambiar de torneo, y al
  // aceptarlo actuaba sobre el torneo abandonado (bloqueo P1 del PR #601). Al
  // salir se retira y contesta «no», así que ningún manejador sigue adelante.
  const { askConfirm, askConfirmWithChecks, currentSignal } = useAbortableConfirm([tournamentId]);
  // Aviso «No se puede quitar … a la foto» (Eduardo 2026-10-30, opción A).
  const [photoLockNotice, setPhotoLockNotice] = useState<"Activo" | "Obligatorio" | null>(null);
  const photoLocklineId = "insc-photo-lockline";
  const photoOffnoteId = "insc-photo-offnote";
  const [summary, setSummary] = useState<RegistrationSummary | null>(null);
  const [orders, setOrders] = useState<RegistrationOrder[]>([]);
  // «Reemplazar jugador» (Eduardo 2026-10-14): quién ya jugó algo (botón apagado).
  const [replaceFor, setReplaceFor] = useState<number | null>(null);
  const [playedIds, setPlayedIds] = useState<Set<number>>(new Set());
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [loading, setLoading] = useState(true);
  // Spinner de página completa solo en la PRIMERA carga: loadDashboard corre
  // tras cada acción (p. ej. Inscripción rápida) y el remonte del árbol se
  // tragaba el primer click siguiente (QA Cowork #12).
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [editingRegistrationFields, setEditingRegistrationFields] = useState(false);
  const [savingRegistrationFields, setSavingRegistrationFields] = useState(false);
  const [registrationFormFields, setRegistrationFormFields] = useState<RegistrationFormField[]>([]);
  const [markingPaidId, setMarkingPaidId] = useState<number | null>(null);
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [sendingWelcomeId, setSendingWelcomeId] = useState<number | null>(null);
  // Mensajes por botón (Eduardo 2026-09-23): documentos pendientes / recordatorio de pago.
  const [sendingMessageId, setSendingMessageId] = useState<number | null>(null);
  const [changingDivisionParticipantId, setChangingDivisionParticipantId] = useState<number | null>(null);
  const [pairingParticipantId, setPairingParticipantId] = useState<number | null>(null);
  const [generatingCredentialId, setGeneratingCredentialId] = useState<number | null>(null);
  const [uploadingDocumentKey, setUploadingDocumentKey] = useState<string | null>(null);
  const [reviewingParticipantId, setReviewingParticipantId] = useState<number | null>(null);
  const [cancellingChargeId, setCancellingChargeId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Inscripción rápida de mostrador (incidente torneo 58): todo en un paso.
  const [qeOpen, setQeOpen] = useState(false);
  // «Inscribir jugadores existentes» (2026-10-11): modal con el alta masiva de Jugadores del torneo.
  const [enrollExistingOpen, setEnrollExistingOpen] = useState(false);
  // «Usar el formulario de otro torneo» (2026-10-11).
  const [formSources, setFormSources] = useState<RegistrationFormSource[] | null>(null);
  const [formSourceId, setFormSourceId] = useState("");
  const [copyingFormSource, setCopyingFormSource] = useState(false);
  // Secciones del editor de campos plegadas (por default todas abiertas).
  const [collapsedFieldSections, setCollapsedFieldSections] = useState<Set<string>>(() => new Set());
  const [qeNombre, setQeNombre] = useState("");
  const [qeApellido, setQeApellido] = useState("");
  const [qeTelefono, setQeTelefono] = useState("");
  const [qeEmail, setQeEmail] = useState("");
  const [qeGenero, setQeGenero] = useState("");
  const [qeNacimiento, setQeNacimiento] = useState("");
  const [qeDivisiones, setQeDivisiones] = useState<Set<number>>(() => new Set());
  const [qeFee, setQeFee] = useState("general");
  const [qeMarkPaid, setQeMarkPaid] = useState(false);
  const [qeSubmitting, setQeSubmitting] = useState(false);
  const [qeMatches, setQeMatches] = useState<PlayerProfile[]>([]);
  const [qeUsando, setQeUsando] = useState<PlayerProfile | null>(null);
  // Categorías con inscritos FUERA del draw (llegaron después de generarlo).
  const [readinessRows, setReadinessRows] = useState<ScheduleReadinessRow[]>([]);
  const [loadingCredentialSample, setLoadingCredentialSample] = useState(false);
  const [reclassifyWhatsapp, setReclassifyWhatsapp] = useState<Record<number, string>>({});
  // Reemitir orden pendiente (Eduardo 2026-09-14): monto manual por orden.
  const [reissueAmount, setReissueAmount] = useState<Record<number, string>>({});
  const [registrationSearch, setRegistrationSearch] = useState("");
  const [bulkLayoutUploading, setBulkLayoutUploading] = useState(false);
  // Rediseño de "Registros recibidos": la tabla es para ESCANEAR (una fila
  // compacta por jugador), el panel expandido es para OPERAR. Chips de
  // categoría con conteos + filtros de trabajo + acciones en lote.
  const [registrationCategoryFilter, setRegistrationCategoryFilter] = useState<number | "none" | null>(null);
  const [registrationWorkFilter, setRegistrationWorkFilter] = useState<"pago" | "docs" | "curp" | "listos" | "aprobar" | null>(null);
  // Bienvenida automática al aprobar (Mensajes): cambia el texto del aviso
  // de inscripciones sin aprobar. Por default encendida.
  const [autoSendWelcome, setAutoSendWelcome] = useState(true);
  // Cambiar de torneo con una carga pendiente: la respuesta vieja no pisa
  // la pantalla del torneo nuevo (ni su aviso de pendientes).
  const loadSeqRef = useRef(0);
  // De qué torneo son las órdenes en pantalla: el aviso no suelta ni aplica
  // «Ver pendientes» con las órdenes del torneo anterior.
  const [loadedTournamentId, setLoadedTournamentId] = useState<number | null>(null);
  // Revisión de CURP vs nombre (avisa, nunca bloquea): orden con una acción
  // del recuadro en curso, y «Revisar CURPs» del torneo en curso.
  const [curpReviewBusyId, setCurpReviewBusyId] = useState<number | null>(null);
  const [reviewingCurps, setReviewingCurps] = useState(false);
  // Reembolso en curso: se abre solo al pulsar "Emitir reembolso" en una orden.
  const [refundDraft, setRefundDraft] = useState<{ orderId: number; amount: string; reason: string } | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  // Eduardo 2026-09-24: al volver de "Ver" un documento, aterrizar en el
  // MISMO jugador (abierto y a la vista), no arriba de la lista.
  const returnOrderHandledRef = useRef<number | null>(null);
  function backToOrderPath(orderId: number): string {
    const params = new URLSearchParams(location.search);
    params.set("order", String(orderId));
    return `${location.pathname}?${params.toString()}`;
  }
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [bulkMarkingPaid, setBulkMarkingPaid] = useState(false);
  const [showTeamOrders, setShowTeamOrders] = useState(false);
  // "Editar registro" / "Eliminar registro" (Eduardo 2026-09-05): corregir
  // datos que el jugador capturó mal, o borrar inscripciones de prueba de un
  // jugador ya dado de baja en un torneo sin calendario ni resultados.
  const [editingRegistrationOrderId, setEditingRegistrationOrderId] = useState<number | null>(null);
  const [registrationEditForm, setRegistrationEditForm] = useState<Record<string, string>>({});
  const [savingRegistrationEdit, setSavingRegistrationEdit] = useState(false);
  const [deletingRegistrationId, setDeletingRegistrationId] = useState<number | null>(null);
  // Cowork 2026-09-19: si "Eliminar registro" no procede, el motivo se ve junto
  // al botón (el aviso de arriba queda fuera de la vista en listas largas).
  const [registrationDeleteBlocked, setRegistrationDeleteBlocked] = useState<{ orderId: number; reason: string } | null>(null);
  // Auditoría de "Eliminar registro" (Eduardo 2026-09-12): lista visible al
  // final de Registros recibidos.
  const [deletedRegistrations, setDeletedRegistrations] = useState<DeletedRegistration[]>([]);

  // Plantilla Mex Tour Cup: solo la ve su organizador (Eduardo 2026-09-12);
  // ni superadmin ni impersonación la muestran a otros admins.
  const canUseMexTourCupTemplate = MEX_TOUR_CUP_ORGANIZER_EMAILS.has((user?.email ?? "").trim().toLowerCase());
  const normalizedRegistrationSchema = useMemo(
    () => normalizeRegistrationFormSchema(settings?.registration_form_schema),
    [settings?.registration_form_schema],
  );
  const visibleRegistrationColumns = useMemo(
    () => tableRegistrationFields(normalizedRegistrationSchema),
    [normalizedRegistrationSchema],
  );
  const mexTourCupTemplateApplied = useMemo(
    () => isMexTourCupSchema({ version: 1, fields: registrationFormFields }),
    [registrationFormFields],
  );
  const hasKidsDivisions = useMemo(() => {
    const year = yearFromTournamentDate(tournament?.start_date);
    return divisions.some((division) => isKidsDivision(division, year));
  }, [divisions, tournament?.start_date]);
  // El sistema los prendió, pero ya no queda ninguna categoría de menores
  // (renombrada, desactivada o borrada): el aviso «Prendimos…» ya no sería
  // cierto, así que se explica que siguen prendidos y cómo apagarlos.
  const guardianStillOnWithoutMinors = useMemo(
    () =>
      Boolean(settings?.guardian_fields_auto_enabled) &&
      !hasKidsDivisions &&
      guardianMinimumFieldsOff(registrationFormFields).length === 0,
    [settings?.guardian_fields_auto_enabled, hasKidsDivisions, registrationFormFields],
  );
  // Con categorías infantiles, «mínimos» incluye los datos del tutor: sin
  // ellos el botón no dice «Ya está en campos mínimos».
  const minimalTemplateApplied = useMemo(
    () => isMinimalRegistrationSchema({ version: 1, fields: registrationFormFields }, { kids: hasKidsDivisions, credential: usesCredential }),
    [registrationFormFields, hasKidsDivisions, usesCredential],
  );
  // Editor agrupado por sección (Eduardo 2026-10-01): el organizador ve dónde
  // queda cada campo en el formulario público. El índice es el del arreglo.
  const registrationFieldGroups = useMemo(
    () =>
      REGISTRATION_FIELD_SECTIONS.map((section) => ({
        section,
        rows: registrationFormFields
          .map((field, index) => ({ field, index }))
          // CURP y foto van arriba, en «Documentos» (abajo).
          .filter(({ field }) => !EDITOR_DOCUMENT_KEYS.includes(field.key))
          .filter(({ field }) => fieldSection(field) === section),
      })).filter((group) => group.rows.length > 0),
    [registrationFormFields],
  );
  // Bloque «Documentos» arriba del editor (Eduardo 2026-10-30): CURP y foto
  // primero SOLO en esta vista; su `order` no cambia, así que el link
  // público y las listas de pendientes siguen igual.
  const registrationDocumentRows = useMemo(
    () =>
      EDITOR_DOCUMENT_KEYS.flatMap((key) => {
        const index = registrationFormFields.findIndex((field) => field.key === key);
        return index >= 0 ? [{ field: registrationFormFields[index], index }] : [];
      }),
    [registrationFormFields],
  );

  const paidPercent = useMemo(() => {
    if (!summary || summary.total_orders === 0) return 0;
    return Math.round((summary.paid_orders / summary.total_orders) * 100);
  }, [summary]);

  const teamPartnerByPlayerId = useMemo(() => {
    const map = new Map<number, { teamParticipant: TournamentParticipant; partnerName: string }>();
    participants.forEach((participant) => {
      if (participant.participant_kind !== "team" || participant.current_status === "dropped" || !participant.team?.members?.length) return;
      participant.team.members.forEach((member) => {
        map.set(member.player_profile_id, { teamParticipant: participant, partnerName: teamPartnerNames(participant, member.player_profile_id) });
      });
    });
    return map;
  }, [participants]);

  // Lo mismo por jugador Y categoría (revisión ronda 1): en un pago de varias
  // categorías, el renglón de dobles cuya fila individual reemplazó la pareja
  // se lee «En pareja con …» y cuenta en esa categoría, no «De baja».
  const teamPartnerNameByPlayerDivision = useMemo(() => {
    const map = new Map<string, string>();
    participants.forEach((participant) => {
      if (participant.participant_kind !== "team" || participant.current_status === "dropped" || !participant.team?.members?.length) return;
      participant.team.members.forEach((member) => {
        map.set(`${member.player_profile_id}:${participant.division_id ?? "none"}`, teamPartnerNames(participant, member.player_profile_id));
      });
    });
    return map;
  }, [participants]);

  // Revisión ronda 2: un participante puede quedar como renglón de dos
  // órdenes (una rechazada y su nueva inscripción); se cuenta y se muestra
  // en una sola.
  const coverageOwners = useMemo(() => coverageOwnerByParticipant(orders), [orders]);
  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let alive = true;
    api.groups
      .replacePlayed(token, tournamentId)
      .then((response) => {
        if (alive) setPlayedIds(new Set(response.data.participant_ids));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [token, tournamentId, orders]);

  function coverageOptionsFor(order: RegistrationOrder): CoverageOptions {
    const ownerOrderIdFor = (participantId: number) => coverageOwners.get(participantId) ?? null;
    // Los renglones de una orden son del mismo jugador que el ancla.
    const profileId = order.participant?.player_profile_id ?? order.player_profile_id ?? null;
    if (profileId === null) return { ownerOrderIdFor };
    return {
      ownerOrderIdFor,
      partnerNameFor: (divisionId) =>
        teamPartnerNameByPlayerDivision.get(`${profileId}:${divisionId ?? "none"}`)
        // Pareja sin categoría: explica la baja en cualquiera (como partnerInfoForOrder).
        ?? teamPartnerNameByPlayerDivision.get(`${profileId}:none`)
        ?? null,
    };
  }

  // La pareja solo explica la baja del registro individual de SU categoría:
  // si el mismo jugador tiene además un registro de singles dado de baja, ese
  // sí es un rechazo (Eduardo 2026-09-19).
  function partnerInfoForOrder(order: RegistrationOrder) {
    const participant = order.participant;
    if (!participant?.player_profile_id) return null;
    const info = teamPartnerByPlayerId.get(participant.player_profile_id);
    if (!info) return null;
    const teamDivisionId = info.teamParticipant.division_id ?? null;
    if (teamDivisionId !== null && (participant.division_id ?? null) !== teamDivisionId) return null;
    return info;
  }

  // Estado del pago de la fila. No es «Rechazado» si el jugador sigue
  // jugando con este pago: en pareja, o en otra categoría que la misma orden
  // cubre y no se dio de baja (revisión ronda 1, caso Oscar Zajiontz).
  function paymentStateFor(order: RegistrationOrder) {
    return registrationPaymentVisualState(
      order,
      Boolean(partnerInfoForOrder(order)) || orderInPlayOtherCategories(order, coverageOptionsFor(order)).length > 0,
    );
  }

  const visualStatusCounts = useMemo(() => {
    const counts = PAYMENT_VISUAL_STATES.reduce<Record<string, number>>((acc, item) => {
      acc[item.key] = 0;
      return acc;
    }, {});
    orders.forEach((order) => {
      // Con pareja formada, el registro individual dado de baja NO cuenta
      // como "Rechazado": se cuenta por el estado real de su pago.
      const state = paymentStateFor(order);
      counts[state.key] = (counts[state.key] ?? 0) + 1;
    });
    return counts;
  }, [orders, teamPartnerByPlayerId, teamPartnerNameByPlayerDivision, coverageOwners]);

  // "Registros recibidos" lista SOLO jugadores individuales: el pago y la
  // inscripción son POR JUGADOR (decisión Eduardo, caso t54 — la fila de la
  // pareja triplicaba registros). La pareja sigue visible en el detalle
  // expandido de cada integrante ("Equipo creado") y en Jugadores → parejas.
  // Las órdenes ancladas a un equipo quedan tras un toggle discreto para
  // poder auditarlas/cancelarlas (lección t54: hubo que cancelar órdenes de
  // pareja erróneas desde el panel).
  const teamOrders = useMemo(() => orders.filter(isTeamAnchoredOrder), [orders]);
  const scopedOrders = useMemo(
    () => scopeOrdersToIndividuals(orders, showTeamOrders),
    [orders, showTeamOrders],
  );

  const registrationCategorySummary = useMemo(() => {
    const byDivision = new Map<number | "none", { id: number | "none"; name: string; total: number; pendingPay: number }>();
    for (const order of scopedOrders) {
      // Un pago puede cubrir varias categorías (caso Oscar Zajiontz, torneo
      // 67): la fila cuenta en cada categoría viva que cubre, como Formato.
      for (const covered of orderCoveredCategories(order, coverageOptionsFor(order))) {
        const entry = byDivision.get(covered.key) ?? { id: covered.key, name: covered.name, total: 0, pendingPay: 0 };
        entry.total += 1;
        // "Deben" por categoría: cancelados/reembolsados/bajas no son deuda,
        // pero dar de baja el ancla no apaga la deuda de otra categoría viva.
        if (covered.owed) entry.pendingPay += 1;
        byDivision.set(covered.key, entry);
      }
    }
    return [...byDivision.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [scopedOrders, teamPartnerNameByPlayerDivision, coverageOwners]);

  // Revisión ronda 2: si el chip de la categoría filtrada desaparece (p. ej.
  // «También dar de baja» quitó su última fila), el filtro se suelta solo;
  // si no, la lista quedaba vacía sin chip activo para quitarlo.
  useEffect(() => {
    if (registrationCategoryFilter === null) return;
    if (!registrationCategorySummary.some((entry) => entry.id === registrationCategoryFilter)) {
      setRegistrationCategoryFilter(null);
    }
  }, [registrationCategoryFilter, registrationCategorySummary]);

  const registrationTotals = useMemo(() => ({
    // Mismo criterio que «deben» de los chips. Solo pantalla: el cobro en
    // lote y el recordatorio siguen con registrationOrderCollectable.
    pendingPay: scopedOrders.filter((order) => registrationOrderOwes(order, coverageOptionsFor(order))).length,
    incompleteDocs: scopedOrders.filter((order) => !registrationDocsComplete(order, usesCredential)).length,
    // CURP por revisar: no toca «Listos ✓» ni «Docs incompletos» (D8).
    toReviewCurp: countCurpToReview(scopedOrders),
    uncheckedCurp: countCurpUnchecked(scopedOrders),
    usesCurp: ordersUseCurp(scopedOrders),
    // Solo para mostrar el número del recuadro «Listos» (mismo criterio que su filtro).
    ready: scopedOrders.filter((order) => order.status === "paid" && registrationDocsComplete(order, usesCredential)).length,
  }), [scopedOrders, teamPartnerNameByPlayerDivision, coverageOwners, usesCredential]);
  // Caso Tecamachalco (2026-10-27): sin aprobar, no sale la bienvenida.
  const pendingApproval = useMemo(() => pendingApprovalCounts(orders), [orders]);
  const ordersReady = hasLoadedOnce && !loading && loadedTournamentId === tournamentId;
  // Aprobada la última, el filtro se suelta solo (si no, lista vacía sin aviso
  // para quitarlo).
  useEffect(() => {
    if (registrationWorkFilter === "aprobar" && pendingApproval.total === 0 && ordersReady) {
      setRegistrationWorkFilter(null);
    }
  }, [registrationWorkFilter, pendingApproval.total, ordersReady]);
  // «Ver pendientes»: filtra SOLO por «tiene algo que aprobar» (sin categoría
  // ni búsqueda encima), así las filas son exactamente las N del aviso.
  function showPendingApproval() {
    setRegistrationCategoryFilter(null);
    setRegistrationSearch("");
    setRegistrationWorkFilter("aprobar");
    window.setTimeout(() => {
      document.getElementById("registros-recibidos")?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 60);
  }
  // «Ver pendientes» desde el inicio del torneo (?pendientes=1), también con
  // la pantalla ya montada (revisión de Torni al PR #593): el parámetro se
  // aplica una vez con las órdenes de ESTE torneo y se quita de la URL
  // (replace), así quitar el filtro no deja la URL diciendo lo contrario y
  // volver a llegar con ?pendientes=1 lo reactiva. Sin bucle: tras quitarlo,
  // el efecto ya no encuentra el parámetro.
  useEffect(() => {
    if (!ordersReady) return;
    const params = new URLSearchParams(location.search);
    if (params.get("pendientes") !== "1") return;
    showPendingApproval();
    params.delete("pendientes");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true, state: location.state },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersReady, location.search, location.pathname]);
  // Torneo cerrado = solo lectura: sin botones de la revisión de CURP.
  const tournamentReadOnly = isClosedLike(tournament?.status);
  // Sub-usuario del Equipo: no cambia datos globales del jugador (D6).
  const isSubUser = Boolean(user?.managed_by_user_id);

  const visibleOrders = useMemo(() => {
    const query = normalizeSearch(registrationSearch);
    return scopedOrders.filter((order) => {
      if (
        registrationCategoryFilter !== null
        && !orderCoveredCategories(order, coverageOptionsFor(order)).some((covered) => covered.key === registrationCategoryFilter)
      ) {
        return false;
      }
      if (registrationWorkFilter === "pago" && !registrationOrderOwes(order, coverageOptionsFor(order))) return false;
      if (registrationWorkFilter === "docs" && registrationDocsComplete(order, usesCredential)) return false;
      if (registrationWorkFilter === "curp" && !curpNeedsReview(order)) return false;
      if (registrationWorkFilter === "aprobar" && !registrationAwaitingApproval(order, coverageOptionsFor(order))) return false;
      if (
        registrationWorkFilter === "listos"
        && !(order.status === "paid" && registrationDocsComplete(order, usesCredential))
      ) return false;
      if (!query) return true;
      const participant = order.participant;
      const haystack = normalizeSearch([
        order.id,
        participantDisplayName(participant),
        participant?.division?.name,
        ...orderOtherCategories(order, coverageOptionsFor(order)).map((other) => other.name),
        participant?.player?.email,
        participant?.player?.phone,
        sourceLabel(participant?.registration_source),
        participantStatusLabel(participant?.current_status),
        paymentStateFor(order).label,
      ].join(" "));
      return haystack.includes(query);
    });
  }, [scopedOrders, registrationSearch, registrationCategoryFilter, registrationWorkFilter, teamPartnerByPlayerId, teamPartnerNameByPlayerDivision, coverageOwners, usesCredential]);

  // Hallazgo 5 (#97): la selección y el detalle expandido siguen al alcance
  // visible — al filtrar/buscar/recargar, el contador del botón de lote nunca
  // puede prometer más de lo que realmente va a operar.
  useEffect(() => {
    const requested = Number(new URLSearchParams(location.search).get("order"));
    if (!Number.isFinite(requested) || requested <= 0 || returnOrderHandledRef.current === requested) return;
    if (!orders.some((order) => order.id === requested)) return;
    returnOrderHandledRef.current = requested;
    setExpandedOrderId(requested);
    window.setTimeout(() => {
      document.getElementById(`registration-order-${requested}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
  }, [orders, location.search]);

  useEffect(() => {
    const visibleCollectableIds = new Set(
      visibleOrders.filter(registrationOrderCollectable).map((order) => order.id),
    );
    setSelectedOrderIds((current) => reconcileOrderSelection(current, visibleCollectableIds));
    setExpandedOrderId((current) =>
      current !== null && !visibleOrders.some((order) => order.id === current) ? null : current,
    );
  }, [visibleOrders]);

  const doublesDivisionIds = useMemo(
    () => new Set(divisions.filter((division) => division.play_mode === "doubles").map((division) => division.id)),
    [divisions],
  );
  const hasDoublesRegistrations = useMemo(
    () => orders.some((order) => {
      const division = order.participant?.division;
      const divisionId = order.participant?.division_id;
      return division?.play_mode === "doubles" || (divisionId != null && doublesDivisionIds.has(divisionId));
    }),
    [doublesDivisionIds, orders],
  );
  function isDoublesOrder(order: RegistrationOrder) {
    const participant = order.participant;
    if (!participant) return false;
    return participant.division?.play_mode === "doubles" || (participant.division_id != null && doublesDivisionIds.has(participant.division_id));
  }

  // Eduardo 2026-09-16: el link público ya pide "¿Quién es tu pareja?" en
  // categorías de dobles/equipos; aquí se usa esa respuesta para sugerir el
  // vínculo (nombre declarado ≈ nombre de otro inscrito de la misma categoría,
  // o el otro declaró a este jugador).
  function normalizeNameForMatch(value: string | null | undefined): string {
    return (value ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function namesLookAlike(declared: string | null | undefined, candidateName: string): boolean {
    const a = normalizeNameForMatch(declared);
    const b = normalizeNameForMatch(candidateName);
    if (!a || !b) return false;
    if (a === b || a.includes(b) || b.includes(a)) return true;
    const tokensA = a.split(" ").filter((token) => token.length > 2);
    const tokensB = new Set(b.split(" ").filter((token) => token.length > 2));
    if (tokensA.length === 0) return false;
    const shared = tokensA.filter((token) => tokensB.has(token)).length;
    return shared >= Math.min(2, tokensA.length);
  }

  function declaredPartnerName(order: RegistrationOrder): string | null {
    const raw = order.participant?.registration_custom_answers?.partner_name;
    const value = typeof raw === "string" ? raw.trim() : "";
    return value || null;
  }

  /** Candidato sugerido: el que coincide con la pareja declarada por este
   * jugador, o el que declaró a este jugador como su pareja. */
  function suggestedPartnerOrder(order: RegistrationOrder, candidates: RegistrationOrder[]): RegistrationOrder | null {
    const declared = declaredPartnerName(order);
    const myName = participantDisplayName(order.participant);
    const byDeclaration = declared
      ? candidates.find((candidate) => namesLookAlike(declared, participantDisplayName(candidate.participant)))
      : undefined;
    if (byDeclaration) return byDeclaration;
    const reciprocal = candidates.find((candidate) => namesLookAlike(declaredPartnerName(candidate), myName));
    return reciprocal ?? null;
  }

  function availablePartnerOrders(order: RegistrationOrder) {
    const participant = order.participant;
    if (!participant?.player_profile_id || participant.division_id == null) return [];
    return orders.filter((candidate) => {
      const candidateParticipant = candidate.participant;
      if (!candidateParticipant?.player_profile_id) return false;
      if (candidateParticipant.id === participant.id) return false;
      if (candidateParticipant.division_id !== participant.division_id) return false;
      if (teamPartnerByPlayerId.has(candidateParticipant.player_profile_id)) return false;
      return candidateParticipant.current_status !== "dropped";
    });
  }

  const loadDeletedRegistrations = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.participants.listDeletedRegistrations(token, tournamentId);
      setDeletedRegistrations(response.data.deleted_registrations ?? []);
    } catch {
      // la auditoría no bloquea la pantalla
      setDeletedRegistrations([]);
    }
  }, [token, tournamentId]);

  const loadDashboard = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    const seq = ++loadSeqRef.current;
    const closedNowSeqAtStart = closedNowSeqRef.current;
    setLoading(true);
    setError(null);
    void loadDeletedRegistrations();
    try {
      const [tournamentResponse, registrationResponse, divisionsResponse, participantsResponse] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.registrations.get(token, tournamentId),
        api.tournaments.listDivisions(token, tournamentId),
        api.participants.list(token, tournamentId),
      ]);
      if (seq !== loadSeqRef.current) return;
      const nextSettings = registrationResponse.data.settings;
      const loadedTournament = tournamentResponse.data.tournament;
      setTournament((current) =>
        closedNowSeqAtStart !== closedNowSeqRef.current && current?.id === loadedTournament.id
          ? { ...loadedTournament, registrations_closed_now: current.registrations_closed_now }
          : loadedTournament,
      );
      setAutoSendWelcome(tournamentResponse.data.settings?.auto_send_welcome !== false);
      setSettings(nextSettings);
      setSummary(registrationResponse.data.summary);
      setOrders(registrationResponse.data.orders);
      setLoadedTournamentId(tournamentId);
      setParticipants(participantsResponse.data.participants);
      setDivisions(divisionsResponse.data.divisions);
      setRegistrationFormFields(normalizeRegistrationFormSchema(nextSettings.registration_form_schema).fields);
      try {
        const readiness = await api.matches.scheduleReadiness(token, tournamentId);
        if (seq === loadSeqRef.current) setReadinessRows(readiness.data.divisions);
      } catch {
        // El aviso de draw es informativo: si falla, la pantalla sigue.
        if (seq === loadSeqRef.current) setReadinessRows([]);
      }
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar inscripciones.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [token, tournamentId, loadDeletedRegistrations]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  // «Ver» de foto, CURP y comprobante abre ligas firmadas que vencen en una
  // hora. Con la revisión de CURPs el organizador se queda mucho rato aquí:
  // antes de que venzan (al volver a la pestaña o cada minuto) se piden ligas
  // nuevas y SOLO se cambia la liga de cada archivo; lo demás de la pantalla
  // no se toca. Si falla, se queda lo que ya se ve.
  const documentLinks = useMemo(() => registrationDocumentLinks(orders), [orders]);
  const refreshDocumentLinks = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    const response = await api.registrations.get(token, tournamentId);
    setOrders((current) => renewRegistrationDocumentLinks(current, response.data.orders));
  }, [token, tournamentId]);
  useSignedLinkRefresh(documentLinks, refreshDocumentLinks);

  // Cambiar de torneo sí vuelve al spinner completo (no mostrar inscripciones
  // del torneo anterior mientras carga el nuevo).
  useEffect(() => {
    setHasLoadedOnce(false);
    // Lo del editor y del Sí/No de la credencial era del torneo anterior.
    setEditingRegistrationFields(false);
    setPhotoLockNotice(null);
    credentialSeqRef.current += 1;
    credentialBusyRef.current = false;
    setSavingCredentialEnabled(false);
    closedNowSeqRef.current += 1;
    closedNowBusyRef.current = false;
    setSavingClosedNow(false);
  }, [tournamentId]);

  // Anti-duplicados: buscar perfiles parecidos mientras se teclea.
  useEffect(() => {
    if (!token || qeUsando) return;
    const term = (qeTelefono || qeEmail || `${qeNombre} ${qeApellido}`).trim();
    if (term.length < 3) {
      setQeMatches([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.players.search(token, { search: term, limit: 3 });
        setQeMatches(response.data.players);
      } catch {
        setQeMatches([]);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [token, qeNombre, qeApellido, qeTelefono, qeEmail, qeUsando]);

  const qeDivisionesActivas = divisions.filter((division) => division.is_active !== false);
  const qeRequiereCategoria = qeDivisionesActivas.length > 0;
  const qeSinCategoria = qeRequiereCategoria && qeDivisiones.size === 0;
  const qeDivisionNames = divisions.filter((division) => qeDivisiones.has(division.id)).map((division) => division.name);
  const qeRestriccionSinDatos = qeDivisionesActivas.some(
    (division) =>
      qeDivisiones.has(division.id) &&
      ((division.gender && !qeGenero && !qeUsando) ||
        ((division.age_min != null || division.age_max != null) && !qeNacimiento && !qeUsando)),
  );
  const qeFeeOptions: Array<{ value: string; label: string }> = [
    { value: "general", label: `General${settings?.entry_fee_amount ? ` — $${settings.entry_fee_amount}` : ""}` },
    ...(settings?.member_fee_amount != null
      ? [{ value: "socio", label: `${feeCategoryLabel("socio", settings)} — $${settings.member_fee_amount}` }]
      : []),
    ...(settings?.coach_fee_amount != null
      ? [{ value: "profesor", label: `${feeCategoryLabel("profesor", settings)} — $${settings.coach_fee_amount}` }]
      : []),
  ];

  async function handleQuickEnroll(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    if (!qeUsando && !qeNombre.trim()) return;
    if (qeSinCategoria) {
      setError("Selecciona al menos una categoría.");
      return;
    }
    // Dos o más categorías: se confirma (acción del admin: pulsó Inscribir).
    if (
      qeDivisionNames.length > 1
      && !(await askConfirm(
        `¿Inscribir a ${qeUsando?.display_name ?? `${qeNombre.trim()} ${qeApellido.trim()}`.trim()} en ${qeDivisionNames.length} categorías?\n\n${qeDivisionNames.join(" + ")}`,
        { acceptLabel: `Sí, en las ${qeDivisionNames.length}`, dismissLabel: "No, regresar" },
      ))
    ) {
      return;
    }
    setQeSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.quickEnroll(token, tournamentId, {
        division_ids: Array.from(qeDivisiones),
        player_profile_id: qeUsando?.id ?? null,
        new_player: qeUsando
          ? null
          : {
              first_name: qeNombre.trim(),
              last_name: qeApellido.trim() || null,
              phone: qeTelefono.trim() || null,
              email: qeEmail.trim() || null,
              gender: qeGenero || null,
              birthdate: qeNacimiento || null,
            },
        fee_category: qeFee,
        mark_paid: qeMarkPaid,
      });
      const nombre = qeUsando?.display_name ?? `${qeNombre.trim()} ${qeApellido.trim()}`.trim();
      const cuantas = response.data.participants.length;
      // Costo por categoría adicional: el servidor decidió el precio; el
      // admin ve que se aplicó (y el total) para avisarle al jugador.
      const adicionales = (response.data.order.items ?? []).filter((item) => item.is_additional_category);
      setMessage(
        `${nombre} inscrito en ${cuantas} categoría${cuantas === 1 ? "" : "s"}` +
          (qeMarkPaid ? " con pago registrado." : " con orden pendiente.") +
          (adicionales.length > 0
            ? ` Se aplicó el costo por categoría adicional en ${adicionales
                .map((item) => item.division_name ?? "otra categoría")
                .join(", ")}: total ${money(response.data.order.gross_amount, response.data.order.currency)}.`
            : ""),
      );
      // Flujo de mostrador: limpiar a la persona, CONSERVAR categorías,
      // costo y "ya pagó" para encadenar la siguiente alta sin reconfigurar.
      setQeNombre("");
      setQeApellido("");
      setQeTelefono("");
      setQeEmail("");
      setQeGenero("");
      setQeNacimiento("");
      setQeUsando(null);
      setQeMatches([]);
      await loadDashboard();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudo inscribir."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setQeSubmitting(false);
    }
  }

  function updateRegistrationField(index: number, patch: Partial<RegistrationFormField>) {
    setRegistrationFormFields((current) => current.map((field, fieldIndex) => {
      if (fieldIndex !== index) return field;
      const locked = Boolean(field.locked);
      return {
        ...field,
        ...patch,
        enabled: locked ? true : (patch.enabled ?? field.enabled),
        required: locked ? true : (patch.required ?? field.required),
      };
    }));
  }

  function addCustomRegistrationField() {
    const nextIndex = registrationFormFields.length + 1;
    setRegistrationFormFields((current) => [
      ...current,
      {
        key: `custom_${Date.now()}`,
        kind: "custom",
        type: "text",
        label: "Nuevo campo",
        required: false,
        enabled: true,
        show_in_players_table: true,
        order: nextIndex * 10,
      },
    ]);
    setEditingRegistrationFields(true);
  }

  function applyMinimalRegistrationTemplate() {
    // Con categorías infantiles también van los datos del papá, mamá o tutor
    // (Eduardo 2026-10-01): con su correo se arma la Cuenta Familiar.
    const kids = hasKidsDivisions;
    setRegistrationFormFields(minimalRegistrationFormSchema({ kids, credential: usesCredential }).fields);
    setEditingRegistrationFields(true);
    setMessage(
      (kids
        ? "Campos mínimos cargados: Nombre, Apellido paterno y Correo, más los datos del papá, mamá o tutor porque hay categorías infantiles. Presiona Guardar campos."
        : "Campos mínimos cargados: solo Nombre, Apellido paterno y Correo. Enciende los que necesites y presiona Guardar campos.") +
        (usesCredential ? ` ${CREDENTIAL_PHOTO_FORCED_TEXT}` : ""),
    );
    setError(null);
  }

  function applyMexTourCupRegistrationTemplate() {
    setRegistrationFormFields(mexTourCupRegistrationFormSchema({ credential: usesCredential }).fields);
    setEditingRegistrationFields(true);
    setMessage(
      "Plantilla Mex Tour Cup cargada. Revisa los campos y presiona Guardar campos para activar el link público." +
        (usesCredential ? ` ${CREDENTIAL_PHOTO_FORCED_TEXT}` : ""),
    );
    setError(null);
  }

  async function handleSaveRegistrationFields() {
    if (!token) return;
    const requestTournamentId = tournamentId;
    setSavingRegistrationFields(true);
    setError(null);
    setMessage(null);
    try {
      let schema = normalizeRegistrationFormSchema({ version: 1, fields: registrationFormFields });
      // Con credencial la foto se ve con candado Activa y Obligatoria: se
      // guarda así (un torneo de antes de la regla queda arreglado al guardar).
      let photoForced = false;
      if (usesCredential) {
        const forced = withCredentialPhoto(schema);
        schema = forced.schema;
        photoForced = forced.forced;
      }
      const response = await api.registrations.updateFormFields(token, tournamentId, schema);
      if (currentTournamentRef.current !== requestTournamentId) return;
      setSettings(response.data.settings);
      setOrders(response.data.orders);
      setSummary(response.data.summary);
      setRegistrationFormFields(normalizeRegistrationFormSchema(response.data.settings.registration_form_schema).fields);
      setEditingRegistrationFields(false);
      setPhotoLockNotice(null);
      setMessage(
        "Campos del formulario de inscripción guardados. El link público ya usará esta configuración." +
          (photoForced ? ` ${CREDENTIAL_PHOTO_FORCED_TEXT}` : ""),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos guardar los campos del formulario.");
    } finally {
      setSavingRegistrationFields(false);
    }
  }


  async function loadFormSources() {
    if (!token || formSources !== null) return;
    try {
      const response = await api.registrations.formSources(token, tournamentId);
      setFormSources(response.data.sources);
    } catch (err) {
      setFormSources([]);
      setError(err instanceof ApiError ? err.message : "No pudimos cargar tus otros formularios.");
    }
  }

  async function handleCopyFormSource() {
    if (!token || !formSourceId) return;
    const source = formSources?.find((item) => String(item.tournament_id) === formSourceId);
    if (!source) return;
    const confirmed = await askConfirm(
      `¿Copiar el formulario de «${source.name}» (${source.field_count} campos)? Reemplaza los campos de este torneo; las inscripciones ya recibidas conservan sus respuestas.`,
      { acceptLabel: "Copiar formulario" },
    );
    if (!confirmed) return;
    const requestTournamentId = tournamentId;
    setCopyingFormSource(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.copyFormFrom(token, tournamentId, source.tournament_id);
      if (currentTournamentRef.current !== requestTournamentId) return;
      setSettings(response.data.settings);
      setOrders(response.data.orders);
      setSummary(response.data.summary);
      setRegistrationFormFields(normalizeRegistrationFormSchema(response.data.settings.registration_form_schema).fields);
      setEditingRegistrationFields(false);
      setFormSourceId("");
      setPhotoLockNotice(null);
      setMessage(
        `Formulario copiado de «${source.name}». El link público ya usa estos campos.` +
          (response.data.photo_forced ? ` ${CREDENTIAL_PHOTO_FORCED_TEXT}` : ""),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos copiar el formulario.");
    } finally {
      setCopyingFormSource(false);
    }
  }

  function toggleFieldSection(section: string) {
    setCollapsedFieldSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  // «Cerrar inscripciones ahora»: una sola acción a la vez, desde que se abre
  // la confirmación, y solo la respuesta del torneo en pantalla aplica.
  async function handleToggleClosedNow(next: boolean) {
    if (!token || closedNowBusyRef.current) return;
    closedNowBusyRef.current = true;
    const seq = ++closedNowSeqRef.current;
    const requestTournamentId = tournamentId;
    // `aborted` cubre también al desmontar: ahí nada cambia los refs de arriba.
    const screenSignal = currentSignal();
    const isCurrent = () =>
      seq === closedNowSeqRef.current && currentTournamentRef.current === requestTournamentId && !screenSignal?.aborted;
    setSavingClosedNow(true);
    setError(null);
    setMessage(null);
    try {
      if (next) {
        const confirmed = await askConfirm(
          "¿Cerrar las inscripciones ahora? El link público dejará de recibir inscripciones nuevas. Quien ya tiene orden puede terminar de pagar y tú puedes seguir dando altas manuales. Tus fechas no cambian.",
          { acceptLabel: "Sí, cerrar inscripciones", tone: "danger" },
        );
        if (!confirmed || !isCurrent()) return;
      }
      const response = next
        ? await api.registrations.closeRegistrationsNow(token, requestTournamentId)
        : await api.registrations.reopenRegistrations(token, requestTournamentId);
      if (!isCurrent()) return;
      const closed = response.data.registrations_closed_now;
      setTournament((current) => (current?.id === requestTournamentId ? { ...current, registrations_closed_now: closed } : current));
      setMessage(closed ? "Inscripciones cerradas." : "Inscripciones reabiertas: mandan tus fechas.");
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : "No pudimos guardar el cambio. Intenta otra vez.");
    } finally {
      if (seq === closedNowSeqRef.current) {
        closedNowBusyRef.current = false;
        setSavingClosedNow(false);
      }
    }
  }

  // «Este torneo usa credencial» (Eduardo 2026-10-05): el servidor aplica el
  // candado del torneo cerrado y los permisos del equipo.
  // Eduardo 2026-10-30: al decir «Sí», el servidor deja la foto Activa y
  // Obligatoria en la misma transacción; al decir «No», no toca el formulario.
  async function handleToggleCredentialEnabled(next: boolean) {
    if (!token || credentialBusyRef.current) return;
    credentialBusyRef.current = true;
    const seq = ++credentialSeqRef.current;
    const requestTournamentId = tournamentId;
    const isCurrent = () => seq === credentialSeqRef.current && currentTournamentRef.current === requestTournamentId;
    setSavingCredentialEnabled(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.setCredentialEnabled(token, tournamentId, next);
      if (!isCurrent()) return;
      setSettings(response.data.settings);
      if (next) {
        // Con el editor abierto solo cambia la foto: lo demás que el
        // organizador lleve sin guardar se queda como está.
        setRegistrationFormFields((current) =>
          current.map((field) => (field.key === CREDENTIAL_PHOTO_KEY ? { ...field, enabled: true, required: true } : field)),
        );
      } else {
        setPhotoLockNotice(null);
      }
      setMessage(
        next
          ? "Este torneo usa credencial: la «Foto de medio cuerpo» quedó Activa y Obligatoria en el formulario (03)."
          : "Este torneo ya no usa credencial: el link de inscripción deja de pedir la foto de medio cuerpo.",
      );
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : "No pudimos guardar el cambio. Intenta otra vez.");
    } finally {
      if (seq === credentialSeqRef.current) {
        credentialBusyRef.current = false;
        setSavingCredentialEnabled(false);
      }
    }
  }

  // Credencial de muestra (Eduardo 2026-08-19): ver el formato con datos de
  // ejemplo aunque nadie tenga foto todavía — usa la identidad real de Datos.
  async function handleViewCredentialSample() {
    if (!token || loadingCredentialSample) return;
    setLoadingCredentialSample(true);
    setError(null);
    try {
      const response = await api.registrations.credentialSample(token, tournamentId);
      const win = window.open("", "_blank");
      if (!win) {
        setError("El navegador bloqueó la ventana. Permite pop-ups para este sitio e intenta de nuevo.");
        return;
      }
      win.document.write(
        `<!doctype html><html><head><meta charset="utf-8" /><title>Credencial muestra</title>` +
          `<style>body{margin:0;background:#111;display:flex;justify-content:center;}svg{max-width:100%;height:auto;}</style>` +
          `</head><body>${response.data.svg}</body></html>`,
      );
      win.document.close();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo generar la credencial de muestra.");
    } finally {
      setLoadingCredentialSample(false);
    }
  }

  async function handleGenerateCredential(order: RegistrationOrder) {
    if (!token) return;
    setGeneratingCredentialId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.generateCredential(token, tournamentId, order.id);
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setMessage(`Credencial generada para ${participantDisplayName(response.data.order.participant)}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos generar la credencial.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setGeneratingCredentialId(null);
    }
  }

  async function handleUploadDocument(order: RegistrationOrder, documentType: RegistrationDocumentType, file: File | null | undefined) {
    if (!token || !file) return;
    const key = `${order.id}:${documentType}`;
    setUploadingDocumentKey(key);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.uploadDocument(token, tournamentId, order.id, documentType, file);
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      const label = documentType === "photo" ? "foto" : documentType === "curp" ? "CURP" : "comprobante";
      const previousDocs = order.participant?.registration_documents;
      const credentialReset = documentType === "photo" && (previousDocs?.credential_url || previousDocs?.has_credential)
        ? " La credencial anterior se limpió; genera una nueva para usar la foto actualizada."
        : "";
      // La CURP se compara al subirla: el resultado contesta la acción del admin.
      const curpResult = documentType === "curp" ? curpUploadResultText(response.data.order.curp_check) : "";
      setMessage(`Se actualizó ${label} de ${participantDisplayName(response.data.order.participant)}.${credentialReset}${curpResult ? ` ${curpResult}` : ""}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos subir el documento.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setUploadingDocumentKey(null);
    }
  }

  function uploadDocumentButton(order: RegistrationOrder, documentType: RegistrationDocumentType, label: string, accept: string) {
    const key = `${order.id}:${documentType}`;
    const isUploading = uploadingDocumentKey === key;
    return (
      <label className={`button button--sm registration-document-upload ${isUploading ? "is-uploading" : ""}`}>
        {isUploading ? "Subiendo…" : label}
        <input
          accept={accept}
          disabled={isUploading}
          type="file"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            event.target.value = "";
            void handleUploadDocument(order, documentType, file);
          }}
        />
      </label>
    );
  }

  // --- Revisión de CURP vs nombre (avisa, nunca bloquea) -------------------
  // Cada acción manda el archivo que el admin vio (checked_url): si alguien
  // subió otra CURP mientras tanto, el servidor contesta 409 y se recarga.

  function replaceOrder(next: RegistrationOrder) {
    setOrders((current) => current.map((item) => (item.id === next.id ? next : item)));
  }

  async function handleCurpActionError(err: unknown, fallback: string) {
    // Vista vieja: la CURP cambió, ya se pidió otra o aún no se revisa.
    // Primero recargar (loadDashboard limpia el error) y luego avisar.
    if (err instanceof ApiError && ["curp_check_stale", "curp_already_requested", "curp_not_checked"].includes(err.code)) {
      await loadDashboard();
      setError(
        err.code === "curp_check_stale"
          ? "La CURP cambió mientras la veías (alguien subió otra). Ya actualizamos la lista: revísala de nuevo."
          : err.message || fallback,
      );
      return;
    }
    setError(err instanceof ApiError ? err.message || fallback : "Error de red. Intenta otra vez.");
  }

  async function handleApplyCurpData(order: RegistrationOrder) {
    const check = order.curp_check;
    if (!token || !check?.checked_url || !check.curp) return;
    const confirmed = await askConfirm(
      curpApplyConfirmText(participantDisplayName(order.participant), check, order.participant?.player),
      { acceptLabel: "Sí, usar datos de la CURP" },
    );
    if (!confirmed) return;
    setCurpReviewBusyId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.applyCurpData(token, tournamentId, order.id, { checked_url: check.checked_url });
      replaceOrder(response.data.order);
      setMessage(
        curpApplySuccessText(participantDisplayName(response.data.order.participant), response.data.applied, response.data.category_warnings),
      );
      // El perfil es global: otras inscripciones del mismo jugador en este
      // torneo también cambian de nombre.
      void loadDashboard();
    } catch (err) {
      await handleCurpActionError(err, "No pudimos usar los datos de la CURP.");
    } finally {
      setCurpReviewBusyId(null);
    }
  }

  async function handleRequestNewCurp(order: RegistrationOrder) {
    const check = order.curp_check;
    if (!token || !check?.checked_url || !curpCanRequestAgain(check)) return;
    const name = participantDisplayName(order.participant);
    const confirmed = await askConfirm(
      `¿Pedir otra CURP para ${name}?\n\nQuitamos esta CURP de su registro y le mandamos el aviso «Documentos pendientes» para que suba la correcta. El archivo anterior se conserva.`,
      { acceptLabel: "Sí, pedir otra CURP", tone: "danger" },
    );
    if (!confirmed) return;
    setCurpReviewBusyId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.requestNewCurp(token, tournamentId, order.id, { checked_url: check.checked_url });
      replaceOrder(response.data.order);
      setMessage(curpRequestResultText(name, response.data.message));
    } catch (err) {
      await handleCurpActionError(err, "No pudimos pedir otra CURP.");
    } finally {
      setCurpReviewBusyId(null);
    }
  }

  async function handleMarkCurpOk(order: RegistrationOrder) {
    const check = order.curp_check;
    if (!token || !check?.checked_url) return;
    setCurpReviewBusyId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.markCurpOk(token, tournamentId, order.id, { checked_url: check.checked_url });
      replaceOrder(response.data.order);
      setMessage(`CURP de ${participantDisplayName(order.participant)} marcada como revisada: se queda como está.`);
    } catch (err) {
      await handleCurpActionError(err, "No pudimos marcar la CURP como revisada.");
    } finally {
      setCurpReviewBusyId(null);
    }
  }

  async function handleReviewCurps() {
    if (!token) return;
    setReviewingCurps(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.reviewCurps(token, tournamentId);
      setMessage(curpReviewRunMessage(response.data));
      await loadDashboard();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No pudimos revisar las CURP." : "Error de red. Intenta otra vez.");
    } finally {
      setReviewingCurps(false);
    }
  }

  /** Recuadro de la CURP en Documentos: qué dice la CURP, qué está
   * registrado, por qué se marcó y los tres botones. Describe el estado
   * actual, así que nunca se vuelve aviso flotante (data-no-toast). */
  function renderCurpReviewBox(order: RegistrationOrder) {
    const check = order.curp_check;
    if (!check) return null;
    const actionOptions = { closed: tournamentReadOnly, subUser: isSubUser, player: order.participant?.player };
    const actions = curpReviewActions(check, actionOptions);
    const sexOnlyHint = curpSexOnlyHint(check, actionOptions);
    const busy = curpReviewBusyId === order.id;
    const reasons = curpReasonTexts(check);
    const notes = curpNoteTexts(check);
    const history = curpHistoryLine(check);
    const conflicts = actions.apply ? categoryConflictNames(check.category_conflicts_if_applied) : "";
    const showComparison = check.state !== "unchecked" && check.state !== "requested";
    return (
      <div
        className={`registration-curp-review registration-curp-review--${curpReviewTone(check)}`}
        data-no-toast
      >
        <p className="registration-curp-review__title"><strong>{curpReviewTitle(check)}</strong></p>
        {check.state === "unchecked" ? (
          <p className="registration-curp-review__line">
            {tournamentReadOnly
              ? "Aún no se compara con el registro."
              : "Aún no se compara con el registro. Pulsa «Revisar CURPs» arriba de la lista."}
          </p>
        ) : null}
        {showComparison && check.curp ? (
          <p className="registration-curp-review__line">
            <span>La CURP dice:</span> {formatCurpLine(check.curp)}
          </p>
        ) : null}
        {showComparison ? (
          <p className="registration-curp-review__line">
            <span>Registrado:</span> {formatRegisteredLine(order.participant?.player)}
          </p>
        ) : null}
        {showComparison && reasons.length > 0 ? (
          <ul className="registration-curp-review__reasons">
            {reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        ) : null}
        {showComparison && notes.length > 0 ? (
          <ul className="registration-curp-review__notes">
            {notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        ) : null}
        {history ? <p className="registration-curp-review__meta">{history}</p> : null}
        {conflicts ? (
          <p className="registration-curp-review__meta">Si usas los datos de la CURP, ya no cabría en: {conflicts}.</p>
        ) : null}
        {sexOnlyHint ? <p className="registration-curp-review__meta">{sexOnlyHint}</p> : null}
        {/* «Pedir otra CURP» desactivado: el porqué a la vista, no solo en el «?». */}
        {actions.request && !curpCanRequestAgain(check) ? (
          <p className="registration-curp-review__meta">{curpRequestHint(check)}</p>
        ) : null}
        {actions.apply || actions.request || actions.ok ? (
          <div className="registration-curp-review__actions">
            {actions.apply ? (
              <span className="registration-action-hint">
                <button
                  className="button button--sm"
                  disabled={busy}
                  onClick={() => void handleApplyCurpData(order)}
                  type="button"
                >
                  Usar datos de la CURP
                </button>
                <FieldHint label="Qué hace Usar datos de la CURP">
                  Cambia el nombre y la fecha de nacimiento del jugador por los de la CURP, en todos sus torneos. No cambia su sexo ni lo mueve de categoría: si ya no cabe, te avisamos para que lo cambies tú.
                </FieldHint>
              </span>
            ) : null}
            {actions.request ? (
              <span className="registration-action-hint">
                <button
                  className="button button--sm button--ghost"
                  disabled={busy || !curpCanRequestAgain(check)}
                  onClick={() => void handleRequestNewCurp(order)}
                  type="button"
                >
                  Pedir otra CURP
                </button>
                <FieldHint label="Qué hace Pedir otra CURP">
                  {curpRequestHint(check)}
                </FieldHint>
              </span>
            ) : null}
            {actions.ok ? (
              <span className="registration-action-hint">
                <button
                  className="button button--sm button--ghost"
                  disabled={busy}
                  onClick={() => void handleMarkCurpOk(order)}
                  type="button"
                >
                  Está bien así
                </button>
                <FieldHint label="Qué hace Está bien así">
                  Deja la CURP y los datos como están y la quita de «CURP por revisar». Úsalo cuando la diferencia no importa, por ejemplo un nombre abreviado.
                </FieldHint>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  // Petición cruda: lanza en caso de error para que el lote pueda contarlo.
  async function markPaidRequest(order: RegistrationOrder) {
    if (!token) throw new Error("Sin sesión");
    const response = await api.registrations.markPaid(token, tournamentId, order.id, {
      provider_reference: `manual-${new Date().toISOString().slice(0, 10)}`,
      notes: "Pago manual confirmado por admin",
    });
    setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
    setSummary(response.data.summary);
  }

  async function handleMarkPaid(order: RegistrationOrder) {
    if (!token) return;
    setMarkingPaidId(order.id);
    setError(null);
    setMessage(null);
    try {
      await markPaidRequest(order);
      setMessage(`Pago confirmado para ${participantDisplayName(order.participant)}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos marcar el pago.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setMarkingPaidId(null);
    }
  }

  async function handleBulkMarkPaid() {
    if (!token) return;
    const targets = visibleOrders.filter(
      (order) => selectedOrderIds.has(order.id) && registrationOrderCollectable(order),
    );
    if (targets.length === 0) return;
    if (!(await askConfirm(`¿Confirmar el pago de ${targets.length} registro(s) seleccionados?`, { acceptLabel: "Sí, confirmar pagos" }))) return;
    setBulkMarkingPaid(true);
    setError(null);
    setMessage(null);
    try {
      // Hallazgo 1 (#97): el lote contabiliza éxitos/fallos REALES; los
      // fallidos quedan seleccionados para reintentar — nunca "éxito por N".
      const result = await runBulkMarkPaid(targets, markPaidRequest);
      // Carrera filtros↔lote (2ª ronda): si un fallido dejó de estar
      // seleccionado mientras corría el lote (filtro/búsqueda/deselección),
      // NO se reintroduce — solo se conserva lo aún vigente.
      const failedIds = result.failed.map((order) => order.id);
      setSelectedOrderIds((current) => retainFailedSelection(failedIds, current));
      if (result.failed.length === 0) {
        setMessage(`Pago confirmado en lote: ${result.succeeded.length} registro(s).`);
      } else {
        if (result.succeeded.length > 0) {
          setMessage(`Pago confirmado en ${result.succeeded.length} de ${targets.length} registro(s).`);
        }
        const failedNames = result.failed
          .map((order) => participantDisplayName(order.participant))
          .join(", ");
        setError(
          `No pudimos confirmar ${result.failed.length} registro(s): ${failedNames}. Siguen seleccionados para reintentar.`,
        );
      }
    } finally {
      setBulkMarkingPaid(false);
    }
  }

  function toggleOrderSelected(orderId: number) {
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }

  // Reclasificación de tipo (Eduardo 2026-08-21): si el jugador no era
  // socio, lo pagado se respeta y solo se persigue la diferencia (saldo en
  // la misma orden). El backend arma el mensaje de WhatsApp para el admin.
  async function handleReclassifyFee(order: RegistrationOrder, nextCategory: string) {
    if (!token || nextCategory === (order.fee_category ?? "general")) return;
    const aviso = order.status === "paid"
      ? `Se conservará lo pagado (${money(order.gross_amount, currency)}) y la diferencia quedará como saldo pendiente. ¿Reclasificar a ${feeCategoryLabel(nextCategory)}?`
      : `El total pendiente se actualizará al precio de ${feeCategoryLabel(nextCategory, settings)}. ¿Reclasificar?`;
    if (!(await askConfirm(aviso, { acceptLabel: "Sí, reclasificar" }))) return;
    setEditingPaymentId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.reclassifyFee(token, tournamentId, order.id, nextCategory);
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setReclassifyWhatsapp((current) => ({ ...current, [order.id]: response.data.whatsapp_message }));
      const saldo = response.data.order.balance_due_amount;
      setMessage(
        saldo
          ? `Reclasificado. Saldo pendiente de ${money(saldo, currency)} — avisa al jugador con el botón de WhatsApp.`
          : "Reclasificado: el total pendiente ya refleja el tipo correcto.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo reclasificar.");
    } finally {
      setEditingPaymentId(null);
    }
  }

  // Reemitir una orden pendiente (Eduardo 2026-09-14: la orden 1080 quedó
  // congelada a $50 durante una prueba). Sin monto → costo vigente; con
  // monto → total fijado a mano. El backend expira el checkout viejo y arma
  // el mensaje de WhatsApp con el link de pago.
  async function handleReissueOrder(order: RegistrationOrder, amount: string | null) {
    if (!token) return;
    const manual = amount !== null && amount.trim() !== "";
    const aviso = manual
      ? `La orden pendiente (${money(order.gross_amount, currency)}) se reemitirá por ${money(amount.trim(), currency)}. Si tenía un link de pago abierto, se invalida. ¿Reemitir?`
      : `La orden pendiente (${money(order.gross_amount, currency)}) se actualizará al costo vigente de la categoría; si el total no cambia, solo se regenera el link de pago. Cualquier checkout abierto se invalida. ¿Continuar?`;
    if (!(await askConfirm(aviso, { acceptLabel: "Sí, continuar" }))) return;
    setEditingPaymentId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.reissueOrder(token, tournamentId, order.id, {
        amount: manual ? amount.trim() : null,
      });
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setReclassifyWhatsapp((current) => ({ ...current, [order.id]: response.data.whatsapp_message }));
      setReissueAmount((current) => ({ ...current, [order.id]: "" }));
      setMessage(
        response.data.regenerated_only
          ? `Link de pago regenerado (mismo total ${money(response.data.order.gross_amount, currency)}; checkouts anteriores invalidados: ${response.data.expired_checkouts ?? 0}). Pídele al jugador que vuelva a abrir su link y pague.`
          : `Orden reemitida: de ${money(response.data.previous_amount, currency)} a ${money(response.data.order.gross_amount, currency)}. Avisa al jugador con el botón de WhatsApp; su link de pago ya cobra el monto nuevo.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo reemitir la orden.");
    } finally {
      setEditingPaymentId(null);
    }
  }

  async function handleResolveBalance(order: RegistrationOrder, action: "paid" | "waived") {
    if (!token) return;
    const texto = action === "paid"
      ? "¿Confirmar que el jugador ya cubrió el saldo?"
      : "¿Condonar este saldo? No se le cobrará la diferencia.";
    if (!(await askConfirm(texto, { acceptLabel: "Sí, continuar" }))) return;
    setEditingPaymentId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.resolveBalance(token, tournamentId, order.id, action);
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setMessage(action === "paid" ? "Saldo marcado como pagado." : "Saldo condonado.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo actualizar el saldo.");
    } finally {
      setEditingPaymentId(null);
    }
  }

  async function handleCopyWhatsapp(orderId: number) {
    const mensaje = reclassifyWhatsapp[orderId];
    if (!mensaje) return;
    try {
      await navigator.clipboard.writeText(mensaje);
      setMessage("Mensaje copiado: pégalo en el WhatsApp del jugador.");
    } catch {
      setError("No se pudo copiar automáticamente; el mensaje se muestra abajo.");
    }
  }

  async function handleUpdatePayment(order: RegistrationOrder, nextStatus: RegistrationPaymentStatus) {
    if (!token) return;
    if (
      (nextStatus === "refunded" || nextStatus === "cancelled") &&
      !(await askConfirm(
        nextStatus === "refunded"
          ? "¿Marcar este pago como reembolsado?"
          : "¿Marcar esta inscripción como rechazada?", { acceptLabel: nextStatus === "refunded" ? "Sí, marcar reembolsado" : "Sí, rechazar inscripción", tone: "danger" }))
    ) {
      return;
    }
    // Deshacer un pago ya confirmado también es delicado — confirmar.
    if (
      order.status === "paid" &&
      nextStatus === "pending" &&
      !(await askConfirm("Este pago ya estaba confirmado. ¿Regresarlo a pendiente?", { acceptLabel: "Sí, regresar a pendiente" }))
    ) {
      return;
    }
    setEditingPaymentId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.updatePayment(token, tournamentId, order.id, {
        status: nextStatus,
        provider_reference: nextStatus === "paid" ? (order.provider_reference ?? `manual-${new Date().toISOString().slice(0, 10)}`) : null,
        notes: nextStatus === "paid" ? (order.notes ?? "Pago manual confirmado por admin") : `Pago corregido a ${statusLabel(nextStatus)} por admin`,
      });
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setSummary(response.data.summary);
      setMessage(`Pago actualizado a ${statusLabel(nextStatus)} para ${participantDisplayName(order.participant)}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos editar el pago.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setEditingPaymentId(null);
    }
  }

  // Emitir reembolso (Eduardo 2026-09-15): con Mercado Pago se devuelve por la
  // API de MP; con cobro manual solo se asienta y el dinero se devuelve por
  // fuera. Eduardo 2026-09-19: monto LIBRE y puede haber varios por orden; la
  // orden sigue pagada (con nota) hasta que lo devuelto alcanza el total.
  function refundRemaining(order: RegistrationOrder): number {
    const remaining = Number(order.gross_amount ?? 0) - Number(order.refunded_amount ?? 0);
    return Number.isFinite(remaining) ? Math.max(0, Math.round(remaining * 100) / 100) : 0;
  }

  function handleOpenRefund(order: RegistrationOrder) {
    setError(null);
    setMessage(null);
    setRefundDraft({ orderId: order.id, amount: refundRemaining(order).toFixed(2), reason: "" });
  }

  async function handleRefundOrder(order: RegistrationOrder) {
    if (!token || !refundDraft || refundDraft.orderId !== order.id) return;
    const remaining = refundRemaining(order);
    const amount = Number(refundDraft.amount.replace(/[$,\s]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Escribe el monto a reembolsar, mayor a cero.");
      return;
    }
    if (amount > remaining + 0.001) {
      setError(`El monto excede lo que queda por reembolsar (${money(remaining, currency)}).`);
      return;
    }
    const isFull = amount >= remaining - 0.001;
    const isMp = ONLINE_PAYMENT_PROVIDERS.has(order.provider);
    const monto = money(amount, currency);
    const aviso = isMp
      ? isFull
        ? `Se devolverán ${monto} al jugador a través de Mercado Pago y la orden quedará como reembolsada. Esto no se puede deshacer. ¿Emitir reembolso?`
        : `Se devolverán ${monto} al jugador a través de Mercado Pago. La orden seguirá como pagada, con ${money(remaining - amount, currency)} sin reembolsar. Esto no se puede deshacer. ¿Emitir reembolso parcial?`
      : isFull
        ? `La orden quedará como reembolsada (${monto}). El dinero se lo devuelves tú por fuera de la plataforma. ¿Continuar?`
        : `Se asentará un reembolso parcial de ${monto}; la orden seguirá como pagada. El dinero se lo devuelves tú por fuera de la plataforma. ¿Continuar?`;
    if (!(await askConfirm(aviso, { acceptLabel: isFull ? "Sí, emitir reembolso" : "Sí, reembolsar una parte", tone: "danger" }))) return;
    setEditingPaymentId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.refundOrder(token, tournamentId, order.id, {
        amount: amount.toFixed(2),
        reason: refundDraft.reason.trim() || null,
      });
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setSummary(response.data.summary);
      setRefundDraft(null);
      const via = response.data.via === "mercadopago"
        ? `Reembolso emitido en Mercado Pago${response.data.refund_id ? ` (ref. ${response.data.refund_id})` : ""} por ${money(response.data.amount, currency)}.`
        : `Reembolso asentado por ${money(response.data.amount, currency)}. Recuerda devolver el dinero al jugador.`;
      setMessage(
        response.data.partial
          ? `${via} La orden sigue pagada; quedan ${money(response.data.remaining_amount, currency)} sin reembolsar.`
          : `${via} La orden quedó como reembolsada; ya puedes eliminar el registro si hace falta.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo emitir el reembolso.");
    } finally {
      setEditingPaymentId(null);
    }
  }

  // Gate #155 (5ª ronda): la cancelación de una orden de PAREJA conserva sus
  // notas EXACTAS (notes = order.notes) — cero cambios colaterales. El copy
  // "Pago corregido a … por admin" es exclusivo del flujo genérico de
  // jugadores, que no cambia.
  async function handleCancelTeamOrder(order: RegistrationOrder) {
    if (!token) return;
    if (!(await askConfirm("¿Cancelar esta orden de pareja?", { acceptLabel: "Sí, cancelar la orden", tone: "danger" }))) return;
    setEditingPaymentId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.updatePayment(token, tournamentId, order.id, {
        status: "cancelled",
        provider_reference: null,
        notes: order.notes ?? null,
      });
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setSummary(response.data.summary);
      setMessage(`Orden de pareja cancelada: ${participantDisplayName(order.participant)}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cancelar la orden.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setEditingPaymentId(null);
    }
  }

  async function handleSendDocsPending(order: RegistrationOrder) {
    if (!token) return;
    setSendingMessageId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.sendDocsPending(token, tournamentId, order.id);
      const name = participantDisplayName(order.participant);
      const docs = response.data.missing_documents;
      setMessage(
        response.data.email_status === "sent"
          ? `«Documentos pendientes» enviado a ${name}${docs.length ? ` (faltan: ${docs.join(", ")})` : ""}.`
          : response.data.email_status === "no_email"
            ? `${name} no tiene correo: el aviso quedó solo dentro de la app.`
            : `Aviso registrado en la app; el correo no salió (${response.data.email_status}).`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setSendingMessageId(null);
    }
  }

  async function handleSendPaymentReminder(order: RegistrationOrder) {
    if (!token) return;
    setSendingMessageId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.sendPaymentReminder(token, tournamentId, order.id);
      const name = participantDisplayName(order.participant);
      setMessage(
        response.data.email_status === "sent"
          ? `«Recordatorio de pago» enviado a ${name}.`
          : `Recordatorio registrado en la app; el correo no salió (${response.data.email_status}).`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setSendingMessageId(null);
    }
  }

  async function handleSendWelcome(order: RegistrationOrder) {
    if (!token) return;
    if (!order.participant || order.participant.current_status !== "active") return;
    setSendingWelcomeId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.sendWelcome(token, tournamentId, order.id);
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      const playerName = participantDisplayName(response.data.order.participant);
      const welcome = response.data.welcome;
      // El correo con credenciales va por entrega directa (spec Sección 4):
      // el admin DEBE enterarse si falló, porque el jugador se queda sin su
      // contraseña temporal y la única cura es re-dar Bienvenida.
      if (welcome?.email_status === "direct_failed") {
        setError(
          `El correo con las credenciales de ${playerName} NO pudo enviarse — reenvía la Bienvenida para regenerar la contraseña temporal.`,
        );
      } else if (welcome?.credentials_reason === "not_account_owner") {
        // A este jugador no lo dio de alta esta organización (Opción A,
        // Eduardo 2026-09-30): la bienvenida sale sin usuario ni contraseña.
        // Si además no tiene correo, no salió NADA por correo: el mensaje lo
        // dice (antes decía «Bienvenida enviada…» y el admin creía que el
        // jugador ya estaba avisado).
        setMessage(
          welcomeNotAccountOwnerMessage(
            playerName,
            accountNotOwnedInfo(response.data.order.participant?.player),
            welcome.email_status,
          ),
        );
      } else if (welcome?.email_status === "no_email") {
        setMessage(
          `Bienvenida registrada, pero ${playerName} no tiene correo registrado: no recibió sus credenciales de acceso.`,
        );
      } else if (welcome?.email_status === "email_not_configured") {
        setError(
          `Bienvenida registrada solo dentro de la app: el envío de correo no está configurado en el servidor, así que ${playerName} NO recibió usuario ni contraseña. Avisa a soporte de Torneos123.`,
        );
      } else if (welcome?.credentials_reason === "already_has_access") {
        const lastLogin = welcome.last_login_at ? formatDateTime(welcome.last_login_at) : null;
        setMessage(
          `Bienvenida enviada a ${playerName} sin credenciales nuevas: esa cuenta ya entró a la plataforma${lastLogin ? ` (último acceso ${lastLogin})` : ""}. Si olvidó su contraseña, puede recuperarla con "Olvidé mi contraseña".`,
        );
      } else if (welcome?.credentials_reason === "staff_account") {
        setMessage(
          `Bienvenida enviada a ${playerName} sin credenciales: su correo pertenece a una cuenta administradora y no se toca.`,
        );
      } else if (welcome?.credentials_reason === "family_account") {
        // Cuenta familiar (Eduardo 2026-09-29): la contraseña del tutor no se
        // cambia desde la bienvenida de uno de sus hijos. Solo pasa si la
        // cuenta aún no ha entrado: no se promete que el tutor ya la tiene.
        setMessage(
          `Bienvenida enviada a ${playerName} sin credenciales: entra con la cuenta familiar de su tutor (que aún no ha entrado). Para recuperarla, que el tutor use «Olvidé mi contraseña» con el correo de la familia, o en Jugadores pulsa «Generar PIN» y elige «El tutor pidió una contraseña nueva»; si ${playerName} necesita su propio usuario, dale «Acceso propio».`,
        );
      } else if (welcome?.credentials_reason === "email_in_other_account") {
        // El correo es el de una cuenta con jugadores que este admin no
        // administra: no se liga ni se revela de quién es. No se sugiere
        // fusionar: el otro perfil suele ser de otro organizador y este admin
        // ni lo ve (Eduardo 2026-09-29).
        setMessage(
          `Bienvenida enviada a ${playerName} sin credenciales: su correo ya es el de otra cuenta de Torneos123 con jugadores que no dio de alta tu organización. Si es la misma persona, pídele que entre con su cuenta y se inscriba desde el link público del torneo (quedará inscrito con su propio perfil) y da de baja este registro duplicado; si ya tiene pagos o partidos, pídele al soporte de Torneos123 que lo ligue a su cuenta. Si no es la misma persona, cambia o quita el correo de su perfil en Jugadores.`,
        );
      } else if (welcome?.credentials_reason === "account_has_foreign_profiles") {
        // Cuenta familiar ya ligada que también usan jugadores que este admin
        // no administra: su contraseña no se toca ni se dice de quién es.
        setMessage(
          `Bienvenida enviada a ${playerName} sin credenciales: entra con una cuenta familiar que también usan jugadores que no dio de alta tu organización, así que no le cambiamos la contraseña. Si ${playerName} necesita su propio usuario, en Jugadores dale «Acceso propio»; si el tutor olvidó su contraseña, que use «Olvidé mi contraseña». Lo demás de esa cuenta lo resuelve el soporte de Torneos123.`,
        );
      } else if (welcome?.credentials_sent && welcome.email_status === "direct_sent") {
        setMessage(
          `Bienvenida enviada con usuario y contraseña temporal al correo de ${playerName}.${welcome.payment_link_included ? " Incluye su link de pago pendiente." : ""}`,
        );
      } else {
        setMessage(`Mail de bienvenida preparado para ${playerName} (${response.data.outbox_count} canal(es) en outbox).`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos enviar la bienvenida.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSendingWelcomeId(null);
    }
  }

  // «Aprobar» (caso Oscar Zajiontz, torneo 67): un pago puede cubrir varias
  // categorías y se aprueban juntas las pre-registradas de la misma orden.
  // Con `only`, solo esa («Aprobar Categoría 3»). El cobro no cambia.
  async function handleApproveRegistration(order: RegistrationOrder, only?: OtherCategory) {
    if (!token || !order.participant) return;
    const name = participantDisplayName(order.participant);
    setReviewingParticipantId(only ? only.participantId : order.participant.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.participants.approveRegistration(token, tournamentId, order.participant.id, {
        order_id: order.id,
        ...(only ? { participant_ids: [only.participantId] } : {}),
      });
      const { approved, order: updated, participant } = response.data;
      const approvedIds = new Set(approved.map((row) => row.participant_id));
      setOrders((current) => current.map((item) => (
        item.id === order.id ? (updated ?? { ...item, participant }) : item
      )));
      setParticipants((current) => current.map((row) => (
        approvedIds.has(row.id) ? { ...row, current_status: "active" } : row
      )));
      const names = approved.map((row) => row.division_name).filter((value): value is string => Boolean(value));
      setMessage(
        names.length > 0
          ? `Inscripción aprobada para ${name} en ${joinSpanishList(names)}.`
          : `Inscripción aprobada para ${name}.`,
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos actualizar la inscripción.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setReviewingParticipantId(null);
    }
  }

  async function handleDropParticipant(order: RegistrationOrder) {
    if (!token || !order.participant) return;
    const name = participantDisplayName(order.participant);
    const consequence = "Queda como baja: sale de la lista activa y sus partidos pendientes se cancelan (sin W/O). Podrás reincorporarlo después desde Acciones.";
    // Si el mismo pago cubre otras categorías donde sigue jugando (vivas o en
    // pareja), la pregunta y el botón hablan de la categoría de la fila, no
    // del torneo; las vivas se ofrecen para dar de baja también (sin marcar).
    // Solo cambia el estado de cada categoría: la orden y su monto no se tocan.
    const options = coverageOptionsFor(order);
    const inPlay = orderInPlayOtherCategories(order, options);
    const others = orderLiveOtherCategories(order, options);
    const anchorCategory = order.participant.division?.name ?? "Sin categoría";
    let alsoDrop: OtherCategory[] = [];
    if (inPlay.length === 0) {
      if (!(await askConfirm(`¿Eliminar a ${name} del torneo? ${consequence}`, { acceptLabel: "Sí, eliminar del torneo", tone: "danger" }))) {
        return;
      }
    } else {
      const question = `¿Eliminar a ${name} de ${anchorCategory}? ${consequence}\n`
        + `Este pago también cubre ${joinSpanishList(inPlay.map((other) => (
          other.partnerName ? `${other.name} (en pareja con ${other.partnerName})` : other.name
        )))}.\n`
        + "El cobro no cambia; si hay que devolver dinero, usa «Emitir reembolso».";
      if (others.length === 0) {
        if (!(await askConfirm(question, { acceptLabel: "Sí, dar de baja", tone: "danger" }))) return;
      } else {
        const answer = await askConfirmWithChecks(question, {
          checks: others.map((other) => ({ key: String(other.participantId), label: `También dar de baja: ${other.name}` })),
          acceptLabel: "Sí, dar de baja",
          tone: "danger",
        });
        if (!answer.accepted) return;
        alsoDrop = others.filter((other) => answer.checked.includes(String(other.participantId)));
      }
    }
    setReviewingParticipantId(order.participant.id);
    setError(null);
    setMessage(null);
    const dropPayload = {
      reason: "Jugador eliminado por admin desde Inscripciones",
      // Un eliminado desde Inscripciones no debió estar jugando: si ya
      // tenía partidos pendientes, se cancelan (nunca W/O — no jugó nada).
      pending_matches_action: "cancel" as const,
    };
    try {
      const response = await api.participants.drop(token, tournamentId, order.participant.id, dropPayload);
      const droppedIds = new Set([order.participant.id]);
      const failed: string[] = [];
      for (const other of alsoDrop) {
        try {
          await api.participants.drop(token, tournamentId, other.participantId, dropPayload);
          droppedIds.add(other.participantId);
        } catch {
          failed.push(other.name);
        }
      }
      setOrders((current) => current.map((item) => (
        item.id === order.id
          ? {
              ...item,
              participant: response.data.participant,
              items: item.items?.map((row) => (droppedIds.has(row.participant_id) ? { ...row, participant_status: "dropped" } : row)),
            }
          : item
      )));
      setParticipants((current) => current.map((row) => (
        droppedIds.has(row.id) ? { ...row, current_status: "dropped" } : row
      )));
      if (inPlay.length === 0) {
        setMessage(`${name} eliminado del torneo. Puedes reincorporarlo desde Acciones.`);
      } else {
        const droppedNames = [anchorCategory, ...alsoDrop.filter((other) => droppedIds.has(other.participantId)).map((other) => other.name)];
        const remaining = inPlay.filter((other) => !droppedIds.has(other.participantId)).map((other) => other.name);
        setMessage(
          `${name} eliminado de ${joinSpanishList(droppedNames)}.`
            + (remaining.length > 0 ? ` Sigue inscrito en ${joinSpanishList(remaining)}.` : "")
            + " Puedes reincorporarlo desde Acciones.",
        );
      }
      if (failed.length > 0) {
        setError(`No se pudo dar de baja en ${joinSpanishList(failed)}. Hazlo desde Jugadores.`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos actualizar la inscripción.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setReviewingParticipantId(null);
    }
  }

  // Campos editables del registro: los del formulario público activos, sin
  // documentos (esos van por "Subir/Cambiar"). core → perfil del jugador
  // (PATCH del perfil del participante en este torneo); custom → respuestas del participante.
  const editableRegistrationFields = useMemo(
    () => enabledRegistrationFields(normalizedRegistrationSchema).filter((field) => field.kind !== "document"),
    [normalizedRegistrationSchema],
  );

  function registrationEditValue(participant: TournamentParticipant | null | undefined, field: RegistrationFormField): string {
    if (!participant) return "";
    if (field.kind === "custom") {
      const value = participant.registration_custom_answers?.[field.key];
      if (Array.isArray(value)) return value.join(", ");
      return value === null || value === undefined ? "" : String(value);
    }
    const player = participant.player as unknown as Record<string, unknown> | null | undefined;
    const value = player?.[field.key];
    return value === null || value === undefined ? "" : String(value);
  }

  function beginEditRegistration(order: RegistrationOrder) {
    const participant = order.participant;
    const form: Record<string, string> = {};
    editableRegistrationFields.forEach((field) => {
      form[field.key] = registrationEditValue(participant, field);
    });
    setRegistrationEditForm(form);
    setEditingRegistrationOrderId(order.id);
    setError(null);
    setMessage(null);
  }

  async function saveRegistrationEdit(order: RegistrationOrder) {
    const participant = order.participant;
    if (!token || !participant) return;
    setSavingRegistrationEdit(true);
    setError(null);
    try {
      const profileUpdates: Record<string, string | null> = {};
      const customUpdates: Record<string, string> = {};
      editableRegistrationFields.forEach((field) => {
        // El correo de un jugador que no dio de alta esta organización no se
        // manda (el backend lo rechazaría y con él TODA la edición): el campo
        // va bloqueado y lo demás sí se guarda (Opción A, Eduardo 2026-09-30).
        if (field.kind !== "custom" && (isAccountLockedProfileField(field.key, participant.player) || (isSubUser && field.key === "email"))) return;
        const next = (registrationEditForm[field.key] ?? "").trim();
        const current = registrationEditValue(participant, field);
        if (next === current) return;
        if (field.kind === "custom") customUpdates[field.key] = next;
        else profileUpdates[field.key] = next === "" ? null : next;
      });
      if (participant.player_profile_id && Object.keys(profileUpdates).length > 0) {
        await api.participants.updateProfile(token, tournamentId, participant.id, profileUpdates as PlayerUpdateInput);
      }
      if (Object.keys(customUpdates).length > 0) {
        await api.participants.update(token, tournamentId, participant.id, { registration_custom_answers: customUpdates });
      }
      setEditingRegistrationOrderId(null);
      setMessage(`Registro de ${participantDisplayName(participant)} actualizado.`);
      await loadDashboard();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo guardar el registro.");
    } finally {
      setSavingRegistrationEdit(false);
    }
  }

  async function handleDeleteRegistration(order: RegistrationOrder) {
    const participant = order.participant;
    if (!token || !participant) return;
    const name = participantDisplayName(participant);
    if (
      !(await askConfirm(
        `¿Eliminar el registro de ${name} en este torneo?\n\nSe borran su inscripción, sus órdenes no pagadas y su historial en este torneo. Si juega en pareja, la pareja queda dada de baja. El jugador sigue existiendo en Jugadores. No se puede deshacer.`, { acceptLabel: "Sí, eliminar registro", tone: "danger" }))
    ) {
      return;
    }
    setDeletingRegistrationId(order.id);
    setError(null);
    setRegistrationDeleteBlocked(null);
    try {
      const response = await api.participants.deleteRegistration(token, tournamentId, participant.id);
      // Torneo 112: si jugaba en pareja, la pareja sale del torneo (baja).
      const pairsDropped = response.data.pairs_dropped?.length ?? 0;
      setMessage(
        pairsDropped > 0
          ? `Registro de ${name} eliminado. Su pareja quedó dada de baja (sus resultados jugados se conservan).`
          : `Registro de ${name} eliminado.`,
      );
      await loadDashboard();
    } catch (err) {
      const reason = err instanceof ApiError ? err.message : "No se pudo eliminar el registro.";
      setError(reason);
      setRegistrationDeleteBlocked({ orderId: order.id, reason });
    } finally {
      setDeletingRegistrationId(null);
    }
  }

  // Reversa de "Eliminar jugador": mismo endpoint que Reactivar en Jugadores.
  // Conserva inscripción, pago y seed; los partidos cancelados al eliminarlo
  // no se recuperan (se rearman desde el draw).
  async function handleReincorporateParticipant(order: RegistrationOrder) {
    if (!token || !order.participant) return;
    const name = participantDisplayName(order.participant);
    const consequence = "Vuelve como activo con su misma inscripción y pago. Los partidos que se cancelaron al eliminarlo no se recuperan.";
    // Un pago de varias categorías (revisión ronda 1): «Reincorporar» regresa
    // la categoría de la fila y ofrece (sin marcar) las otras que el mismo
    // pago cubre y están de baja; nunca la fila de dobles que reemplazó una
    // pareja, ni con la orden cancelada o reembolsada. El cobro no cambia.
    const options = coverageOptionsFor(order);
    const multiCategory = orderOtherCategories(order, options).length > 0;
    const anchorCategory = order.participant.division?.name ?? "Sin categoría";
    const reinstatable = orderReinstatableOtherCategories(order, options);
    let alsoBack: OtherCategory[] = [];
    if (reinstatable.length === 0) {
      const question = multiCategory
        ? `¿Reincorporar a ${name} en ${anchorCategory}? ${consequence}`
        : `¿Reincorporar a ${name} al torneo? ${consequence}`;
      if (!(await askConfirm(question, { acceptLabel: "Sí, reincorporar" }))) return;
    } else {
      const answer = await askConfirmWithChecks(
        `¿Reincorporar a ${name} en ${anchorCategory}? ${consequence}\n`
          + `Este pago también cubre ${joinSpanishList(reinstatable.map((other) => other.name))}, `
          + `que ${reinstatable.length === 1 ? "está" : "están"} de baja.`,
        {
          checks: reinstatable.map((other) => ({ key: String(other.participantId), label: `También reincorporar: ${other.name}` })),
          acceptLabel: "Sí, reincorporar",
        },
      );
      if (!answer.accepted) return;
      alsoBack = reinstatable.filter((other) => answer.checked.includes(String(other.participantId)));
    }
    setReviewingParticipantId(order.participant.id);
    setError(null);
    setMessage(null);
    const reactivatePayload = { reason: "Jugador reincorporado por admin desde Inscripciones" };
    try {
      const response = await api.participants.reactivate(token, tournamentId, order.participant.id, reactivatePayload);
      const backIds = new Set([order.participant.id]);
      const failed: string[] = [];
      for (const other of alsoBack) {
        try {
          await api.participants.reactivate(token, tournamentId, other.participantId, reactivatePayload);
          backIds.add(other.participantId);
        } catch {
          failed.push(other.name);
        }
      }
      setOrders((current) => current.map((item) => (
        item.id === order.id
          ? {
              ...item,
              participant: response.data.participant,
              items: item.items?.map((row) => (backIds.has(row.participant_id) ? { ...row, participant_status: "active" } : row)),
            }
          : item
      )));
      setParticipants((current) => current.map((row) => (
        backIds.has(row.id) ? { ...row, current_status: "active" } : row
      )));
      if (!multiCategory) {
        setMessage(`${name} reincorporado al torneo.`);
      } else {
        const backNames = [anchorCategory, ...alsoBack.filter((other) => backIds.has(other.participantId)).map((other) => other.name)];
        const stillOut = orderOtherCategories(order, options)
          .filter((other) => other.status === "dropped" && !other.partnerName && !backIds.has(other.participantId))
          .map((other) => other.name);
        setMessage(
          `${name} reincorporado en ${joinSpanishList(backNames)}.`
            + (stillOut.length > 0 ? ` Sigue de baja en ${joinSpanishList(stillOut)}.` : ""),
        );
      }
      if (failed.length > 0) {
        setError(`No se pudo reincorporar en ${joinSpanishList(failed)}. Hazlo desde Jugadores.`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos reincorporar al jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setReviewingParticipantId(null);
    }
  }

  function paymentUrlForOrder(order: RegistrationOrder): string {
    const slug = tournament?.slug ?? tournamentId;
    return `/inscripcion/${slug}/orden/${order.public_token ?? ""}`;
  }

  async function handleCopyChargeLink(order: RegistrationOrder) {
    const url = `${window.location.origin}${paymentUrlForOrder(order)}`;
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Link de pago copiado.");
    } catch {
      // Incluir la URL en el mensaje: sin clipboard, al menos se puede
      // seleccionar y copiar manualmente desde el alert.
      setError(`No se pudo copiar automáticamente. Copia el link manualmente: ${url}`);
    }
  }

  async function handleActivateCharge(order: RegistrationOrder) {
    if (!token || !order.participant) return;
    setReviewingParticipantId(order.participant.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.participants.update(token, tournamentId, order.participant.id, {
        status: "active",
        notes: order.participant.notes ?? "Cobro sin alta pagado — activado por admin",
      });
      setOrders((current) => current.map((item) => (
        item.id === order.id ? { ...item, participant: response.data.participant } : item
      )));
      setMessage(`${participantDisplayName(response.data.participant)} activado.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos activar al jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setReviewingParticipantId(null);
    }
  }

  async function handleCancelCharge(order: RegistrationOrder) {
    if (!token || !order.participant) return;
    if (!(await askConfirm("¿Cancelar la preinscripción y su orden de pago?", { acceptLabel: "Sí, cancelar preinscripción", tone: "danger" }))) return;
    setCancellingChargeId(order.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.registrations.cancelCharge(token, tournamentId, order.id);
      setOrders((current) => current.map((item) => (item.id === order.id ? response.data.order : item)));
      setMessage(`Preinscripción cancelada para ${participantDisplayName(response.data.order.participant)}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cancelar la preinscripción.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setCancellingChargeId(null);
    }
  }

  async function handleChangeParticipantDivision(order: RegistrationOrder, nextDivisionId: number | null) {
    if (!token || !order.participant) return;
    // Se toma al empezar: tras esperar la revisión, la señal del momento ya
    // sería la del torneo nuevo (mismo bloqueo que el #601).
    const signal = currentSignal();
    // Eduardo 2026-10-13 (torneo 71): al cambiar de categoría sus partidos
    // pendientes del grupo viejo se cancelan y ese grupo se rehace sin
    // programar. Si eso pasa, se confirma antes (la revisión no escribe; si
    // falla, se sigue como siempre).
    if (nextDivisionId != null && nextDivisionId !== order.participant.division_id) {
      const previewResponse = await settleWithin(
        api.groups.membershipPreview(token, tournamentId, {
          action: "change_division",
          participant_id: order.participant.id,
          division_id: nextDivisionId,
        }),
      );
      // Se cambió de torneo mientras llegaba la revisión: no se mueve a nadie.
      if (signal?.aborted) return;
      const preview: MembershipChangePreview | null = previewResponse?.data ?? null;
      if (preview && (preview.warning || preview.notice)) {
        const nextName = divisions.find((division) => division.id === nextDivisionId)?.name ?? "otra categoría";
        const accepted = await askConfirm(
          membershipConfirmText(
            `¿Cambiar a ${participantDisplayName(order.participant)} a ${nextName}? Sale de su grupo y sus partidos pendientes ahí se cancelan.`,
            preview,
          ),
          { acceptLabel: "Sí, cambiar de categoría", tone: membershipLosesSchedule(preview) ? "danger" : "default", signal },
        );
        if (!accepted || signal?.aborted) return;
      }
    }
    setChangingDivisionParticipantId(order.participant.id);
    setError(null);
    setMessage(null);
    try {
      // Eduardo 2026-09-24: el rank (seed) es POR categoría; al mover al
      // jugador se limpia (S/R) para no chocar con los ranks de la categoría
      // destino. Se vuelve a asignar en el paso Ranking.
      const hadRank = order.participant.initial_rank != null || order.participant.tournament_rank != null;
      const response = await api.participants.update(token, tournamentId, order.participant.id, {
        division_id: nextDivisionId,
        ...(hadRank ? { initial_rank: null } : {}),
      });
      setOrders((current) => current.map((item) => (
        item.id === order.id ? { ...item, participant: response.data.participant } : item
      )));
      const nextDivision = divisions.find((division) => division.id === nextDivisionId);
      // Orden multi-categoría: si la categoría elegida ya era un renglón de la
      // misma orden, el backend no mueve a nadie: la vuelve la PRINCIPAL del
      // registro (responde con ese otro participante).
      const becamePrimary = response.data.participant.id !== order.participant.id;
      const rebuildNotice = rrRebuildNotice(response.data.rr_rebuild);
      const baseMessage =
        becamePrimary && nextDivision
          ? `${nextDivision.name} es ahora la categoría principal del registro de ${participantDisplayName(response.data.participant)}. Sus otras categorías y el pago no cambian.`
          : nextDivision
            ? `Categoría actualizada a ${nextDivision.name} para ${participantDisplayName(response.data.participant)}.${hadRank && !becamePrimary ? " Su rank quedó en S/R: asígnalo en el paso Ranking." : ""}`
            : `Categoría removida para ${participantDisplayName(response.data.participant)}.`;
      setMessage(rebuildNotice ? `${baseMessage} ${rebuildNotice}` : baseMessage);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cambiar la categoría del jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setChangingDivisionParticipantId(null);
    }
  }

  async function handleCreateRegistrationPair(order: RegistrationOrder, partnerOrderId: number) {
    if (!token || !order.participant?.player_profile_id || !partnerOrderId) return;
    const partnerOrder = orders.find((item) => item.id === partnerOrderId);
    const partnerParticipant = partnerOrder?.participant;
    if (!partnerParticipant?.player_profile_id) return;
    if (order.participant.division_id == null || order.participant.division_id !== partnerParticipant.division_id) {
      setError("La pareja debe estar en la misma categoría de dobles.");
      return;
    }
    setPairingParticipantId(order.participant.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.participants.createTeam(token, tournamentId, {
        player_profile_ids: [order.participant.player_profile_id, partnerParticipant.player_profile_id],
        division_id: order.participant.division_id,
      });
      setMessage(`Pareja creada: ${participantDisplayName(response.data.participant)}.`);
      await loadDashboard();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos crear la pareja.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setPairingParticipantId(null);
    }
  }

  /** PDF de inscritos por categoría (Eduardo 2026-09-23): mismo formato de
   * marca que Calendario/Resultados, con el logo del organizador. */
  function printRegistrationsPdf() {
    // Revisión Hermes 2026-09-24: la lista sale de los PARTICIPANTES del
    // torneo (no de las órdenes): así entran las categorías secundarias de
    // una inscripción multi-categoría y las parejas de dobles, sin depender
    // del toggle visual de la bandeja. Cada participante busca su orden
    // (principal o como renglón de la orden) para Pago.
    const orderByParticipantId = new Map<number, RegistrationOrder>();
    for (const order of orders) {
      if (order.participant_id && !orderByParticipantId.has(order.participant_id)) orderByParticipantId.set(order.participant_id, order);
      for (const item of order.items ?? []) {
        if (!orderByParticipantId.has(item.participant_id)) orderByParticipantId.set(item.participant_id, order);
      }
    }
    const rows = participants.filter((participant) => participant.current_status !== "dropped");
    if (rows.length === 0) {
      setError("No hay inscritos para imprimir.");
      return;
    }
    const byDivision = new Map<string, TournamentParticipant[]>();
    for (const participant of rows) {
      const name = participant.division?.name ?? "Sin categoría";
      byDivision.set(name, [...(byDivision.get(name) ?? []), participant]);
    }
    const divisionOrder = [
      ...divisions.map((division) => division.name),
      ...[...byDivision.keys()].filter((name) => !divisions.some((division) => division.name === name)),
    ].filter((name) => byDivision.has(name));
    // Eduardo 2026-10-09: la columna «Club / Academia» sale solo cuando el
    // formulario del torneo tiene ese campo activo.
    const showClub = enabledRegistrationFields(normalizedRegistrationSchema).some((field) => field.key === "club_name");
    const colgroup = showClub
      ? '<col style="width:5%"/><col style="width:33%"/><col style="width:20%"/><col style="width:13%"/><col style="width:15%"/><col style="width:14%"/>'
      : '<col style="width:6%"/><col style="width:44%"/><col style="width:16%"/><col style="width:18%"/><col style="width:16%"/>';
    const sections = divisionOrder
      .map((name) => {
        const list = [...(byDivision.get(name) ?? [])].sort((a, b) =>
          participantDisplayName(a).localeCompare(participantDisplayName(b), "es"),
        );
        return `
      <h2 class="print-section-title">${escapeHtml(name)} <span class="print-muted">· ${list.length} inscrito(s)</span></h2>
      <table>
        <colgroup>${colgroup}</colgroup>
        <thead><tr><th>#</th><th>Jugador</th>${showClub ? "<th>Club / Academia</th>" : ""}<th>Estatus</th><th>Pago</th><th>Documentos</th></tr></thead>
        <tbody>${list
          .map((participant, index) => {
            const order = orderByParticipantId.get(participant.id) ?? null;
            const pago = order ? paymentStateFor(order).label : "—";
            const docs = participant.registration_documents;
            const receiptOk = registrationDocumentReceived(docs, "payment_receipt") || (order ? registrationPaidOnline(order) : false);
            const photoOk = !usesCredential || registrationDocumentReceived(docs, "photo");
            const docsOk = photoOk && registrationDocumentReceived(docs, "curp_pdf") && receiptOk;
            return `<tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(participantDisplayName(participant))}</td>${showClub ? `
          <td>${escapeHtml(participantClubLabel(participant))}</td>` : ""}
          <td>${escapeHtml(participantStatusLabel(participant.current_status))}</td>
          <td>${escapeHtml(pago)}</td>
          <td>${participant.participant_kind === "team" ? "—" : docsOk ? "Completos" : "Faltan"}</td>
        </tr>`;
          })
          .join("")}</tbody>
      </table>`;
      })
      .join("");
    try {
      openPrintWindow({
        title: `Inscritos · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: "Lista de inscritos",
        heading: tournament?.name ?? "Torneos123",
        subtitle: `${rows.length} inscrito(s) · ${divisionOrder.length} categoría(s)`,
        bodyHtml: sections,
        organizerSlot: true,
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la impresión.");
    }
  }

  function downloadRegistrationReviewLayout() {
    const headers = [
      "order_id",
      "participant_id",
      "jugador",
      "email",
      "telefono",
      "categoria_actual",
      "categoria_nueva",
      "estatus_pago",
      "comprobante_admin",
      "total",
      "notas_pago",
    ];
    // Gate #155: la descarga respeta el alcance EXPLÍCITO de la bandeja —
    // con el toggle apagado no exporta órdenes de pareja; encendido las
    // incluye solo para auditoría (la carga nunca las paga).
    const rows = scopedOrders.map((order) => {
      const participant = order.participant;
      const docs = participant?.registration_documents;
      return [
        order.id,
        participant?.id ?? "",
        participantDisplayName(participant),
        participant?.player?.email ?? "",
        participant?.player?.phone ?? "",
        participant?.division?.name ?? "",
        // Pareja: categoria_nueva se exporta VACÍA (4ª ronda #155) — su
        // categoría no se cambia por layout y así el reimport para cancelar
        // no parece un intento de mutación.
        isTeamAnchoredOrder(order) ? "" : participant?.division?.name ?? "",
        order.status === "paid" ? "pagado" : order.status === "cancelled" ? "rechazado" : order.status === "refunded" ? "reembolsado" : "debe",
        registrationDocumentReceived(docs, "payment_receipt") ? "si" : "no",
        order.gross_amount,
        order.notes ?? "",
      ];
    });
    const helpRows = [
      ["", "", "INSTRUCCIONES", "Edita solo categoria_nueva, estatus_pago, comprobante_admin y notas_pago.", "", "", "", "Valores: pagado, debe, rechazado, reembolsado", "Valores: si/no", "", ""],
      ["", "", "CATEGORIAS", activeDivisionsPool(divisions).map((division) => division.name).join(" | "), "", "", "", "", "", "", ""],
    ];
    const csv = [headers, ...helpRows, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${csv}\n`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `layout_registros_recibidos_torneo_${tournamentId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function paymentStatusFromLayout(value: string): RegistrationPaymentStatus | null {
    const normalized = normalizeSearch(value).trim();
    if (!normalized) return null;
    if (["pagado", "pago", "paid", "si", "sí", "confirmado"].includes(normalized)) return "paid";
    if (["debe", "pendiente", "pending", "no", "adeudo", "por pagar"].includes(normalized)) return "pending";
    if (["rechazado", "cancelado", "cancelled", "cancelada"].includes(normalized)) return "cancelled";
    if (["reembolsado", "refunded"].includes(normalized)) return "refunded";
    return null;
  }

  function divisionFromLayout(value: string, pool: TournamentDivision[]): TournamentDivision | null | undefined {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber)) {
      const byId = pool.find((division) => division.id === asNumber);
      if (byId) return byId;
    }
    const normalized = normalizeSearch(trimmed).trim();
    return pool.find((division) => normalizeSearch(division.name).trim() === normalized) ?? null;
  }

  async function handleRegistrationLayoutUpload(file: File | null) {
    if (!token || !file) return;
    setBulkLayoutUploading(true);
    setError(null);
    setMessage(null);
    try {
      const rows = parseCsv(await file.text());
      const header = rows[0] ?? [];
      const keyByIndex = header.map(normalizeLayoutHeader);
      const dataRows = rows.slice(1).filter((row) => row.some((value) => value.trim() !== "") && !normalizeSearch(row[2] ?? "").includes("instrucciones") && !normalizeSearch(row[2] ?? "").includes("categorias"));
      const valueFor = (row: string[], key: string) => {
        const index = keyByIndex.indexOf(key);
        return index >= 0 ? row[index] ?? "" : "";
      };
      // Categorías FRESCAS y activas al momento de cargar (rescate de #147):
      // la lista en memoria podía ser anterior a una edición de categorías y
      // daba «División X not found»; refrescar la página lo «arreglaba».
      const divisionsResponse = await api.tournaments.listDivisions(token, tournamentId);
      const allDivisions = divisionsResponse.data.divisions;
      const freshDivisions = activeDivisionsPool(allDivisions);
      let updatedCategories = 0;
      // Cambios de categoría que rehicieron grupos (Eduardo 2026-10-13).
      const layoutRebuilds: Array<Parameters<typeof rrRebuildNotice>[0]> = [];
      let updatedPayments = 0;
      let skipped = 0;
      let cancelledTeamOrders = 0;
      let blockedTeamRows = 0;
      const failures: string[] = [];
      for (const row of dataRows) {
        const orderId = Number(valueFor(row, "order_id"));
        // Gate #155: la carga resuelve contra el alcance visible — una orden
        // de pareja OCULTA (toggle apagado) no es alcanzable desde el CSV.
        const order = scopedOrders.find((item) => item.id === orderId);
        if (!order?.participant) {
          skipped += 1;
          continue;
        }
        // Cada fila es independiente: un error se reporta al final y NO
        // aborta el resto (antes la carga quedaba aplicada a medias).
        try {
          if (isTeamAnchoredOrder(order)) {
            // Orden de pareja visible (4ª ronda #155): cada celda editable se
            // compara contra el estado ACTUAL — el CSV exportado viene
            // pre-llenado y una celda con valor NO es un cambio. Único cambio
            // permitido: debe → rechazado. Al cancelar, las notas se CONSERVAN
            // (notes = order.notes), jamás el texto del CSV.
            const teamCategoryInput = valueFor(row, "categoria_nueva");
            const teamDivision = divisionFromLayout(teamCategoryInput, freshDivisions);
            const decision = teamLayoutRowDecision({
              order,
              categoryInput: teamCategoryInput,
              categoryResolvedId: teamDivision === undefined ? undefined : teamDivision === null ? null : teamDivision.id,
              statusParsed: paymentStatusFromLayout(valueFor(row, "estatus_pago")),
              notesInput: valueFor(row, "notas_pago"),
              receiptInput: normalizeSearch(valueFor(row, "comprobante_admin")).trim(),
            });
            if (decision === "cancel") {
              await api.registrations.updatePayment(token, tournamentId, order.id, {
                status: "cancelled",
                provider_reference: null,
                notes: order.notes || null,
              });
              cancelledTeamOrders += 1;
            } else if (decision === "block") {
              blockedTeamRows += 1;
            }
            continue;
          }
          const nextDivisionInput = valueFor(row, "categoria_nueva");
          // Su categoría ACTUAL (aunque se haya desactivado) = sin cambio: la
          // fila sigue con su pago. Solo un cambio necesita categoría activa.
          const currentDivision = divisionFromLayout(nextDivisionInput, allDivisions);
          const keepsCurrent = currentDivision != null && currentDivision.id === order.participant.division_id;
          const nextDivision = keepsCurrent ? undefined : divisionFromLayout(nextDivisionInput, freshDivisions);
          if (nextDivision === null) {
            failures.push(
              `${participantDisplayName(order.participant)} (orden ${order.id}): la categoría «${nextDivisionInput.trim()}» no existe o está inactiva; no se cambió de categoría`,
            );
          }
          if (nextDivision != null && order.participant.division_id !== nextDivision.id) {
            // El rank es por categoría: al mover por layout también se limpia.
            const changed = await api.participants.update(token, tournamentId, order.participant.id, { division_id: nextDivision.id, initial_rank: null });
            layoutRebuilds.push(changed.data?.rr_rebuild);
            updatedCategories += 1;
          }
          const nextStatus = paymentStatusFromLayout(valueFor(row, "estatus_pago"));
          const receiptValue = normalizeSearch(valueFor(row, "comprobante_admin"));
          const adminHasReceipt = ["si", "sí", "yes", "1", "true", "x"].includes(receiptValue);
          const layoutNotes = valueFor(row, "notas_pago");
          const extraNotes: string[] = [];
          if (adminHasReceipt && !registrationDocumentReceived(order.participant.registration_documents, "payment_receipt")) {
            extraNotes.push("Comprobante recibido por admin fuera del link público.");
          }
          // Sin cambio real → nada; pago en línea (Mercado Pago/Stripe) → no se
          // toca (el PATCH lo marcaría «manual» y el reembolso ya no iría por
          // el proveedor); resto → se aplica. La celda de notas es la nota
          // completa (viene pre-llenada).
          const decision = layoutPaymentDecision(order, nextStatus, layoutNotes, extraNotes);
          if (decision.kind === "locked") {
            failures.push(
              `${participantDisplayName(order.participant)} (orden ${order.id}): pago en línea, se administra desde el panel`,
            );
          } else if (decision.kind === "patch") {
            const status = decision.status as RegistrationPaymentStatus;
            await api.registrations.updatePayment(token, tournamentId, order.id, {
              status,
              provider_reference: status === "paid" ? (order.provider_reference ?? `layout-${new Date().toISOString().slice(0, 10)}`) : null,
              notes: decision.notes || null,
            });
            updatedPayments += 1;
          }
        } catch (err) {
          const reason = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "error desconocido";
          failures.push(`${participantDisplayName(order.participant)} (orden ${order.id}): ${reason}`);
        }
      }
      await loadDashboard();
      const teamSummary = [
        cancelledTeamOrders > 0 ? `${cancelledTeamOrders} orden(es) de pareja cancelada(s)` : null,
        blockedTeamRows > 0 ? `${blockedTeamRows} fila(s) de pareja bloqueada(s) — solo se permite cancelar` : null,
      ].filter(Boolean).join(", ");
      const layoutRebuildNotice = mergedRrRebuildNotice(layoutRebuilds);
      setMessage(`Layout cargado: ${updatedCategories} categoría(s) actualizada(s), ${updatedPayments} pago(s)/nota(s) actualizados. ${skipped} fila(s) omitida(s).${teamSummary ? ` ${teamSummary}.` : ""}${layoutRebuildNotice ? ` ${layoutRebuildNotice}` : ""}`);
      const failuresMessage = layoutFailuresMessage(failures);
      if (failuresMessage) setError(failuresMessage);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "No pudimos cargar el layout.");
    } finally {
      setBulkLayoutUploading(false);
    }
  }

  // Renglón del editor de campos (Documentos arriba y las secciones abajo).
  // La foto con credencial (Eduardo 2026-10-30, opción A): Activo y
  // Obligatorio con candado; el clic explica por qué y lleva al paso 02.
  // aria-disabled (no disabled) para que el clic llegue y no baje el contraste.
  function renderRegistrationFieldRow(field: RegistrationFormField, index: number) {
    const isPhoto = field.key === CREDENTIAL_PHOTO_KEY;
    return (
      <article
        className={`registration-field-builder__row${field.locked ? " is-locked" : ""}${field.kind === "document" && EDITOR_DOCUMENT_KEYS.includes(field.key) ? " is-document" : ""}`}
        aria-label={field.label}
      >
        <div className="registration-field-builder__main">
          {field.kind !== "core" ? (
            <span className="registration-field-builder__kind">
              {field.kind === "document" ? "Documento" : "Personalizado"}
            </span>
          ) : null}
          <input
            aria-label="Etiqueta pública"
            value={field.label}
            onChange={(event) => updateRegistrationField(index, { label: event.target.value })}
          />
          {field.locked ? <small className="status-chip">Requerido</small> : null}
          {isPhoto && usesCredential ? (
            <p className="insc-photo-lockline" id={photoLocklineId}>
              <svg className="insc-pill__lock" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 7V5a3 3 0 0 1 6 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1zm1.5 0h3V5a1.5 1.5 0 0 0-3 0v2z"/></svg>
              Obligatoria porque el torneo usa credencial (02)
            </p>
          ) : isPhoto ? (
            <p className="insc-photo-offnote" id={photoOffnoteId}>
              No se pide: el torneo no usa credencial. Se activa sola si en el paso 02 eliges «Sí».
            </p>
          ) : null}
          {field.kind === "custom" && field.key !== "partner_name" && field.key !== "partner_contact" ? (
            <label className="registration-field-builder__key">
              <span>Clave interna</span>
              <input
                aria-label="Clave del campo"
                value={field.key}
                onChange={(event) => updateRegistrationField(index, { key: event.target.value.replace(/[^a-zA-Z0-9_]/g, "_") })}
              />
            </label>
          ) : null}
          <label className="registration-field-builder__key registration-field-builder__help">
            <input
              aria-label="Texto opcional debajo del campo para explicar al jugador"
              placeholder="Texto opcional debajo del campo para explicar al jugador"
              value={field.help_text ?? ""}
              onChange={(event) => updateRegistrationField(index, { help_text: event.target.value })}
            />
          </label>
          {suggestsGuardianSection(field) ? (
            <p className="registration-field-builder__suggest">
              <span>¿Va en Papá, mamá o tutor?</span>
              <button className="button button--ghost button--sm" type="button" onClick={() => updateRegistrationField(index, { section: "guardian" })}>
                Mover
              </button>
              {/* «Otros» ya se ve elegido en «Va en»: sin este botón
                  no había forma obvia de quitar la sugerencia. */}
              <button className="button button--ghost button--sm" type="button" aria-label="No, se queda en Otros" onClick={() => updateRegistrationField(index, { section: "other" })}>
                No
              </button>
            </p>
          ) : null}
        </div>
        <div className="registration-field-builder__checks">
          {isPhoto && usesCredential ? (
            (["Activo", "Obligatorio"] as const).map((chip) => (
              <label className="registration-field-toggle insc-pill insc-pill--sm is-on is-credential-locked" key={chip}>
                <input
                  type="checkbox"
                  checked
                  aria-disabled="true"
                  aria-describedby={photoLocklineId}
                  // Controlado en true: React lo deja marcado; el clic (o la
                  // barra espaciadora) solo explica por qué.
                  onChange={() => setPhotoLockNotice(chip)}
                />
                <span>{chip}</span><svg className="insc-pill__lock" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 7V5a3 3 0 0 1 6 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1zm1.5 0h3V5a1.5 1.5 0 0 0-3 0v2z"/></svg>
              </label>
            ))
          ) : isPhoto ? (
            (["Activo", "Obligatorio"] as const).map((chip) => (
              <label className="registration-field-toggle insc-pill insc-pill--sm" key={chip}>
                <input type="checkbox" checked={false} disabled aria-describedby={photoOffnoteId} onChange={() => undefined} />
                <span>{chip}</span>
              </label>
            ))
          ) : (
            <>
            <label className={`registration-field-toggle insc-pill insc-pill--sm${(field.enabled !== false) ? " is-on" : ""}`}>
              <input type="checkbox" checked={field.enabled !== false} disabled={field.locked} onChange={(event) => updateRegistrationField(index, { enabled: event.target.checked })} />
              <span>Activo</span>{field.locked ? <svg className="insc-pill__lock" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 7V5a3 3 0 0 1 6 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1zm1.5 0h3V5a1.5 1.5 0 0 0-3 0v2z"/></svg> : null}
            </label>
            <label className={`registration-field-toggle insc-pill insc-pill--sm${(Boolean(field.required)) ? " is-on" : ""}`}>
              <input type="checkbox" checked={Boolean(field.required)} disabled={field.locked} onChange={(event) => updateRegistrationField(index, { required: event.target.checked })} />
              <span>Obligatorio</span>{field.locked ? <svg className="insc-pill__lock" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 7V5a3 3 0 0 1 6 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1zm1.5 0h3V5a1.5 1.5 0 0 0-3 0v2z"/></svg> : null}
            </label>
            </>
          )}
          <label className={`registration-field-toggle insc-pill insc-pill--sm${(Boolean(field.show_in_players_table || field.visible_in_players_table)) ? " is-on" : ""}`}>
            <input type="checkbox" checked={Boolean(field.show_in_players_table || field.visible_in_players_table)} onChange={(event) => updateRegistrationField(index, { show_in_players_table: event.target.checked })} />
            <span>En tabla</span>
          </label>
        </div>
        <div className="registration-field-builder__type">
          {field.kind === "custom" ? (
            <select className="t123-select" value={field.type} onChange={(event) => updateRegistrationField(index, { type: event.target.value })}>
              <option value="text">Texto</option>
              <option value="textarea">Texto largo</option>
              <option value="number">Número</option>
              <option value="date">Fecha</option>
              <option value="select">Selector</option>
              <option value="checkbox">Checkbox</option>
            </select>
          ) : <span className="registration-field-builder__type-pill">{field.kind === "document" ? "Archivo" : "Campo actual"}</span>}
          {field.kind === "custom" && field.type === "select" ? (
            <input placeholder="Opciones separadas por coma" value={(field.options ?? []).join(", ")} onChange={(event) => updateRegistrationField(index, { options: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} />
          ) : null}
          {field.kind === "custom" ? (
            <label className="registration-field-builder__key">
              <span>Va en</span>
              <select className="t123-select" value={fieldSection(field)} onChange={(event) => updateRegistrationField(index, { section: event.target.value })}>
                {REGISTRATION_FIELD_SECTIONS.map((section) => (
                  <option key={section} value={section}>{REGISTRATION_FIELD_SECTION_LABELS[section]}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        {isPhoto && usesCredential && photoLockNotice ? (
          <div className="insc-photo-blocked" role="alert" data-no-toast>
            <p>
              <strong>No se puede quitar «{photoLockNotice}» a la foto mientras el torneo use credencial.</strong>
            </p>
            <p>
              La credencial lleva la foto del jugador; sin ella no se puede generar. Si este torneo no necesita
              credencial, cámbialo en el paso 02 y la foto deja de pedirse.
            </p>
            <div className="insc-photo-blocked__actions">
              <button className="button button--secondary" type="button" onClick={goToCredentialSection}>
                Ir a 02 · Credencial
              </button>
              <button className="button button--ghost" type="button" onClick={() => setPhotoLockNotice(null)}>
                Entendido
              </button>
            </div>
          </div>
        ) : null}
      </article>
    );
  }

  function goToCredentialSection() {
    const section = document.getElementById("insc-credencial");
    section?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    section?.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.focus({ preventScroll: true });
  }

  if (loading && !hasLoadedOnce) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={5} />}
        {/* 3ª ronda #97: el anuncio de carga vive AQUÍ — este return temprano
            impedía que la live region de abajo se montara durante la carga. */}
        <section className="card" role="status" aria-live="polite">
          <p className="muted">Cargando inscripciones…</p>
        </section>
      </>
    );
  }

  const currency = settings?.currency ?? "MXN";
  const hoyLocalIso = (() => {
    const ahora = new Date();
    const mes = String(ahora.getMonth() + 1).padStart(2, "0");
    const dia = String(ahora.getDate()).padStart(2, "0");
    return `${ahora.getFullYear()}-${mes}-${dia}`;
  })();
  const startNotice = tournamentStartNotice(tournament?.start_date, hoyLocalIso);
  // Estado de inscripción por categoría (cierre por categoría, 2026-10-24).
  const hoyCdmx = todayMexicoIso();
  const divisionStates = divisions
    .filter((division) => division.is_active !== false)
    .map((division) => {
      const enrolled = participants.filter(
        (participant) => participant.division_id === division.id && participant.current_status !== "dropped",
      ).length;
      const approved = participants.filter(
        (participant) =>
          participant.division_id === division.id &&
          (participant.current_status === "active" || participant.current_status === "confirmed"),
      ).length;
      const state = divisionRegistrationState({ tournament, division, approvedCount: approved, today: hoyCdmx });
      // Sin el interruptor: lo que quedaría al apagarlo.
      const openByDates = divisionRegistrationState({
        tournament: tournament ? { ...tournament, registrations_closed_now: false } : tournament,
        division,
        approvedCount: approved,
        today: hoyCdmx,
      }).open;
      return { division, enrolled, state, openByDates };
    });
  const closedNow = tournament?.registrations_closed_now === true;
  const closedEvenWithoutSwitch = divisionStates.length > 0 && divisionStates.every(({ openByDates }) => !openByDates);

  const publicRegistrationUrl = tournament?.slug
    ? `https://torneos123.com/inscripcion/${tournament.slug}`
    : "";

  async function copyPublicRegistrationLink() {
    if (!publicRegistrationUrl) return;
    try {
      await navigator.clipboard.writeText(publicRegistrationUrl);
      setMessage("Link de inscripción copiado.");
    } catch {
      setError("No se pudo copiar el link. Cópialo manualmente desde el botón Abrir.");
    }
  }



  return (
    <>
      {setupMode && <TournamentSetupProgress current={5} />}
      <section className="admin-registrations-page setup-registration-page">
        {/* Rediseño 2026-10 (maqueta aprobada): encabezado adm-* con la barra
            de herramientas de la bandeja. Mismos botones y handlers que antes. */}
        <header className="adm-players__head insc-head">
          <div>
            <span className="adm-players__eyebrow">
              {setupMode ? "Paso 5" : "Administración del torneo"}{tournament ? ` · ${tournament.name}` : ""}
            </span>
            <h1 className="adm-players__title">
              <span className="hint-label">Inscripciones<GuideHelpButton /></span>
              <span className="adm-players__count">{scopedOrders.length} inscritos</span>
            </h1>
            <p className="muted insc-head__lead">
              Controla jugadores inscritos, pagos confirmados, fee de Torneos123 y neto del organizador.
            </p>
          </div>
          <div className="adm-players__head-actions insc-head__actions">
            <button
              className="adm-players__btn adm-players__btn--secondary"
              disabled={orders.length === 0}
              onClick={printRegistrationsPdf}
              type="button"
              title="Lista de inscritos por categoría, con el logo del organizador"
            >
              Inscritos PDF
            </button>
            <details className="insc-menu">
              <summary className="adm-players__btn adm-players__btn--secondary">Excel ▾</summary>
              <div className="insc-menu__list">
                <button
                  className="insc-menu__item"
                  disabled={orders.length === 0 || bulkLayoutUploading}
                  onClick={downloadRegistrationReviewLayout}
                  type="button"
                >
                  Descargar layout Excel
                </button>
                <label className={`insc-menu__item registration-layout-upload ${bulkLayoutUploading ? "is-uploading" : ""}`}>
                  {bulkLayoutUploading ? "Cargando layout…" : "Cargar layout lleno"}
                  <input
                    accept=".csv,.txt,text/csv,text/plain"
                    disabled={bulkLayoutUploading}
                    type="file"
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      event.target.value = "";
                      void handleRegistrationLayoutUpload(file);
                    }}
                  />
                </label>
              </div>
            </details>
            {registrationTotals.usesCurp && !tournamentReadOnly ? (
              <button
                className="adm-players__btn adm-players__btn--secondary"
                disabled={reviewingCurps}
                onClick={() => void handleReviewCurps()}
                type="button"
                title="Compara las CURP que aún no se revisan con el nombre y la fecha de nacimiento registrados. Solo avisa: no cambia nada."
              >
                {reviewingCurps
                  ? "Revisando CURPs…"
                  : `Revisar CURPs${registrationTotals.uncheckedCurp > 0 ? ` (${registrationTotals.uncheckedCurp} sin revisar)` : ""}`}
              </button>
            ) : null}
            {canMoney ? (
              <Link to={`/admin/tournaments/${tournamentId}/registrations/cobrar`} className="adm-players__btn adm-players__btn--secondary">
                Cobrar inscripción
              </Link>
            ) : null}
            {/* Eduardo 2026-09-23: dentro del wizard el stepper es la navegación; el botón sobraba. */}
            {!setupMode ? (
              <Link to={`/admin/tournaments/${tournamentId}`} className="adm-players__btn adm-players__btn--secondary">
                ← Volver al torneo
              </Link>
            ) : null}
            <button
              className="adm-players__btn adm-players__btn--primary"
              type="button"
              aria-expanded={qeOpen}
              onClick={() => setQeOpen((v) => !v)}
            >
              {qeOpen ? "Cerrar inscripción rápida" : "+ Inscribir jugador"}
            </button>
          </div>
        </header>
        {!tournamentReadOnly ? (
          <section className={`card insc-closenow${closedNow ? " is-on" : ""}`} aria-labelledby="insc-closenow-title">
            <div className="insc-closenow__text">
              <h2 id="insc-closenow-title">Cerrar inscripciones ahora</h2>
              {closedNow ? (
                <p>
                  <strong>Cerradas en este momento.</strong> El link público ya no recibe inscripciones nuevas. Quien ya
                  tiene orden puede terminar de pagar y tú puedes seguir dando altas con «+ Inscribir jugador».{" "}
                  {closedEvenWithoutSwitch
                    ? "Aunque lo apagues seguirán cerradas por tus fechas o por cupo."
                    : "Tus fechas no cambiaron: al apagarlo vuelven a mandar."}
                </p>
              ) : closedEvenWithoutSwitch ? (
                <p>Por tus fechas o por cupo ya están cerradas. Para abrirlas cambia las fechas o el cupo.</p>
              ) : (
                <p>Cierra el link público en este momento sin mover tus fechas. Puedes reabrirlo cuando quieras.</p>
              )}
            </div>
            <label className={`insc-switch insc-closenow__switch${closedNow ? " is-on" : ""}`}>
              <input
                type="checkbox"
                role="switch"
                aria-labelledby="insc-closenow-title"
                checked={closedNow}
                disabled={savingClosedNow || tournament?.id !== tournamentId}
                onChange={(event) => void handleToggleClosedNow(event.target.checked)}
              />
              <span>{savingClosedNow ? "Guardando…" : closedNow ? "Activo" : "Apagado"}</span>
            </label>
          </section>
        ) : null}
        {divisionStates.length > 0 ? (
          <section className="insc-cats" aria-label="Estado de inscripción por categoría">
            {divisionStates.map(({ division, enrolled, state }) => (
              <div className="insc-cats__row" key={division.id}>
                <div className="insc-cats__main">
                  <strong>{division.name}</strong>
                  <span>
                    {enrolled} {division.play_mode === "doubles" ? (enrolled === 1 ? "pareja" : "parejas") : enrolled === 1 ? "inscrito" : "inscritos"}
                  </span>
                </div>
                <span className={`insc-cats__chip ${state.open ? "is-open" : "is-closed"}`}>
                  {divisionStateChipLabel(state)}
                </span>
              </div>
            ))}
            {divisionStates.some(({ state }) => !state.open) ? (
              <p className="muted insc-cats__note">
                Puedes seguir dando de alta a mano en una categoría cerrada con «+ Inscribir jugador».
              </p>
            ) : null}
          </section>
        ) : null}
        {/* 2ª ronda #97: live regions permanentes — carga, éxito, fallo
            parcial y fin de lote se anuncian al lector de pantalla. */}
        <div role="alert" aria-live="assertive">
          {error ? <div className="alert alert--error">{error}</div> : null}
        </div>
        <div role="status" aria-live="polite">
          {message ? <div className="alert alert--success">{message}</div> : null}
          {bulkMarkingPaid ? <p className="sr-only">Confirmando pagos seleccionados…</p> : null}
        </div>
        {!tournamentReadOnly ? (
          <PendingApprovalNotice
            total={pendingApproval.total}
            paid={pendingApproval.paid}
            autoSendWelcome={autoSendWelcome}
            showingPending={registrationWorkFilter === "aprobar"}
            onShow={() => {
              if (registrationWorkFilter === "aprobar") setRegistrationWorkFilter(null);
              else showPendingApproval();
            }}
          />
        ) : null}
        {token && Number.isFinite(tournamentId) ? (
          <RegistrationDiagnosticsCard token={token} tournamentId={tournamentId} />
        ) : null}


        {readinessRows.some((row) => (row.out_of_draw ?? 0) > 0) && (
          <div className="alert alert--info" role="alert">
            <strong>Inscritos fuera del draw:</strong>{" "}
            {readinessRows
              .filter((row) => (row.out_of_draw ?? 0) > 0)
              .map((row) => `${row.name} (${row.out_of_draw})`)
              .join(", ")}
            . Se inscribieron después de generar el draw —{" "}
            <Link to={`/admin/tournaments/${tournamentId}/rounds`}>
              ve a Draw para regenerarlo
            </Link>{" "}
            y que jueguen.
          </div>
        )}
        {qeOpen && (
        <section className="card setup-call-card setup-registration-card insc-quick">
          <div className="card__header-row">
            <div>
              <h2 className="card__title">Inscripción rápida</h2>
              <p className="card__subtitle">
                Para cuando el jugador no llenó el formulario: perfil, categoría,
                cobro y pago en un solo paso, sin salir de esta pantalla.
              </p>
            </div>
            <button className="insc-btn insc-btn--ghost" type="button" onClick={() => setQeOpen((v) => !v)}>
              Cerrar
            </button>
          </div>
          {qeOpen && (
            <form className="form insc-quick__form" onSubmit={handleQuickEnroll}>
              {qeUsando ? (
                <p className="form-message">
                  Usando jugador existente: <strong>{qeUsando.display_name}</strong>
                  {qeUsando.phone ? ` · ${qeUsando.phone}` : ""}
                  {" "}
                  <button className="btn btn--ghost btn--compact" type="button" onClick={() => setQeUsando(null)}>
                    Cambiar
                  </button>
                </p>
              ) : (
                <>
                  <div className="insc-quick__grid">
                    <label>
                      Nombre
                      <input value={qeNombre} onChange={(e) => setQeNombre(e.target.value)} required disabled={qeSubmitting} />
                    </label>
                    <label>
                      Apellido
                      <input value={qeApellido} onChange={(e) => setQeApellido(e.target.value)} disabled={qeSubmitting} />
                    </label>
                    <label>
                      Teléfono
                      <input value={qeTelefono} onChange={(e) => setQeTelefono(e.target.value)} disabled={qeSubmitting} />
                    </label>
                    <label>
                      Email
                      <input type="email" value={qeEmail} onChange={(e) => setQeEmail(e.target.value)} disabled={qeSubmitting} />
                    </label>
                    <label>
                      Género
                      <select className="t123-select" value={qeGenero} onChange={(e) => setQeGenero(e.target.value)} disabled={qeSubmitting}>
                        <option value="">—</option>
                        <option value="male">Varonil</option>
                        <option value="female">Femenil</option>
                      </select>
                    </label>
                    <label>
                      Fecha de nacimiento
                      <input type="date" value={qeNacimiento} onChange={(e) => setQeNacimiento(e.target.value)} disabled={qeSubmitting} />
                    </label>
                  </div>
                </>
              )}

              {qeDivisionesActivas.length > 0 && (
                <div className="insc-quick__cats">
                  <span className="insc-quick__label">Categoría(s)</span>
                  <div className="insc-pills">
                    {qeDivisionesActivas.map((division) => (
                      <label key={division.id} className={`insc-pill${qeDivisiones.has(division.id) ? " is-on" : ""}`}>
                        <input
                          type="checkbox"
                          checked={qeDivisiones.has(division.id)}
                          onChange={() =>
                            setQeDivisiones((prev) => {
                              const next = new Set(prev);
                              if (next.has(division.id)) next.delete(division.id);
                              else next.add(division.id);
                              return next;
                            })
                          }
                          disabled={qeSubmitting}
                        />
                        <span>{division.name}</span>
                      </label>
                    ))}
                  </div>
                  {qeRestriccionSinDatos && (
                    <p className="form__hint">
                      Ojo: alguna categoría marcada tiene restricción de edad o género y
                      no capturaste esos datos — puedes completarlos después en su perfil.
                    </p>
                  )}
                  {qeSinCategoria && (
                    <p className="form__hint">Selecciona al menos una categoría para crear la inscripción.</p>
                  )}
                </div>
              )}

              <div className="insc-quick__foot">
                {qeFeeOptions.length > 1 && (
                  <label>
                    Tipo de costo
                    <select className="t123-select" value={qeFee} onChange={(e) => setQeFee(e.target.value)} disabled={qeSubmitting}>
                      {qeFeeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                )}
                {canMoney ? (
                <label className={`insc-switch${qeMarkPaid ? " is-on" : ""}`}>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={qeMarkPaid}
                    onChange={(e) => setQeMarkPaid(e.target.checked)}
                    disabled={qeSubmitting}
                  />
                  <span>
                    <span className="hint-label">
                      Ya pagó
                      <FieldHint>
                        Registra también el pago manual al crear la inscripción,
                        sin esperar comprobante.
                      </FieldHint>
                    </span>
                  </span>
                </label>
                ) : null}
                <button className="insc-btn insc-btn--lime insc-quick__submit" type="submit" disabled={qeSubmitting || (!qeUsando && !qeNombre.trim()) || qeSinCategoria}>
                  {qeSubmitting ? "Inscribiendo…" : "Inscribir"}
                </button>
              </div>
              {/* Las categorías se CONSERVAN entre altas a propósito (flujo de
                  mostrador). Cowork 2026-09-20 inscribió a 6 jugadores en dos
                  categorías sin querer: aquí se ve siempre en cuáles va a quedar. */}
              {qeDivisionNames.length > 0 ? (
                <p className={qeDivisionNames.length > 1 ? "field-warning" : "muted"} style={{ margin: "0.35rem 0 0" }}>
                  Se inscribirá en: <strong>{qeDivisionNames.join(" + ")}</strong>
                  {qeDivisionNames.length > 1 ? ` (${qeDivisionNames.length} categorías, un solo cobro). Desmarca la que no aplique.` : "."}
                </p>
              ) : null}
              {/* Las sugerencias de duplicados van DEBAJO del botón: aparecen
                  por el debounce de búsqueda mientras el admin teclea, y si se
                  insertan arriba desplazan "Inscribir" justo cuando va a
                  clickearlo (QA Cowork #12: primer click perdido). */}
              {!qeUsando && qeMatches.length > 0 && (
                <div className="alert alert--info" role="note">
                  <strong>¿Es alguno de estos?</strong> Evita duplicarlo:
                  {qeMatches.map((match) => (
                    <button
                      key={match.id}
                      className="btn btn--ghost btn--compact"
                      type="button"
                      onClick={() => setQeUsando(match)}
                    >
                      {match.display_name}{match.phone ? ` · ${match.phone}` : ""}
                    </button>
                  ))}
                </div>
              )}
            </form>
          )}
        </section>
        )}

        <div className="registration-layout-grid setup-registration-layout-grid">
          <section className="card setup-call-card setup-registration-card setup-registration-card--stats">
            <div className="setup-call-card__heading">
              <span>01</span>
              <div>
                <h2>Dashboard de Estado de Inscripciones</h2>
                <p>
                  Resumen rápido del dinero registrado y pagos pendientes. El costo, los precios por
                  tipo y la cuenta de cobro se editan en{" "}
                  <Link to={`/admin/tournaments/${tournamentId}/edit`}>Datos del torneo</Link>.
                </p>
              </div>
            </div>
            <div className="insc-money">
              <div className="insc-money__net">
                <span className="insc-money__label">Neto organizador</span>
                <strong className="insc-money__net-value">{money(summary?.organizer_net_ready, currency)}</strong>
                <small>Listo para liberar al admin</small>
              </div>
              <div className="insc-money__metrics">
                <div className="insc-money__metric insc-money__metric--lime">
                  <span className="insc-money__label">Cobrado</span>
                  <strong>{money(summary?.gross_collected, currency)}</strong>
                  <small>{`${summary?.paid_orders ?? 0} de ${summary?.total_orders ?? 0} pagados · ${paidPercent}%`}</small>
                </div>
                <div className="insc-money__metric">
                  <span className="insc-money__label">Fee plataforma</span>
                  <strong>{money(summary?.platform_fee_accrued, currency)}</strong>
                </div>
              </div>
              <div className="registration-visual-status-strip insc-money__chips" aria-label="Estados visuales de inscripción">
                {PAYMENT_VISUAL_STATES.map((state) => (
                  <span className={`registration-status registration-status--${state.key}`} key={state.key}>
                    {state.label}: {visualStatusCounts[state.key] ?? 0}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* 02 Credencial (Eduardo 2026-10-30): se decide ANTES del formulario
              porque cambia lo que se le pide al jugador (la foto). */}
          <section
            className="card setup-call-card setup-registration-card registration-credential-card"
            id="insc-credencial"
            aria-labelledby="insc-credencial-title"
          >
            <div className="setup-call-card__heading">
              <span>02</span>
              <div>
                <h2 className="hint-label" id="insc-credencial-title">
                  Credencial
                  <FieldHint label="¿De dónde sale la credencial?">
                    La credencial usa el nombre del torneo, la empresa y el logo del organizador que
                    configuras en <Link to={`/admin/tournaments/${tournamentId}/edit`}>Datos del torneo</Link>,
                    más la foto que cada jugador sube al inscribirse.
                    {/* Apagada, Datos del torneo esconde el botón de regenerar. */}
                    {usesCredential
                      ? " Si cambias esa identidad después de emitir credenciales, en Datos del torneo está el botón para regenerarlas."
                      : " Este torneo no la usa: se activa eligiendo «Sí, usa credencial»."}
                  </FieldHint>
                </h2>
                <p>Decide esto primero: cambia lo que el formulario le pide al jugador.</p>
              </div>
            </div>
            <p className="insc-cred__question" id="insc-cred-question">¿Este torneo usa credencial?</p>
            <div className="insc-cred__choice" role="radiogroup" aria-labelledby="insc-cred-question">
              <label className={`insc-cred__option${usesCredential ? " is-on" : ""}`}>
                <input
                  type="radio"
                  name={`insc-credencial-${tournamentId}`}
                  value="on"
                  checked={usesCredential}
                  disabled={!settings || savingCredentialEnabled || tournamentReadOnly}
                  onChange={() => void handleToggleCredentialEnabled(true)}
                />
                <span>
                  <strong>Sí, usa credencial</strong>
                  <small>Cada jugador recibe su credencial digital con su foto.</small>
                </span>
              </label>
              <label className={`insc-cred__option${!usesCredential ? " is-on" : ""}`}>
                <input
                  type="radio"
                  name={`insc-credencial-${tournamentId}`}
                  value="off"
                  checked={!usesCredential}
                  disabled={!settings || savingCredentialEnabled || tournamentReadOnly}
                  onChange={() => void handleToggleCredentialEnabled(false)}
                />
                <span>
                  <strong>No usa credencial</strong>
                  <small>No se pide foto y no se generan credenciales.</small>
                </span>
              </label>
            </div>
            {usesCredential && settings?.credential_photo_missing ? (
              <div className="insc-cred__box insc-cred__box--warn" data-no-toast>
                <p>
                  <strong>Tu formulario no pide la foto: actívala o indica que no usas credencial</strong>
                </p>
                <p>
                  Sin la «Foto de medio cuerpo» no se puede hacer la credencial. Los inscritos que no tengan foto
                  solo quedan como pendientes; no se les manda ningún recordatorio.
                </p>
                <div className="insc-cred__actions">
                  <button
                    className="button button--primary"
                    type="button"
                    disabled={savingCredentialEnabled || tournamentReadOnly}
                    onClick={() => void handleToggleCredentialEnabled(true)}
                  >
                    {savingCredentialEnabled ? "Guardando…" : "Activar la foto"}
                  </button>
                  <button
                    className="button button--secondary"
                    type="button"
                    disabled={savingCredentialEnabled || tournamentReadOnly}
                    onClick={() => void handleToggleCredentialEnabled(false)}
                  >
                    No usa credencial
                  </button>
                </div>
              </div>
            ) : usesCredential ? (
              <div className="insc-cred__box insc-cred__box--ok" data-no-toast>
                <p>
                  <strong>La «Foto de medio cuerpo» quedó Activa y Obligatoria</strong> en el formulario (paso 03). Sin
                  foto no se puede hacer la credencial.
                </p>
                <p>La CURP se sigue pidiendo si está activa en tu formulario.</p>
              </div>
            ) : (
              <div className="insc-cred__box insc-cred__box--info" data-no-toast>
                <p>
                  <strong>El link de inscripción ya no pide la foto de medio cuerpo</strong>, no se generan credenciales
                  y los jugadores no ven la suya.
                </p>
                <p>Las fotos que ya subieron se guardan; si vuelves a decir «Sí», regresan.</p>
              </div>
            )}
            {usesCredential ? (
              <div className="insc-cred__actions">
                <button
                  className="button button--primary"
                  type="button"
                  disabled={loadingCredentialSample}
                  onClick={() => void handleViewCredentialSample()}
                >
                  {loadingCredentialSample ? "Generando…" : "Ver credencial muestra"}
                </button>
              </div>
            ) : null}
          </section>

          <section className="card setup-call-card setup-registration-card registration-form-fields-card">
            <div className="setup-call-card__heading">
              <span>03</span>
              <div>
                <h2 className="hint-label">
                  Formulario y link público
                  <FieldHint label="¿Qué significa cada control de un campo?">
                    <RegistrationFieldLegend />
                  </FieldHint>
                </h2>
                <p>Define qué verá el jugador y comparte el link, todo desde aquí.</p>
              </div>
            </div>
            {settings?.guardian_fields_auto_enabled && hasKidsDivisions ? (
              <p className="guardian-minors-notice" role="status" data-no-toast>
                {GUARDIAN_AUTO_ENABLED_NOTICE}
              </p>
            ) : null}
            {guardianStillOnWithoutMinors ? (
              <p className="guardian-minors-hint" role="status" data-no-toast>
                {GUARDIAN_STILL_ON_HINT}
              </p>
            ) : null}
            {/* Sin aviso «Ojo… están apagados» (Eduardo 2026-10-03, torneo 246):
                con «Mi hijo o hija» el formulario público siempre pide los
                datos del papá, mamá o tutor, así que el aviso era falso. */}
            {!editingRegistrationFields ? (
              <div className="registration-form-fields-summary">
                <div className="public-registration-link-card__actions">
                  <span className="hint-label">
                    <button className="button button--primary" type="button" onClick={() => setEditingRegistrationFields(true)}>Editar campos</button>
                    <FieldHint label="¿Cómo funcionan los campos?">
                      Los campos actuales están cargados como default. Los
                      obligatorios del sistema quedan bloqueados como “Requerido
                      por el sistema”.
                    </FieldHint>
                  </span>
                  <button className="button button--ghost" type="button" onClick={addCustomRegistrationField}>+ Crear campo</button>
                  <button className="button button--secondary" type="button" onClick={applyMinimalRegistrationTemplate}>
                    {minimalTemplateApplied ? "Ya está en campos mínimos" : "Aplicar campos mínimos del sistema"}
                  </button>
                  {canUseMexTourCupTemplate ? (
                    <button className="button button--secondary" type="button" onClick={applyMexTourCupRegistrationTemplate}>
                      {mexTourCupTemplateApplied ? "Reaplicar plantilla Mex Tour Cup" : "Aplicar plantilla Mex Tour Cup"}
                    </button>
                  ) : null}
                </div>
                {/* «Usar el formulario de otro torneo» (2026-10-11): solo tus
                    torneos (dueño o coadmin); copia el formulario, nunca
                    documentos ni pagos. */}
                <details
                  className="insc-copy-form"
                  onToggle={(event) => {
                    if ((event.currentTarget as HTMLDetailsElement).open) void loadFormSources();
                  }}
                >
                  <summary>Usar el formulario de otro torneo</summary>
                  {formSources === null ? (
                    <p className="muted">Cargando tus torneos…</p>
                  ) : formSources.length === 0 ? (
                    <p className="muted">Ninguno de tus otros torneos tiene formulario propio todavía.</p>
                  ) : (
                    <div className="insc-copy-form__row">
                      <label className="insc-copy-form__field">
                        <span>Torneo</span>
                        <select className="t123-select" value={formSourceId} onChange={(event) => setFormSourceId(event.target.value)} disabled={copyingFormSource || tournamentReadOnly}>
                          <option value="">Elige un torneo…</option>
                          {[...formSources].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })).map((source) => (
                            <option key={source.tournament_id} value={source.tournament_id}>
                              {source.name} · {source.field_count} campos
                            </option>
                          ))}
                        </select>
                      </label>
                      {formSourceId ? (
                        <span className="insc-copy-form__preview">
                          {(() => {
                            const source = formSources.find((item) => String(item.tournament_id) === formSourceId);
                            if (!source) return null;
                            return `${source.field_count} campos (${source.enabled_field_count} activos)${source.updated_at ? ` · actualizado ${formatDate(source.updated_at)}` : ""}`;
                          })()}
                        </span>
                      ) : null}
                      <button
                        className="insc-btn insc-btn--navy"
                        type="button"
                        disabled={!formSourceId || copyingFormSource || tournamentReadOnly}
                        onClick={() => void handleCopyFormSource()}
                      >
                        {copyingFormSource ? "Copiando…" : "Copiar formulario"}
                      </button>
                    </div>
                  )}
                </details>
              </div>
            ) : (
              <div className="registration-field-builder insc-fields">
                <div className="registration-field-builder__header" aria-hidden="true">
                  <span>Campo</span>
                  <span>Configuración</span>
                  <span>Tipo</span>
                </div>
                <p className="registration-field-builder__sections-note">Las secciones solo ordenan dónde lo ve la familia</p>
                {registrationDocumentRows.length > 0 ? (
                  <section className="registration-field-builder__section insc-fields__documents" aria-labelledby="registration-field-section-documents">
                    <div className="insc-fields__section-head">
                      <h3 className="registration-field-builder__section-title" id="registration-field-section-documents">
                        Documentos
                      </h3>
                      <span className="insc-fields__count">{registrationDocumentRows.length}</span>
                    </div>
                    <p className="insc-fields__documents-note">
                      Siempre arriba. En el link público se piden en el paso de documentos, como hoy.
                    </p>
                    {registrationDocumentRows.map(({ field, index }) => (
                      <Fragment key={`${field.key}-${index}`}>{renderRegistrationFieldRow(field, index)}</Fragment>
                    ))}
                  </section>
                ) : null}
                {registrationFieldGroups.map(({ section, rows }) => (
                  <section className={`registration-field-builder__section${collapsedFieldSections.has(section) ? " is-collapsed" : ""}`} key={section} aria-labelledby={`registration-field-section-${section}`}>
                    <div className="insc-fields__section-head">
                      <h3 className="registration-field-builder__section-title" id={`registration-field-section-${section}`}>
                        {REGISTRATION_FIELD_SECTION_LABELS[section]}
                      </h3>
                      <span className="insc-fields__count">{rows.length}</span>
                      <button
                        type="button"
                        className="insc-fields__toggle"
                        aria-expanded={!collapsedFieldSections.has(section)}
                        aria-label={`${collapsedFieldSections.has(section) ? "Mostrar" : "Ocultar"} ${REGISTRATION_FIELD_SECTION_LABELS[section]}`}
                        onClick={() => toggleFieldSection(section)}
                      >
                        {collapsedFieldSections.has(section) ? "Mostrar" : "Ocultar"}
                      </button>
                    </div>
                    {!collapsedFieldSections.has(section) && rows.map(({ field, index }) => (
                      <Fragment key={`${field.key}-${index}`}>{renderRegistrationFieldRow(field, index)}</Fragment>
                    ))}
                  </section>
                ))}
                <div className="public-registration-link-card__actions registration-field-builder__actions">
                  <button className="button button--ghost" type="button" onClick={addCustomRegistrationField}>+ Crear nuevo campo</button>
                  <button className="button button--secondary" type="button" onClick={applyMinimalRegistrationTemplate}>
                    {minimalTemplateApplied ? "Ya está en campos mínimos" : "Aplicar campos mínimos del sistema"}
                  </button>
                  {canUseMexTourCupTemplate ? (
                    <button className="button button--secondary" type="button" onClick={applyMexTourCupRegistrationTemplate}>
                      {mexTourCupTemplateApplied ? "Reaplicar plantilla Mex Tour Cup" : "Aplicar plantilla Mex Tour Cup"}
                    </button>
                  ) : null}
                  <button className="button button--secondary" type="button" onClick={() => { setRegistrationFormFields(normalizedRegistrationSchema.fields); setEditingRegistrationFields(false); setPhotoLockNotice(null); }}>Cancelar</button>
                  <button className="button button--primary" disabled={savingRegistrationFields} type="button" onClick={() => void handleSaveRegistrationFields()}>
                    {savingRegistrationFields ? "Guardando…" : "Guardar campos"}
                  </button>
                </div>
              </div>
            )}

            {/* El link vive JUNTO a los campos que define (pedido Eduardo
                2026-08-06): antes estaba dos tarjetas más abajo, así que se
                compartía sin revisar el formulario que iba a abrir. */}
            {publicRegistrationUrl ? (
              <div className="registration-public-link-block">
                <h3>
                  <span className="hint-label">
                    Link público de inscripción
                    <FieldHint>
                      Compártelo con jugadores: verán la convocatoria y este
                      formulario.
                    </FieldHint>
                  </span>
                </h3>
                {startNotice && (
                  <p role="alert" className="form-error registration-start-notice">
                    ⚠️ {startNotice.message}
                  </p>
                )}
                <div className="public-registration-link-card__url">{publicRegistrationUrl}</div>
                <div className="public-registration-link-card__actions">
                  <a className="button button--primary" href={publicRegistrationUrl} target="_blank" rel="noreferrer">
                    Abrir link
                  </a>
                  <button className="button button--ghost" type="button" onClick={() => void copyPublicRegistrationLink()}>
                    Copiar link
                  </button>
                </div>
              </div>
            ) : null}
          </section>

            <div className="card setup-call-card setup-registration-card registration-orders-card public-registration-inbox-card" id="registros-recibidos">
              <div className="setup-call-card__heading registration-orders-heading">
                <span>04</span>
                <div>
                  <h2>Registros recibidos</h2>
                  <p>Revisa jugadores que llegaron por el link público, sus documentos y el pago manual.</p>
                </div>
                {/* 2ª ronda #97: "pendientes" = cobrables de verdad; el
                    summary.pending_orders del backend aún cuenta terminales. */}
                <span className="status-chip">{registrationTotals.pendingPay} pendientes</span>
              </div>
              {/* Tarjeta azul: cada recuadro es el MISMO filtro de trabajo que
                  antes eran los chips (mismo nombre accesible). «Inscritos»
                  quita el filtro. */}
              {orders.length > 0 && (
                <div className="insc-summary">
                  <div className="insc-summary__tiles">
                    <button
                      type="button"
                      className={`insc-summary__tile${registrationWorkFilter === null ? " is-active" : ""}`}
                      aria-pressed={registrationWorkFilter === null}
                      onClick={() => setRegistrationWorkFilter(null)}
                    >
                      <span className="insc-summary__value">{scopedOrders.length}</span>
                      <span className="insc-summary__label">Inscritos</span>
                    </button>
                    {([
                      ["pago", `Pendientes de pago (${registrationTotals.pendingPay})`, registrationTotals.pendingPay, "Pendientes de pago"],
                      ["docs", `Docs incompletos (${registrationTotals.incompleteDocs})`, registrationTotals.incompleteDocs, "Documentos incompletos"],
                      ...(registrationTotals.usesCurp || registrationWorkFilter === "curp"
                        ? [["curp", `CURP por revisar (${registrationTotals.toReviewCurp})`, registrationTotals.toReviewCurp, "CURP por revisar"] as const]
                        : []),
                      ["listos", "Listos ✓", registrationTotals.ready, "Listos"],
                    ] as const).map(([key, label, value, text]) => (
                      <button
                        key={key}
                        type="button"
                        aria-label={label}
                        className={`insc-summary__tile registration-work-filter${registrationWorkFilter === key ? " is-active" : ""}`}
                        onClick={() => setRegistrationWorkFilter(registrationWorkFilter === key ? null : key)}
                      >
                        <span className="insc-summary__value" aria-hidden="true">{value}</span>
                        <span className="insc-summary__label" aria-hidden="true">{text}</span>
                      </button>
                    ))}
                  </div>
                  {selectedOrderIds.size > 0 && canMoney && (
                    <button
                      type="button"
                      className="button button--sm registration-paid-button"
                      disabled={bulkMarkingPaid}
                      onClick={() => void handleBulkMarkPaid()}
                    >
                      {bulkMarkingPaid ? "Confirmando…" : `Confirmar pago (${selectedOrderIds.size})`}
                    </button>
                  )}
                </div>
              )}
              <div className="registration-inbox-toolbar">
                <label className="registration-inbox-search">
                  Buscar registro
                  <input
                    value={registrationSearch}
                    onChange={(event) => setRegistrationSearch(event.target.value)}
                    placeholder="Jugador, categoría, email, pago…"
                  />
                </label>
                <div className="registration-inbox-toolbar__meta">
                  <strong>{visibleOrders.length}</strong> visibles de {scopedOrders.length}
                  {teamOrders.length > 0 && (
                    <button
                      className="button button--ghost button--sm"
                      onClick={() => setShowTeamOrders((current) => !current)}
                      title="El cobro es por jugador; las órdenes ancladas a una pareja se ocultan de la lista"
                      type="button"
                    >
                      {showTeamOrders
                        ? "Ocultar órdenes de pareja"
                        : `Mostrar ${teamOrders.length} de pareja`}
                    </button>
                  )}
                </div>
              </div>

              {orders.length > 0 && (
                <div className="registration-inbox-filters">
                  <div className="registration-inbox-filters__categories">
                    {registrationCategorySummary.map((entry) => {
                      const active = registrationCategoryFilter === entry.id;
                      return (
                        <button
                          key={String(entry.id)}
                          type="button"
                          className={`registration-category-chip${active ? " is-active" : ""}`}
                          onClick={() => setRegistrationCategoryFilter(active ? null : entry.id)}
                        >
                          {entry.name} · {entry.total}
                          {entry.pendingPay > 0 && (
                            <span className="registration-category-chip__pending">{entry.pendingPay} deben</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {orders.length === 0 ? (
                <p className="muted">Aún no hay registros recibidos en este torneo.</p>
              ) : visibleOrders.length === 0 ? (
                <p className="muted">
                  {scopedOrders.length === 0 && teamOrders.length > 0
                    ? "Solo hay órdenes de pareja, ocultas porque el cobro es por jugador. Usa «Mostrar de pareja» para verlas."
                    : "No hay registros que coincidan con la búsqueda."}
                </p>
              ) : (
                <div className="registration-orders-table-wrap public-registration-inbox-table-wrap">
                  <table className="registration-orders-table public-registration-inbox-table registration-compact-table">
                    <thead>
                      <tr>
                        <th className="registration-compact-check" aria-label="Seleccionar" />
                        <th>Jugador</th>
                        <th>Categoría</th>
                        <th>Pago</th>
                        <th>Documentos</th>
                        <th className="registration-compact-chevron" aria-label="Detalle" />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleOrders.map((order) => {
                        const participant = order.participant;
                        const docs = participant?.registration_documents;
                        const partnerInfo = partnerInfoForOrder(order);
                        // Eduardo 2026-09-16: al formar pareja el registro individual queda
                        // "dropped" y se veía "Rechazado"; con pareja se muestra el estado real del pago.
                        const paymentState = paymentStateFor(order);
                        // Gate #155: la orden de pareja es SOLO auditoría —
                        // sin pagos, sin lote, sin acciones de jugador;
                        // únicamente cancelar. El backend rebota paid (409).
                        const isTeamOrder = isTeamAnchoredOrder(order);
                        const pairOptions = isDoublesOrder(order) && !partnerInfo ? availablePartnerOrders(order) : [];
                        const paidOnline = registrationPaidOnline(order);
                        // «Llegó» sale de has_*: el perfil Capturista ve que la
                        // familia mandó el archivo aunque no reciba la liga para
                        // abrirlo (sin liga no hay «Ver»).
                        const hasPhoto = registrationDocumentReceived(docs, "photo");
                        const hasCurp = registrationDocumentReceived(docs, "curp_pdf");
                        const hasReceipt = registrationDocumentReceived(docs, "payment_receipt");
                        // Como la foto: el Capturista ve que la credencial ya se
                        // generó (has_credential) aunque no reciba la liga.
                        const hasCredential = Boolean(docs?.credential_url || docs?.has_credential);
                        const credential = {
                          ready: hasCredential,
                          ...credentialStatus(hasCredential, participant?.current_status === "active", hasPhoto),
                        };
                        const allDocItems = [
                          { key: "photo", label: "Foto", short: "F", url: docs?.photo_url, ready: hasPhoto, dotClass: hasPhoto ? "is-ready" : "is-missing", statusText: documentStatusLabel(hasPhoto, "Foto"), uploadLabel: hasPhoto ? "Cambiar" : "Subir", accept: "image/jpeg,image/png,image/webp" },
                          // «C» con la revisión de la CURP: ámbar por revisar, rojo si parece de otra persona.
                          { key: "curp", label: "CURP", short: "C", url: docs?.curp_pdf_url, ready: hasCurp, dotClass: curpChipClass(order.curp_check, hasCurp), statusText: curpStatusText(order.curp_check, hasCurp), uploadLabel: hasCurp ? "Cambiar" : "Subir", accept: "application/pdf" },
                          {
                            key: "payment_receipt",
                            label: "Comprobante",
                            short: "$",
                            url: docs?.payment_receipt_url,
                            // Pago en línea confirmado: el comprobante no aplica y el chip va en verde.
                            ready: hasReceipt || paidOnline,
                            dotClass: hasReceipt || paidOnline ? "is-ready" : "is-missing",
                            statusText: hasReceipt
                              ? documentStatusLabel(true, "Comprobante")
                              : paidOnline
                                ? "Comprobante no aplica · pago en línea confirmado"
                                : documentStatusLabel(false, "Comprobante"),
                            uploadLabel: hasReceipt ? "Cambiar" : "Subir",
                            accept: "application/pdf,image/jpeg,image/png,image/webp",
                          },
                        ] as const;
                        // «F» es la foto para la credencial (Eduardo 2026-10-05):
                        // sin credencial no se pide y no se muestra.
                        const docItems = usesCredential ? allDocItems : allDocItems.filter((doc) => doc.key !== "photo");
                        const isExpanded = expandedOrderId === order.id;
                        return (
                          <Fragment key={order.id}>
                            <tr
                              id={`registration-order-${order.id}`}
                              className={`registration-compact-row${isExpanded ? " is-expanded" : ""}`}
                              onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                            >
                              <td className="registration-compact-check" onClick={(event) => event.stopPropagation()}>
                                {registrationOrderCollectable(order) && (
                                  <input
                                    aria-label={`Seleccionar a ${participantDisplayName(participant)} para confirmar pago`}
                                    checked={selectedOrderIds.has(order.id)}
                                    onChange={() => toggleOrderSelected(order.id)}
                                    type="checkbox"
                                  />
                                )}
                              </td>
                              <td className="registration-player-cell">
                                <strong>{participantDisplayName(participant)}</strong>
                                <small>#{order.id} · {sourceLabel(participant?.registration_source)}{partnerInfo ? ` · pareja: ${partnerInfo.partnerName}` : ""}</small>
                              </td>
                              <td className="registration-compact-category">
                                {participant?.division?.name ?? <span className="muted">Sin categoría</span>}
                                {orderOtherCategories(order, coverageOptionsFor(order)).map((other) => (
                                  <small className="registration-also-chip" key={other.participantId}>
                                    También: {other.name} · {other.statusLabel}
                                  </small>
                                ))}
                              </td>
                              <td>
                                <span className={`registration-status registration-status--${paymentState.key}`} title={paymentState.hint}>
                                  {paymentState.label}
                                </span>
                                {order.fee_category && order.fee_category !== "general" ? (
                                  <small className="muted" title="Tipo declarado por el jugador; validar por fuera">
                                    {" "}{feeCategoryLabel(order.fee_category, settings)}
                                  </small>
                                ) : null}
                                {order.balance_due_status === "pending" ? (
                                  <small
                                    className="registration-status registration-status--pending"
                                    title={order.balance_due_reason ?? "Saldo por reclasificación"}
                                  >
                                    {" "}Saldo {money(order.balance_due_amount ?? "0", currency)}
                                  </small>
                                ) : null}
                              </td>
                              <td>
                                <span className="registration-doc-strip">
                                  {docItems.map((doc) => (
                                    <span
                                      key={doc.key}
                                      className={`registration-doc-dot insc-doc-chip ${doc.dotClass}`}
                                      role="img"
                                      aria-label={doc.statusText}
                                      title={doc.statusText}
                                    >
                                      {doc.key === "photo" ? "Foto" : doc.key === "curp" ? "CURP" : "$"} {docChipMark(doc.dotClass)}
                                    </span>
                                  ))}
                                  {usesCredential ? (
                                    <span
                                      className={`registration-doc-dot insc-doc-chip ${credential.ready ? "is-ready" : "is-missing"}`}
                                      role="img"
                                      aria-label={credential.hint}
                                      title={credential.hint}
                                    >
                                      {credential.label}
                                    </span>
                                  ) : null}
                                </span>
                              </td>
                              <td className="registration-compact-chevron" onClick={(event) => event.stopPropagation()}>
                                <button
                                  type="button"
                                  className="registration-compact-toggle"
                                  aria-expanded={isExpanded}
                                  aria-controls={isExpanded ? `registration-detail-${order.id}` : undefined}
                                  onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                                >
                                  <span aria-hidden="true">{isExpanded ? "▾" : "▸"}</span>
                                  <span className="sr-only">
                                    {isExpanded ? "Cerrar" : "Ver"} detalle de {participantDisplayName(participant)}
                                  </span>
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="registration-detail-row" id={`registration-detail-${order.id}`}>
                                <td colSpan={6}>
                                  <div className="registration-detail-grid insc-detail">
                                    <div className="insc-detail__col">
                                    <div className="registration-detail-section">
                                      <h4>Categoría{hasDoublesRegistrations ? " y pareja" : ""}</h4>
                                      {participant ? (
                                        <label className="registration-division-cell registration-division-cell--compact">
                                          <select className="t123-select"
                                            aria-label={`Categoría de ${participantDisplayName(participant)}`}
                                            disabled={isTeamOrder || changingDivisionParticipantId === participant.id || divisions.length === 0 || Boolean(partnerInfo)}
                                            title={isTeamOrder ? "La categoría de una pareja se administra desde sus integrantes" : undefined}
                                            value={participant.division_id ?? ""}
                                            onChange={(event) => void handleChangeParticipantDivision(
                                              order,
                                              event.target.value ? Number(event.target.value) : null,
                                            )}
                                          >
                                            <option value="">Sin categoría</option>
                                            {divisions.map((division) => (
                                              <option key={division.id} value={division.id}>{division.name}</option>
                                            ))}
                                          </select>
                                          <small>{participant.division ? divisionDetailsLabel(participant.division) : "Selecciona una categoría"}</small>
                                        </label>
                                      ) : (
                                        <span className="muted">—</span>
                                      )}
                                      {hasDoublesRegistrations && isDoublesOrder(order) && participant?.player_profile_id ? (
                                        partnerInfo ? (
                                          <div className="registration-pair-cell registration-pair-cell--ready">
                                            <strong>{partnerInfo.partnerName}</strong>
                                            <small>Equipo creado</small>
                                          </div>
                                        ) : (
                                          <label className="registration-pair-cell">
                                            {(() => {
                                              const declared = declaredPartnerName(order);
                                              const suggested = suggestedPartnerOrder(order, pairOptions);
                                              const orderedOptions = suggested
                                                ? [suggested, ...pairOptions.filter((candidate) => candidate.id !== suggested.id)]
                                                : pairOptions;
                                              return (
                                                <>
                                                  {declared ? (
                                                    <small className="registration-pair-cell__declared">Declaró como pareja: <strong>{declared}</strong></small>
                                                  ) : null}
                                                  {suggested ? (
                                                    <button
                                                      type="button"
                                                      className="button button--sm registration-pair-cell__suggest"
                                                      disabled={pairingParticipantId === participant.id}
                                                      onClick={() => void handleCreateRegistrationPair(order, suggested.id)}
                                                      title="Coincide con la pareja declarada en el formulario"
                                                    >
                                                      Vincular con {participantDisplayName(suggested.participant)}
                                                    </button>
                                                  ) : null}
                                                  <select className="t123-select"
                                                    aria-label={`Pareja de ${participantDisplayName(participant)}`}
                                                    disabled={pairingParticipantId === participant.id || pairOptions.length === 0}
                                                    value=""
                                                    onChange={(event) => void handleCreateRegistrationPair(order, Number(event.target.value))}
                                                  >
                                                    <option value="">Seleccionar pareja</option>
                                                    {orderedOptions.map((candidate) => (
                                                      <option key={candidate.id} value={candidate.id}>
                                                        {participantDisplayName(candidate.participant)}{suggested && candidate.id === suggested.id ? " · sugerida" : ""}
                                                      </option>
                                                    ))}
                                                  </select>
                                                  <small>
                                                    {pairOptions.length === 0
                                                      ? "Sin jugadores disponibles"
                                                      : suggested
                                                        ? "Sugerencia por el nombre declarado; confírmala o elige otra"
                                                        : declared
                                                          ? "Su pareja aún no se ha inscrito o el nombre no coincide"
                                                          : "Forma equipo de 2"}
                                                  </small>
                                                </>
                                              );
                                            })()}
                                          </label>
                                        )
                                      ) : null}
                                    </div>
                                    <div className="registration-detail-section">
                                      <h4>Estado y pago</h4>
                                      <span className={`registration-review-status registration-review-status--${partnerInfo ? "paired" : participant?.current_status ?? "unknown"}`}>
                                        {partnerInfo ? "En pareja" : participantStatusLabel(participant?.current_status)}
                                      </span>
                                      <div className="registration-payment-cell">
                                        {isTeamOrder ? (
                                          <>
                                            <small>El cobro es por jugador — orden de pareja en modo auditoría.</small>
                                            <span className={`registration-status registration-status--${paymentState.key}`}>{paymentState.label}</span>
                                            {order.status === "pending" && (
                                              <button
                                                className="button button--sm button--ghost registration-reject-button"
                                                disabled={editingPaymentId === order.id}
                                                onClick={() => void handleCancelTeamOrder(order)}
                                                type="button"
                                              >
                                                {editingPaymentId === order.id ? "Cancelando…" : "Cancelar orden"}
                                              </button>
                                            )}
                                          </>
                                        ) : (
                                          <>
                                            <small>{paymentState.hint}</small>
                                            <select
                                              aria-label={`Estatus de pago de ${participantDisplayName(participant)}`}
                                              className="t123-select registration-payment-select"
                                              disabled={
                                                editingPaymentId === order.id ||
                                                (isPaymentStatusLockedForProvider(order) && !hasRole("superadmin")) ||
                                                (!canMoney && order.status !== "pending")
                                              }
                                              title={
                                                isPaymentStatusLockedForProvider(order) && !hasRole("superadmin")
                                                  ? "Confirmado por el proveedor de pago"
                                                  : !canMoney
                                                    ? "Soporte Torneos123 solo puede rechazar una inscripción sin pago"
                                                    : undefined
                                              }
                                              value={order.status}
                                              onChange={(event) => void handleUpdatePayment(order, event.target.value as RegistrationPaymentStatus)}
                                            >
                                              <option value="pending">Pendiente de pago</option>
                                              {canMoney || order.status === "paid" ? <option value="paid">Pago confirmado</option> : null}
                                              <option value="cancelled">Rechazado</option>
                                              {canMoney || order.status === "refunded" ? <option value="refunded">Reembolsado</option> : null}
                                            </select>
                                            {canMoney && order.status === "paid" && refundDraft?.orderId !== order.id && (
                                              <button
                                                className="button button--sm button--ghost registration-reject-button"
                                                disabled={editingPaymentId === order.id}
                                                onClick={() => handleOpenRefund(order)}
                                                type="button"
                                                title={ONLINE_PAYMENT_PROVIDERS.has(order.provider) ? "Devuelve todo o una parte por Mercado Pago" : "Asienta el reembolso (total o parcial); el dinero lo devuelves tú"}
                                              >
                                                Emitir reembolso
                                              </button>
                                            )}
                                            {order.status === "paid" && refundDraft?.orderId === order.id && (
                                              <div className="registration-refund-form">
                                                <label>
                                                  <span>Monto a reembolsar (máx. {money(refundRemaining(order), currency)})</span>
                                                  <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={refundDraft.amount}
                                                    onChange={(event) => setRefundDraft({ ...refundDraft, amount: event.target.value })}
                                                  />
                                                </label>
                                                <label>
                                                  <span>Motivo (opcional)</span>
                                                  <input
                                                    type="text"
                                                    maxLength={200}
                                                    value={refundDraft.reason}
                                                    onChange={(event) => setRefundDraft({ ...refundDraft, reason: event.target.value })}
                                                    placeholder="Ej. no jugó dobles"
                                                  />
                                                </label>
                                                <div className="registration-refund-form__actions">
                                                  <button
                                                    className="button button--sm button--ghost"
                                                    type="button"
                                                    disabled={editingPaymentId === order.id}
                                                    onClick={() => setRefundDraft(null)}
                                                  >
                                                    No, regresar
                                                  </button>
                                                  <button
                                                    className="button button--sm registration-reject-button"
                                                    type="button"
                                                    disabled={editingPaymentId === order.id}
                                                    onClick={() => void handleRefundOrder(order)}
                                                  >
                                                    {editingPaymentId === order.id ? "Procesando…" : "Reembolsar"}
                                                  </button>
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        )}
                                        <small>
                                          Total {money(order.gross_amount, currency)}
                                          {Number(order.refunded_amount ?? 0) > 0 && order.status === "paid" ? ` · Reembolsado ${money(order.refunded_amount, currency)}` : ""}
                                          {" "}· Neto admin {money(order.net_amount ?? order.organizer_net_amount, currency)}
                                          {order.provider_fee_amount ? ` · Mercado Pago −${money(order.provider_fee_amount, currency)}` : ""}
                                        </small>
                                        {(order.items?.length ?? 0) > 1 && (
                                          <small>
                                            Cubre: {(order.items ?? [])
                                              .map((item) => `${item.division_name ?? "Sin categoría"} ${money(item.amount, currency)}${item.is_additional_category ? " (adicional)" : ""}`)
                                              .join(" · ")}
                                          </small>
                                        )}
                                        {(settings?.member_fee_amount != null || settings?.coach_fee_amount != null || (order.fee_category ?? "general") !== "general") && (
                                          <label className="registration-reclassify">
                                            <small className="muted">Tipo de jugador (declarado; el admin valida por fuera)</small>
                                            <select className="t123-select"
                                              aria-label={`Tipo de jugador de ${participantDisplayName(participant)}`}
                                              value={order.fee_category ?? "general"}
                                              disabled={!canMoney || editingPaymentId === order.id || order.status === "cancelled" || order.status === "refunded"}
                                              onChange={(event) => void handleReclassifyFee(order, event.target.value)}
                                            >
                                              <option value="general">General — {money(settings?.entry_fee_amount ?? "0", currency)}</option>
                                              {settings?.member_fee_amount != null && (
                                                <option value="socio">{feeCategoryLabel("socio", settings)} — {money(settings.member_fee_amount, currency)}</option>
                                              )}
                                              {settings?.coach_fee_amount != null && (
                                                <option value="profesor">{feeCategoryLabel("profesor", settings)} — {money(settings.coach_fee_amount, currency)}</option>
                                              )}
                                            </select>
                                          </label>
                                        )}
                                        {order.status === "pending" && order.public_token && !isAdminChargeOrder(order) ? (
                                          <div className="registration-reissue__row">
                                            <button
                                              className="button button--sm button--ghost"
                                              onClick={() => void handleCopyChargeLink(order)}
                                              type="button"
                                              title="Página de pago de esta orden: no pide usuario ni contraseña"
                                            >
                                              Copiar link de pago
                                            </button>
                                            <small className="muted">El jugador paga desde ese link sin iniciar sesión.</small>
                                          </div>
                                        ) : null}
                                        {canMoney && order.status === "pending" && (
                                          <details className="registration-reissue insc-reissue">
                                            <summary>Reemitir orden (costo cambió o monto especial)</summary>
                                            <small className="muted">Reemitir orden: si el costo cambió después de la inscripción o necesitas un monto especial.</small>
                                            <div className="registration-reissue__row">
                                              <button
                                                type="button"
                                                className="btn btn--ghost btn--compact"
                                                disabled={editingPaymentId === order.id}
                                                onClick={() => void handleReissueOrder(order, null)}
                                                title="Si el total no cambia, solo regenera el link de pago (invalida checkouts abiertos)"
                                              >
                                                Actualizar al costo vigente / regenerar link
                                              </button>
                                              <input
                                                className="registration-reissue__amount"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Monto manual"
                                                aria-label={`Monto manual para ${participantDisplayName(participant)}`}
                                                value={reissueAmount[order.id] ?? ""}
                                                disabled={editingPaymentId === order.id}
                                                onChange={(event) => setReissueAmount((current) => ({ ...current, [order.id]: event.target.value }))}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn--compact"
                                                disabled={editingPaymentId === order.id || !(reissueAmount[order.id] ?? "").trim()}
                                                onClick={() => void handleReissueOrder(order, reissueAmount[order.id] ?? "")}
                                              >
                                                Reemitir con ese monto
                                              </button>
                                            </div>
                                          </details>
                                        )}
                                        {order.balance_due_status === "pending" && (
                                          <small className="form-error">
                                            Saldo pendiente {money(order.balance_due_amount ?? "0", currency)} · {order.balance_due_reason}
                                            {" "}
                                            {canMoney ? (
                                              <>
                                                <button type="button" className="btn btn--compact" disabled={editingPaymentId === order.id} onClick={() => void handleResolveBalance(order, "paid")}>Saldo pagado</button>
                                                {" "}
                                                <button type="button" className="btn btn--ghost btn--compact" disabled={editingPaymentId === order.id} onClick={() => void handleResolveBalance(order, "waived")}>Condonar</button>
                                              </>
                                            ) : null}
                                          </small>
                                        )}
                                        {order.balance_due_status === "paid" && (
                                          <small className="muted">Saldo de {money(order.balance_due_amount ?? "0", currency)} cobrado ✓</small>
                                        )}
                                        {order.balance_due_status === "waived" && (
                                          <small className="muted">Saldo de {money(order.balance_due_amount ?? "0", currency)} condonado</small>
                                        )}
                                        {reclassifyWhatsapp[order.id] && (
                                          <small>
                                            <button type="button" className="btn btn--ghost btn--compact" onClick={() => void handleCopyWhatsapp(order.id)}>
                                              Copiar mensaje de WhatsApp
                                            </button>
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                    </div>
                                    <div className="registration-detail-section">
                                      <h4>Documentos</h4>
                                      <div className="registration-document-checklist">
                                        {docItems.map((doc) => (
                                          <Fragment key={doc.key}>
                                            <div className={`registration-document-row ${doc.dotClass}`}>
                                              <span>{doc.statusText}</span>
                                              <div>
                                                {doc.url ? documentButton("Ver", doc.url, backToOrderPath(order.id)) : null}
                                                {/* Gate #155: pareja = solo lectura — el backend además
                                                    rebota 409 team_order_audit_only. */}
                                                {isTeamOrder ? null : uploadDocumentButton(order, doc.key, doc.uploadLabel, doc.accept)}
                                              </div>
                                            </div>
                                            {/* Revisión de la CURP: a todo lo ancho, debajo de su renglón. */}
                                            {doc.key === "curp" && !isTeamOrder ? renderCurpReviewBox(order) : null}
                                          </Fragment>
                                        ))}
                                        {usesCredential ? (
                                          <div className={`registration-document-row ${credential.ready ? "is-ready" : "is-missing"}`}>
                                            <span title={credential.hint}>{credential.label}</span>
                                            <div>{documentButton("Ver", docs?.credential_url, backToOrderPath(order.id), { credential: 1, tournamentId, orderId: order.id })}</div>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                    {visibleRegistrationColumns.length > 0 && (
                                      <div className="registration-detail-section">
                                        <h4>Datos del formulario</h4>
                                        <dl className="registration-detail-fields">
                                          {visibleRegistrationColumns.slice(0, 4).map((field) => (
                                            <div key={`${order.id}-${field.key}`}>
                                              <dt>{field.label}</dt>
                                              <dd>{registrationFieldValue(participant, field)}</dd>
                                            </div>
                                          ))}
                                        </dl>
                                        {visibleRegistrationColumns.length > 4 ? (
                                          <details className="insc-more-fields">
                                            <summary>Ver todos los datos ▾</summary>
                                            <dl className="registration-detail-fields">
                                              {visibleRegistrationColumns.slice(4).map((field) => (
                                                <div key={`${order.id}-${field.key}`}>
                                                  <dt>{field.label}</dt>
                                                  <dd>{registrationFieldValue(participant, field)}</dd>
                                                </div>
                                              ))}
                                            </dl>
                                          </details>
                                        ) : null}
                                      </div>
                                    )}
                                    <div className="registration-detail-section registration-detail-section--actions insc-detail__actions">
                                      <h4>{isTeamOrder ? "Auditoría" : "Acciones"}</h4>
                                      {isTeamOrder ? (
                                        <p className="muted">
                                          Orden de pareja: sin acciones de jugador (aprobar, bienvenida,
                                          credencial, pareja, categoría). Solo puede cancelarse desde
                                          «Estado y pago»; las pagadas históricas quedan para conciliación.
                                        </p>
                                      ) : (
                                      <div className="registration-row-actions registration-row-actions--finance">
                                {isAdminChargeOrder(order) && participant?.current_status === "pre_registered" ? (
                                  <>
                                    {order.status === "paid" ? (
                                      <>
                                        <span className="registration-status registration-status--paid" title="Pago recibido — falta activarlo">
                                          Pagado — por activar
                                        </span>
                                        <button
                                          className="button button--sm registration-approve-button"
                                          disabled={reviewingParticipantId === participant.id}
                                          onClick={() => void handleActivateCharge(order)}
                                          type="button"
                                        >
                                          {reviewingParticipantId === participant.id ? "Activando…" : "Activar"}
                                        </button>
                                        <FieldHint label="Qué hace Activar">
                                          Confirma la inscripción ya pagada: el jugador pasa a activo.{" "}
                                          {autoSendWelcome
                                            ? "Al activarla le llega su correo de bienvenida con su usuario y contraseña (o PIN)."
                                            : "La bienvenida automática está apagada en Mensajes: después, usa «Enviar bienvenida»."}
                                        </FieldHint>
                                      </>
                                    ) : order.status === "pending" ? (
                                      <>
                                        <span className="registration-status registration-status--pending" title="El jugador todavía no paga su cobro">
                                          Preinscrito — pago pendiente
                                        </span>
                                        {order.public_token ? (
                                          <button
                                            className="button button--sm button--ghost"
                                            onClick={() => void handleCopyChargeLink(order)}
                                            type="button"
                                          >
                                            Copiar link de pago
                                          </button>
                                        ) : null}
                                        {canMoney ? (
                                          <button
                                            className="button button--sm button--ghost registration-reject-button"
                                            disabled={cancellingChargeId === order.id}
                                            onClick={() => void handleCancelCharge(order)}
                                            type="button"
                                          >
                                            {cancellingChargeId === order.id ? "Cancelando…" : "Cancelar preinscripción"}
                                          </button>
                                        ) : null}
                                      </>
                                    ) : null}
                                  </>
                                ) : participant?.current_status === "pre_registered" ? (
                                  <span className="registration-action-hint">
                                    <button
                                      className="button button--sm registration-approve-button"
                                      disabled={reviewingParticipantId === participant.id}
                                      onClick={() => void handleApproveRegistration(order)}
                                      type="button"
                                    >
                                      {reviewingParticipantId === participant.id ? "Guardando…" : "Aprobar"}
                                    </button>
                                    <FieldHint label="Qué hace Aprobar">
                                      Acepta la inscripción: el jugador pasa a activo y ya puede entrar al draw.{" "}
                                      {autoSendWelcome
                                        ? "Al aprobar le llega su correo de bienvenida con su usuario y contraseña (o PIN) para entrar a la app; mientras no la apruebes, no lo recibe."
                                        : "La bienvenida automática está apagada en Mensajes: después de aprobar, usa «Enviar bienvenida» para mandarle su usuario y contraseña."}{" "}
                                      Si el mismo pago cubre otras categorías pendientes de revisión, también las aprueba. El pago se controla aparte (puede seguir pendiente).
                                    </FieldHint>
                                  </span>
                                ) : participant ? (
                                  orderApprovableOtherCategories(order, coverageOptionsFor(order)).map((other) => (
                                    <span className="registration-action-hint" key={`approve-${other.participantId}`}>
                                      <button
                                        className="button button--sm registration-approve-button"
                                        disabled={reviewingParticipantId === other.participantId}
                                        onClick={() => void handleApproveRegistration(order, other)}
                                        type="button"
                                      >
                                        {reviewingParticipantId === other.participantId ? "Guardando…" : `Aprobar ${other.name}`}
                                      </button>
                                      <FieldHint label={`Qué hace Aprobar ${other.name}`}>
                                        Este pago también cubre {other.name}, que sigue pendiente de revisión: la aprueba sin tocar el cobro.
                                      </FieldHint>
                                    </span>
                                  ))
                                ) : null}
                                {order.status !== "paid" && canMoney ? (
                                  <span className="registration-action-hint">
                                    <button className="button button--sm registration-paid-button" disabled={markingPaidId === order.id} onClick={() => void handleMarkPaid(order)} type="button">
                                      {markingPaidId === order.id ? "Marcando…" : "Marcar pagado"}
                                    </button>
                                    <FieldHint label="Qué hace Marcar pagado">
                                      Registra que ya recibiste el pago (transferencia, efectivo o comprobante). La orden pasa a pagada, se suma en Finanzas y el jugador ve el link del grupo de WhatsApp. Si el jugador paga en línea con Mercado Pago, esto ocurre solo.
                                    </FieldHint>
                                  </span>
                                ) : null}
                                {participant?.current_status === "active" ? (
                                  <>
                                    <span className="registration-action-hint">
                                      <button
                                        className={`button button--sm button--ghost registration-welcome-button${order.welcome_sent_at ? " registration-welcome-button--resend" : ""}`}
                                        disabled={sendingWelcomeId === order.id}
                                        onClick={() => void handleSendWelcome(order)}
                                        type="button"
                                        title={order.welcome_sent_at ? `Enviado el ${formatDateTime(order.welcome_sent_at)}${(order.welcome_count ?? 0) > 1 ? ` (${order.welcome_count} veces)` : ""}` : undefined}
                                      >
                                        {sendingWelcomeId === order.id
                                          ? "Enviando…"
                                          : order.welcome_sent_at
                                            ? "Re-enviar bienvenida"
                                            : "Enviar bienvenida"}
                                      </button>
                                      <FieldHint label="Qué hace Enviar bienvenida">
                                        Envía al jugador el correo de bienvenida al torneo con su usuario y contraseña temporal (solo si su cuenta nunca ha entrado a la plataforma) y, si debe, su link de pago. También queda como comunicado dentro de la app. Cuando ya se envió una vez, el botón cambia a &quot;Re-enviar&quot; y se ve en azul más claro.
                                      </FieldHint>
                                    </span>
                                    {usesCredential ? (
                                      <span className="registration-action-hint">
                                        <button
                                          className="button button--sm button--ghost"
                                          disabled={generatingCredentialId === order.id}
                                          onClick={() => void handleGenerateCredential(order)}
                                          type="button"
                                        >
                                          {generatingCredentialId === order.id ? "Generando…" : "Credencial"}
                                        </button>
                                        <FieldHint label="Qué hace Credencial">
                                          Genera la credencial digital del jugador (imagen con su foto, nombre, categoría y datos del torneo) para descargarla o compartirla. Usa la foto del jugador y el diseño de la sección 02 · Credencial.
                                        </FieldHint>
                                      </span>
                                    ) : null}
                                  </>
                                ) : null}
                                {participant && participant.current_status !== "dropped" ? (
                                  <>
                                    <span className="registration-action-hint">
                                      <button
                                        className="button button--sm button--ghost"
                                        disabled={sendingMessageId === order.id}
                                        onClick={() => void handleSendDocsPending(order)}
                                        type="button"
                                      >
                                        {sendingMessageId === order.id ? "Enviando…" : "Enviar documentos pendientes"}
                                      </button>
                                      <FieldHint label="Qué hace Enviar documentos pendientes">
                                        Le manda al jugador (app y correo; WhatsApp si lo tienes encendido) la lista exacta de documentos obligatorios que le faltan y la liga para subirlos. Puedes ver el texto en Comunicados → Mensajes del torneo.
                                      </FieldHint>
                                    </span>
                                    {order.status === "pending" && Number(order.gross_amount) > 0 ? (
                                      <span className="registration-action-hint">
                                        <button
                                          className="button button--sm button--ghost"
                                          disabled={sendingMessageId === order.id}
                                          onClick={() => void handleSendPaymentReminder(order)}
                                          type="button"
                                        >
                                          {sendingMessageId === order.id ? "Enviando…" : "Enviar recordatorio de pago"}
                                        </button>
                                        <FieldHint label="Qué hace Enviar recordatorio de pago">
                                          Le recuerda al jugador el monto pendiente con la liga para pagar o subir su comprobante (app y correo; WhatsApp si lo tienes encendido).
                                        </FieldHint>
                                      </span>
                                    ) : null}
                                  </>
                                ) : null}
                                {participant?.current_status === "dropped" && !partnerInfo && registrationOrderOwes(order, coverageOptionsFor(order)) ? (
                                  (order.items ?? []).some((item) => item.participant_id === participant.id) ? (
                                    // Sigue debiendo otra categoría del mismo pago, pero el monto
                                    // incluye la de la fila (de baja): recordarlo o cobrarlo en lote
                                    // pediría dinero por una baja. El cobro no cambia en esta fase.
                                    <small className="muted registration-coverage-note" data-no-toast>
                                      Este pago incluye {participant.division?.name ?? "una categoría"}, que está de baja, y su monto no cambia: no entra al recordatorio de pago ni a «Confirmar pago» en lote.
                                    </small>
                                  ) : (
                                    // Revisión ronda 2: la orden ya no tiene el renglón de la fila
                                    // (se reabrió sin él); no decir que lo incluye.
                                    <small className="muted registration-coverage-note" data-no-toast>
                                      Este pago ya no incluye {participant.division?.name ?? "la categoría de esta fila"} (de baja): sigue cubriendo {joinSpanishList(orderInPlayOtherCategories(order, coverageOptionsFor(order)).map((other) => other.name))}. Como esta fila está de baja, no entra al recordatorio de pago ni a «Confirmar pago» en lote.
                                    </small>
                                  )
                                ) : null}
                                {participant && participant.current_status !== "dropped" && !(isAdminChargeOrder(order) && participant.current_status === "pre_registered") ? (
                                  <span className="registration-action-hint">
                                    <button
                                      className="button button--sm button--ghost registration-reject-button"
                                      disabled={reviewingParticipantId === participant.id}
                                      onClick={() => void handleDropParticipant(order)}
                                      type="button"
                                      title="Queda como baja; podrás reincorporarlo después"
                                    >
                                      {reviewingParticipantId === participant.id ? "Eliminando…" : "Eliminar jugador"}
                                    </button>
                                    <FieldHint label="Qué hace Eliminar jugador">
                                      Da de baja al jugador: sale del draw y sus partidos pendientes se cancelan (sin W/O). Su inscripción y su pago se conservan y aparece &quot;Reincorporar jugador&quot; por si fue un error.
                                    </FieldHint>
                                  </span>
                                ) : null}
                                {participant && participant.current_status !== "dropped" && !(isAdminChargeOrder(order) && participant.current_status === "pre_registered") ? (
                                  <span className="registration-action-hint">
                                    <button
                                      className="button button--sm button--ghost"
                                      disabled={playedIds.has(participant.id)}
                                      onClick={() => setReplaceFor(participant.id)}
                                      type="button"
                                      title={
                                        playedIds.has(participant.id)
                                          ? "Ya jugó (o tiene un resultado o W.O.): no se puede reemplazar."
                                          : "Otro jugador toma su lugar con los mismos partidos, fechas y canchas"
                                      }
                                    >
                                      Reemplazar jugador
                                    </button>
                                    <FieldHint label="Qué hace Reemplazar jugador">
                                      {playedIds.has(participant.id)
                                        ? "Ya jugó (o tiene un resultado o W.O.), así que no se puede reemplazar."
                                        : "Otro jugador toma su lugar en el mismo grupo con los mismos partidos, fechas y canchas. Este jugador queda dado de baja. No se manda ningún aviso."}
                                    </FieldHint>
                                  </span>
                                ) : null}
                                {participant?.current_status === "dropped" ? (
                                  <span className="registration-action-hint">
                                    <button
                                      className="button button--sm registration-approve-button"
                                      disabled={reviewingParticipantId === participant.id}
                                      onClick={() => void handleReincorporateParticipant(order)}
                                      type="button"
                                      title="Vuelve como activo con su misma inscripción y pago"
                                    >
                                      {reviewingParticipantId === participant.id ? "Reincorporando…" : "Reincorporar jugador"}
                                    </button>
                                    <FieldHint label="Qué hace Reincorporar jugador">
                                      Regresa al jugador como activo con su misma inscripción, categoría y pago. Los partidos que se cancelaron al eliminarlo no se recuperan.
                                    </FieldHint>
                                  </span>
                                ) : null}
                                {participant ? (
                                  <span className="registration-action-hint">
                                    <button
                                      className="button button--sm button--ghost"
                                      disabled={savingRegistrationEdit}
                                      onClick={() => (editingRegistrationOrderId === order.id ? setEditingRegistrationOrderId(null) : beginEditRegistration(order))}
                                      type="button"
                                    >
                                      {editingRegistrationOrderId === order.id ? "Cerrar edición" : "Editar registro"}
                                    </button>
                                    <FieldHint label="Qué hace Editar registro">
                                      Corrige los datos del formulario que el jugador capturó mal o te mandó por fuera (nombre, contacto, fecha de nacimiento, tutor, respuestas del formulario). Los documentos se cambian arriba con &quot;Subir&quot; o &quot;Cambiar&quot;.
                                    </FieldHint>
                                  </span>
                                ) : null}
                                {participant?.current_status === "dropped" ? (
                                  <span className="registration-action-hint insc-detail__danger">
                                    <button
                                      className="button button--sm button--ghost registration-reject-button"
                                      disabled={deletingRegistrationId === order.id}
                                      onClick={() => void handleDeleteRegistration(order)}
                                      type="button"
                                    >
                                      {deletingRegistrationId === order.id ? "Eliminando…" : "Eliminar registro"}
                                    </button>
                                    {registrationDeleteBlocked?.orderId === order.id ? (
                                      <small className="field-warning">No se eliminó: {registrationDeleteBlocked.reason}</small>
                                    ) : null}
                                    <FieldHint label="Qué hace Eliminar registro">
                                      Borra por completo esta inscripción (para pruebas o registros equivocados): participante, órdenes no pagadas e historial en este torneo. Solo se permite con el jugador dado de baja, sin partidos y con el torneo sin calendario ni resultados; si tiene pago confirmado, primero cancela o reembolsa. El jugador sigue en Jugadores.
                                    </FieldHint>
                                  </span>
                                ) : null}
                              </div>
                                      )}
                                      {editingRegistrationOrderId === order.id && participant ? (
                                        <div className="registration-edit-panel">
                                          <h5>Editar registro</h5>
                                          <div className="setup-call-grid setup-call-grid--two">
                                            {editableRegistrationFields.map((field) => (
                                              <label key={`edit-${order.id}-${field.key}`}>
                                                {field.label}
                                                {field.type === "select" && (field.options?.length ?? 0) > 0 ? (
                                                  <select className="t123-select"
                                                    value={registrationEditForm[field.key] ?? ""}
                                                    onChange={(event) => setRegistrationEditForm((current) => ({ ...current, [field.key]: event.target.value }))}
                                                  >
                                                    <option value="">—</option>
                                                    {(field.options ?? []).map((option) => (
                                                      <option key={String(option)} value={String(option)}>
                                                        {field.key === "gender" ? (option === "male" ? "Masculino" : option === "female" ? "Femenino" : String(option)) : String(option)}
                                                      </option>
                                                    ))}
                                                  </select>
                                                ) : (
                                                  <input
                                                    type={field.type === "date" ? "date" : field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"}
                                                    value={registrationEditForm[field.key] ?? ""}
                                                    onChange={(event) => setRegistrationEditForm((current) => ({ ...current, [field.key]: event.target.value }))}
                                                    disabled={field.kind !== "custom" && (isAccountLockedProfileField(field.key, participant.player) || (isSubUser && field.key === "email"))}
                                                  />
                                                )}
                                                {field.kind !== "custom" && (isAccountLockedProfileField(field.key, participant.player) || (isSubUser && field.key === "email")) ? (
                                                  // Aviso fijo, no un error al guardar.
                                                  <small className="muted" data-no-toast>
                                                    {isSubUser ? "El equipo no cambia el correo de acceso. Pide al dueño del perfil que lo corrija." : accountNotOwnedEmailNote(accountNotOwnedInfo(participant.player))}
                                                  </small>
                                                ) : null}
                                              </label>
                                            ))}
                                          </div>
                                          <div className="form__actions">
                                            <button className="button button--sm button--primary" type="button" disabled={savingRegistrationEdit} onClick={() => void saveRegistrationEdit(order)}>
                                              {savingRegistrationEdit ? "Guardando…" : "Guardar registro"}
                                            </button>
                                            <button className="button button--sm button--ghost" type="button" disabled={savingRegistrationEdit} onClick={() => setEditingRegistrationOrderId(null)}>
                                              Cancelar
                                            </button>
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {deletedRegistrations.length > 0 ? (
              <details className="card setup-call-card setup-registration-card deleted-registrations-card">
                <summary className="deleted-registrations-card__summary">
                  <strong>Registros borrados ({deletedRegistrations.length})</strong>
                  <span className="muted">Auditoría de "Eliminar registro": quién borró qué registro y cuándo.</span>
                </summary>
                <div className="responsive-table-wrap">
                  <table className="data-table deleted-registrations-table">
                    <thead>
                      <tr>
                        <th>Borrado</th>
                        <th>Jugador</th>
                        <th>Categoría</th>
                        <th>Órdenes</th>
                        <th>Borró</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletedRegistrations.map((entry) => (
                        <tr key={entry.id}>
                          <td data-label="Borrado">{entry.deleted_at ? new Date(entry.deleted_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                          <td data-label="Jugador">
                            <strong>{entry.name ?? `Participante #${entry.participant_id ?? "?"}`}</strong>
                            {entry.email ? <div className="muted">{entry.email}</div> : null}
                          </td>
                          <td data-label="Categoría">{entry.division_name ?? "—"}</td>
                          <td data-label="Órdenes">
                            {entry.orders && entry.orders.length > 0
                              ? entry.orders.map((order) => `#${order.id} ${order.status}${order.gross_amount ? ` · $${order.gross_amount}` : ""}`).join(", ")
                              : "Sin órdenes"}
                          </td>
                          <td data-label="Borró">{entry.deleted_by?.name ?? (entry.deleted_by_user_id ? `Usuario #${entry.deleted_by_user_id}` : "—")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ) : null}

            {setupMode && (
              <section className="card setup-call-card setup-registration-card registration-players-cta">
                <div className="setup-call-card__heading">
                  <span>05</span>
                  <div>
                    <h2>Alta y selección de inscritos</h2>
                    <p>Directamente en sistema sin Link Público: agrega jugadores existentes o da de alta jugadores nuevos antes de controlar sus pagos.</p>
                  </div>
                </div>
                <div className="insc-enroll-cta">
                  <button
                    className="insc-btn insc-btn--lime"
                    type="button"
                    disabled={tournamentReadOnly}
                    onClick={() => setEnrollExistingOpen(true)}
                  >
                    Inscribir jugadores existentes
                  </button>
                  <Link className="insc-enroll-cta__link" to={`/admin/tournaments/${tournamentId}/setup/players`}>
                    Administrar jugadores del torneo
                  </Link>
                </div>
              </section>
            )}
            {enrollExistingOpen && token ? (
              <EnrollExistingPlayersDialog
                token={token}
                tournamentId={tournamentId}
                divisions={divisions}
                onClose={() => setEnrollExistingOpen(false)}
                onEnrolled={(text) => {
                  setEnrollExistingOpen(false);
                  setMessage(text);
                  void loadDashboard();
                }}
              />
            ) : null}
            {replaceFor !== null && token ? (
              <ReplacePlayerDialog
                token={token}
                tournamentId={tournamentId}
                participantId={replaceFor}
                onClose={() => setReplaceFor(null)}
                onDone={(result) => {
                  setReplaceFor(null);
                  setError(null);
                  setMessage(result.summary);
                  void loadDashboard();
                }}
              />
            ) : null}

        </div>

        {setupMode ? (
          <div className="wiz-bottombar wiz-bottombar--brand setup-registration-actions">
            <Link className="wiz-bottombar__back" to={`/admin/tournaments/${tournamentId}/setup/settings`}>‹ Volver a Comunicación</Link>
            <Link className="wiz-bottombar__next" to={`/admin/tournaments/${tournamentId}/setup/format`}>Continuar a Formato ›</Link>
          </div>
        ) : (
          <TournamentBottomNav tournamentId={tournamentId} />
        )}
      </section>
    </>
  );
}
